import { z } from 'zod';
import { TZDate } from '@date-fns/tz';
import { startOfDay, endOfDay, format, isSameDay, differenceInDays, addDays } from 'date-fns';

var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all)
    __defProp(target, name, { get: all[name], enumerable: true });
};

// src/money.ts
var money_exports = {};
__export(money_exports, {
  abs: () => abs,
  add: () => add,
  allocate: () => allocate,
  compare: () => compare,
  eq: () => eq,
  formatBRL: () => formatBRL,
  fromCents: () => fromCents,
  fromReais: () => fromReais,
  fromWire: () => fromWire,
  gt: () => gt,
  gte: () => gte,
  isMoney: () => isMoney,
  isNegative: () => isNegative,
  isPositive: () => isPositive,
  isZero: () => isZero,
  lt: () => lt,
  lte: () => lte,
  max: () => max,
  min: () => min,
  moneySchema: () => moneySchema,
  moneySchemaNonNegative: () => moneySchemaNonNegative,
  moneySchemaPositive: () => moneySchemaPositive,
  mulQuantity: () => mulQuantity,
  mulRate: () => mulRate,
  neg: () => neg,
  parse: () => parse,
  percentOf: () => percentOf,
  sub: () => sub,
  sumAll: () => sumAll,
  toDecimalString: () => toDecimalString,
  toWire: () => toWire,
  zero: () => zero
});
var CENTS_PER_UNIT = 100n;
var WIRE_PATTERN = /^[+-]?\d+$/;
var RATE_PATTERN = /^[+-]?(?:\d+\.\d+|\d+)$/;
var DIGITS_PATTERN = /^\d+$/;
var WHITESPACE_PATTERN = /\s/g;
var CURRENCY_PATTERN = /R\$/gi;
function brand(value) {
  return value;
}
function negate(value) {
  return 0n - value;
}
function assertMoney(value) {
  if (typeof value !== "bigint") {
    throw new TypeError("Valor monet\xE1rio inv\xE1lido: Money precisa ser bigint em centavos.");
  }
}
var zero = brand(0n);
function isMoney(value) {
  return typeof value === "bigint";
}
function fromCents(value) {
  if (typeof value === "bigint") {
    return brand(value);
  }
  if (!Number.isFinite(value)) {
    throw new TypeError("Valor monet\xE1rio inv\xE1lido: n\xFAmero n\xE3o finito em centavos.");
  }
  if (!Number.isInteger(value)) {
    throw new TypeError(
      `Valor monet\xE1rio inv\xE1lido: ${String(value)} n\xE3o \xE9 inteiro. Centavos n\xE3o admitem fra\xE7\xE3o.`
    );
  }
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(
      `Valor monet\xE1rio inv\xE1lido: ${String(value)} excede o inteiro seguro do JavaScript. Use bigint ou string.`
    );
  }
  return brand(BigInt(value));
}
function fromReais(value) {
  const cleaned = value.replace(WHITESPACE_PATTERN, "").replace(CURRENCY_PATTERN, "");
  if (cleaned === "") {
    throw new TypeError("Valor monet\xE1rio inv\xE1lido: string vazia.");
  }
  const negative = cleaned.startsWith("-");
  const unsigned = negative || cleaned.startsWith("+") ? cleaned.slice(1) : cleaned;
  if (unsigned === "") {
    throw new TypeError(`Valor monet\xE1rio inv\xE1lido: "${value}" tem sinal sem d\xEDgitos.`);
  }
  const { integerDigits, fractionDigits } = splitReais(unsigned, value);
  if (!DIGITS_PATTERN.test(integerDigits)) {
    throw new TypeError(`Valor monet\xE1rio inv\xE1lido: "${value}" cont\xE9m caractere n\xE3o num\xE9rico.`);
  }
  if (fractionDigits !== "" && !DIGITS_PATTERN.test(fractionDigits)) {
    throw new TypeError(`Valor monet\xE1rio inv\xE1lido: "${value}" cont\xE9m caractere n\xE3o num\xE9rico.`);
  }
  if (fractionDigits.length > 2) {
    throw new RangeError(
      `Valor monet\xE1rio inv\xE1lido: "${value}" tem ${String(fractionDigits.length)} casas decimais. Centavos admitem no m\xE1ximo 2.`
    );
  }
  const cents = BigInt(integerDigits) * CENTS_PER_UNIT + BigInt(fractionDigits.padEnd(2, "0").slice(0, 2));
  return brand(negative ? -cents : cents);
}
function splitReais(unsigned, original) {
  const lastDot = unsigned.lastIndexOf(".");
  const lastComma = unsigned.lastIndexOf(",");
  if (lastDot !== -1 && lastComma !== -1) {
    const decimalIndex = Math.max(lastDot, lastComma);
    const grouping = decimalIndex === lastDot ? "," : ".";
    return {
      integerDigits: unsigned.slice(0, decimalIndex).split(grouping).join(""),
      fractionDigits: unsigned.slice(decimalIndex + 1)
    };
  }
  if (lastComma !== -1) {
    if (unsigned.indexOf(",") !== lastComma) {
      throw new TypeError(`Valor monet\xE1rio inv\xE1lido: "${original}" tem mais de uma v\xEDrgula.`);
    }
    return {
      integerDigits: unsigned.slice(0, lastComma),
      fractionDigits: unsigned.slice(lastComma + 1)
    };
  }
  if (lastDot !== -1) {
    const parts = unsigned.split(".");
    const leading = parts[0] ?? "";
    const tail = parts[parts.length - 1] ?? "";
    const ehMilhar = parts.length > 2 || tail.length === 3 && leading.length <= 3;
    if (ehMilhar) {
      const gruposIrregulares = leading.length === 0 || leading.length > 3 || parts.slice(1).some((group) => group.length !== 3);
      if (gruposIrregulares) {
        throw new TypeError(
          `Valor monet\xE1rio inv\xE1lido: "${original}" tem agrupamento de milhar irregular.`
        );
      }
      return { integerDigits: parts.join(""), fractionDigits: "" };
    }
    return { integerDigits: parts.slice(0, -1).join(""), fractionDigits: tail };
  }
  return { integerDigits: unsigned, fractionDigits: "" };
}
function fromWire(value) {
  if (!WIRE_PATTERN.test(value)) {
    throw new TypeError(
      `Valor monet\xE1rio inv\xE1lido no wire: "${value}". Era esperada uma string decimal de centavos, ex.: "123456".`
    );
  }
  return brand(BigInt(value));
}
function toWire(value) {
  assertMoney(value);
  return value.toString();
}
function parse(value) {
  if (typeof value === "bigint" || typeof value === "number") {
    return fromCents(value);
  }
  const trimmed = value.trim();
  return WIRE_PATTERN.test(trimmed) ? fromWire(trimmed) : fromReais(trimmed);
}
function add(a, b) {
  return brand(a + b);
}
function sub(a, b) {
  return brand(a - b);
}
function neg(value) {
  return brand(negate(value));
}
function abs(value) {
  return brand(value < 0n ? negate(value) : value);
}
function min(a, b) {
  return a <= b ? a : b;
}
function max(a, b) {
  return a >= b ? a : b;
}
function compare(a, b) {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}
function eq(a, b) {
  return a === b;
}
function gt(a, b) {
  return a > b;
}
function gte(a, b) {
  return a >= b;
}
function lt(a, b) {
  return a < b;
}
function lte(a, b) {
  return a <= b;
}
function isZero(value) {
  return value === 0n;
}
function isNegative(value) {
  return value < 0n;
}
function isPositive(value) {
  return value > 0n;
}
function sumAll(values) {
  let total = 0n;
  for (const value of values) {
    total += value;
  }
  return brand(total);
}
function divideRoundHalfUp(numerator, denominator) {
  if (denominator === 0n) {
    throw new RangeError("C\xE1lculo monet\xE1rio inv\xE1lido: divis\xE3o por zero.");
  }
  const resultIsNegative = numerator < 0n !== denominator < 0n;
  const absNumerator = numerator < 0n ? -numerator : numerator;
  const absDenominator = denominator < 0n ? -denominator : denominator;
  const quotient = absNumerator / absDenominator;
  const remainder = absNumerator % absDenominator;
  const rounded = remainder * 2n >= absDenominator ? quotient + 1n : quotient;
  return resultIsNegative ? -rounded : rounded;
}
function parseRateString(rate) {
  const normalized = rate.trim().replace(",", ".");
  if (!RATE_PATTERN.test(normalized)) {
    throw new TypeError(
      `Taxa inv\xE1lida: "${rate}". Era esperada uma string decimal, ex.: "0.075" ou "-1.5".`
    );
  }
  const negative = normalized.startsWith("-");
  const unsigned = negative || normalized.startsWith("+") ? normalized.slice(1) : normalized;
  const dotIndex = unsigned.indexOf(".");
  const integerDigits = dotIndex === -1 ? unsigned : unsigned.slice(0, dotIndex);
  const fractionDigits = dotIndex === -1 ? "" : unsigned.slice(dotIndex + 1);
  const numerator = BigInt(`${integerDigits === "" ? "0" : integerDigits}${fractionDigits}`);
  const denominator = 10n ** BigInt(fractionDigits.length);
  return { numerator: negative ? -numerator : numerator, denominator };
}
function toFraction(rate) {
  if (typeof rate === "string") {
    return parseRateString(rate);
  }
  if (rate.denominator === 0n) {
    throw new RangeError("Taxa inv\xE1lida: denominador zero.");
  }
  return rate;
}
function mulRate(value, rate) {
  assertMoney(value);
  const { numerator, denominator } = toFraction(rate);
  return brand(divideRoundHalfUp(value * numerator, denominator));
}
function percentOf(value, percent) {
  const { numerator, denominator } = toFraction(percent);
  return mulRate(value, { numerator, denominator: denominator * 100n });
}
function mulQuantity(value, quantity) {
  assertMoney(value);
  const factor = typeof quantity === "bigint" ? quantity : toSafeBigInt(quantity, "quantidade");
  return brand(value * factor);
}
function toSafeBigInt(value, label) {
  if (!Number.isInteger(value)) {
    throw new TypeError(`Valor inv\xE1lido: ${label} precisa ser um n\xFAmero inteiro.`);
  }
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`Valor inv\xE1lido: ${label} excede o inteiro seguro do JavaScript.`);
  }
  return BigInt(value);
}
function allocate(total, weights) {
  assertMoney(total);
  if (weights.length === 0) {
    throw new RangeError("Rateio inv\xE1lido: \xE9 preciso pelo menos um peso.");
  }
  const normalized = weights.map((weight, index) => normalizeWeight(weight, index));
  const totalWeight = normalized.reduce((acc, weight) => acc + weight, 0n);
  if (totalWeight === 0n) {
    throw new RangeError("Rateio inv\xE1lido: a soma dos pesos n\xE3o pode ser zero.");
  }
  const negative = total < 0n;
  const absTotal = negative ? negate(total) : total;
  const rows = normalized.map((weight, index) => {
    const exact = absTotal * weight;
    return { index, part: exact / totalWeight, remainder: exact % totalWeight };
  });
  const distributed = rows.reduce((acc, row) => acc + row.part, 0n);
  let leftover = absTotal - distributed;
  const byRemainder = [...rows].sort((a, b) => {
    if (a.remainder === b.remainder) return a.index - b.index;
    return a.remainder > b.remainder ? -1 : 1;
  });
  for (const row of byRemainder) {
    if (leftover <= 0n) break;
    row.part += 1n;
    leftover -= 1n;
  }
  return rows.map((row) => brand(negative ? -row.part : row.part));
}
function normalizeWeight(weight, index) {
  const label = `o peso na posi\xE7\xE3o ${String(index)}`;
  const value = typeof weight === "bigint" ? weight : toSafeBigInt(weight, label);
  if (value < 0n) {
    throw new RangeError(`Rateio inv\xE1lido: ${label} \xE9 negativo.`);
  }
  return value;
}
var groupingFormatter = new Intl.NumberFormat("pt-BR", {
  style: "decimal",
  useGrouping: true,
  maximumFractionDigits: 0
});
function formatBRL(value) {
  assertMoney(value);
  const negative = value < 0n;
  const absolute = negative ? negate(value) : value;
  const grouped = groupingFormatter.format(absolute / CENTS_PER_UNIT);
  const cents = (absolute % CENTS_PER_UNIT).toString().padStart(2, "0");
  return `${negative ? "-" : ""}R$ ${grouped},${cents}`;
}
function toDecimalString(value) {
  assertMoney(value);
  const negative = value < 0n;
  const absolute = negative ? negate(value) : value;
  const units = (absolute / CENTS_PER_UNIT).toString();
  const cents = (absolute % CENTS_PER_UNIT).toString().padStart(2, "0");
  return `${negative ? "-" : ""}${units}.${cents}`;
}
var moneyInputSchema = z.union([z.string(), z.number(), z.bigint()]);
var moneySchema = moneyInputSchema.transform((value, ctx) => {
  try {
    return parse(value);
  } catch (error) {
    ctx.addIssue({
      code: "custom",
      message: error instanceof Error ? error.message : "Valor monet\xE1rio inv\xE1lido."
    });
    return z.NEVER;
  }
});
var moneySchemaNonNegative = moneySchema.refine((value) => value >= 0n, {
  message: "Valor monet\xE1rio inv\xE1lido: n\xE3o pode ser negativo."
});
var moneySchemaPositive = moneySchema.refine((value) => value > 0n, {
  message: "Valor monet\xE1rio inv\xE1lido: precisa ser maior que zero."
});

// src/datetime.ts
var datetime_exports = {};
__export(datetime_exports, {
  APP_TIMEZONE: () => APP_TIMEZONE,
  DATE_FORMAT_BR: () => DATE_FORMAT_BR,
  DATE_KEY_FORMAT: () => DATE_KEY_FORMAT,
  DATE_TIME_FORMAT_BR: () => DATE_TIME_FORMAT_BR,
  DEFAULT_RENTAL_DAY_POLICY: () => DEFAULT_RENTAL_DAY_POLICY,
  ISO_INSTANT_PATTERN: () => ISO_INSTANT_PATTERN,
  calendarDaysSpanned: () => calendarDaysSpanned,
  dayOfWeekFromKey: () => dayOfWeekFromKey,
  endOfLocalDay: () => endOfLocalDay,
  formatDateBR: () => formatDateBR,
  formatDateTimeBR: () => formatDateTimeBR,
  formatInTimeZone: () => formatInTimeZone,
  isSameLocalDay: () => isSameLocalDay,
  localDayKey: () => localDayKey,
  nowInTimeZone: () => nowInTimeZone,
  parseUtcIso: () => parseUtcIso,
  rentalDays: () => rentalDays,
  startOfLocalDay: () => startOfLocalDay,
  toInstant: () => toInstant,
  toUtcIso: () => toUtcIso,
  toZoned: () => toZoned
});
var APP_TIMEZONE = "America/Sao_Paulo";
var DATE_FORMAT_BR = "dd/MM/yyyy";
var DATE_TIME_FORMAT_BR = "dd/MM/yyyy HH:mm";
var DATE_KEY_FORMAT = "yyyy-MM-dd";
var ISO_INSTANT_PATTERN = /^(?:\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}|\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}|\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2}\.\d{1,9})(?:Z|[+-]\d{2}:?\d{2})$/;
function assertValidDate(value, label) {
  if (Number.isNaN(value.getTime())) {
    throw new TypeError(`Data inv\xE1lida: ${label}.`);
  }
}
function toZoned(instant, timeZone = APP_TIMEZONE) {
  assertValidDate(instant, "instante informado");
  return new TZDate(instant.getTime(), timeZone);
}
function toInstant(zoned) {
  return new Date(zoned.getTime());
}
function nowInTimeZone(timeZone = APP_TIMEZONE) {
  return TZDate.tz(timeZone);
}
function parseUtcIso(value) {
  if (!ISO_INSTANT_PATTERN.test(value)) {
    throw new TypeError(
      `Data inv\xE1lida: "${value}". Era esperada uma string ISO 8601 com fuso expl\xEDcito, ex.: "2026-03-15T12:00:00Z".`
    );
  }
  const parsed = new Date(value);
  assertValidDate(parsed, `"${value}"`);
  return parsed;
}
function toUtcIso(instant) {
  assertValidDate(instant, "instante informado");
  return instant.toISOString();
}
function startOfLocalDay(instant, timeZone = APP_TIMEZONE) {
  return toInstant(startOfDay(toZoned(instant, timeZone)));
}
function endOfLocalDay(instant, timeZone = APP_TIMEZONE) {
  return toInstant(endOfDay(toZoned(instant, timeZone)));
}
function formatInTimeZone(instant, pattern, timeZone = APP_TIMEZONE) {
  return format(toZoned(instant, timeZone), pattern);
}
function formatDateBR(instant, timeZone = APP_TIMEZONE) {
  return formatInTimeZone(instant, DATE_FORMAT_BR, timeZone);
}
function formatDateTimeBR(instant, timeZone = APP_TIMEZONE) {
  return formatInTimeZone(instant, DATE_TIME_FORMAT_BR, timeZone);
}
function localDayKey(instant, timeZone = APP_TIMEZONE) {
  return formatInTimeZone(instant, DATE_KEY_FORMAT, timeZone);
}
function dayOfWeekFromKey(dayKey) {
  return (/* @__PURE__ */ new Date(`${dayKey}T12:00:00Z`)).getUTCDay();
}
function isSameLocalDay(a, b, timeZone = APP_TIMEZONE) {
  return isSameDay(toZoned(a, timeZone), toZoned(b, timeZone));
}
var MINUTOS_POR_DIA = 24 * 60;
var DEFAULT_RENTAL_DAY_POLICY = { toleranceMinutes: 0 };
function rentalDays(pickupUtc, returnUtc, timeZone = APP_TIMEZONE, policy = DEFAULT_RENTAL_DAY_POLICY) {
  const { toleranceMinutes } = policy;
  if (!Number.isInteger(toleranceMinutes) || toleranceMinutes < 0 || toleranceMinutes >= MINUTOS_POR_DIA) {
    throw new RangeError(
      `Toler\xE2ncia inv\xE1lida: ${String(toleranceMinutes)}. Informe um n\xFAmero inteiro de minutos entre 0 e ${String(MINUTOS_POR_DIA - 1)}.`
    );
  }
  const pickup = toZoned(pickupUtc, timeZone);
  const dropoff = toZoned(returnUtc, timeZone);
  if (dropoff.getTime() < pickup.getTime()) {
    throw new RangeError("Per\xEDodo inv\xE1lido: a devolu\xE7\xE3o \xE9 anterior \xE0 retirada.");
  }
  const fullDays = differenceInDays(dropoff, pickup);
  const anchor = addDays(pickup, fullDays);
  const sobraMs = dropoff.getTime() - anchor.getTime();
  const toleranciaMs = toleranceMinutes * 6e4;
  const days = sobraMs > toleranciaMs ? fullDays + 1 : fullDays;
  return Math.max(days, 1);
}
function calendarDaysSpanned(startUtc, endUtc, timeZone = APP_TIMEZONE) {
  const start = startOfDay(toZoned(startUtc, timeZone));
  const end = startOfDay(toZoned(endUtc, timeZone));
  if (end.getTime() < start.getTime()) {
    throw new RangeError("Per\xEDodo inv\xE1lido: a data final \xE9 anterior \xE0 inicial.");
  }
  return differenceInDays(end, start) + 1;
}
var EMAIL_TAMANHO_MAXIMO = 254;
var SENHA_TAMANHO_MINIMO = 12;
var SENHA_TAMANHO_MAXIMO = 128;
var IDADE_MINIMA_TITULAR = 18;
var IDADE_MAXIMA_TITULAR = 120;
var SENHAS_PROIBIDAS = /* @__PURE__ */ new Set([
  "senha123456789",
  "123456789012",
  "1234567890123",
  "abcdefghijkl",
  "qwertyuiopas",
  "password1234",
  "locaveri1234",
  "administrador"
]);
var uuidSchema = z.uuid({ error: "identificador precisa ser um UUID v\xE1lido" });
var emailSchema = z.string({ error: "e-mail \xE9 obrigat\xF3rio" }).trim().toLowerCase().pipe(
  z.email({ error: "e-mail inv\xE1lido" }).max(EMAIL_TAMANHO_MAXIMO, {
    error: `e-mail pode ter no m\xE1ximo ${String(EMAIL_TAMANHO_MAXIMO)} caracteres`
  })
);
var CONTEM_LETRA = /\p{L}/u;
var nomeSchema = z.string({ error: "nome \xE9 obrigat\xF3rio" }).trim().min(2, { error: "nome precisa de pelo menos 2 caracteres" }).max(120, { error: "nome pode ter no m\xE1ximo 120 caracteres" }).refine((valor) => CONTEM_LETRA.test(valor), {
  error: "nome precisa conter pelo menos uma letra"
});
var NAO_DIGITO = /\D/g;
var DDDS_INVALIDOS = /* @__PURE__ */ new Set([
  "10",
  "20",
  "23",
  "25",
  "26",
  "29",
  "30",
  "36",
  "39",
  "40",
  "50",
  "52",
  "56",
  "57",
  "58",
  "59",
  "60",
  "70",
  "72",
  "76",
  "78",
  "80",
  "90"
]);
function normalizarTelefone(valor) {
  const digitos = valor.replace(NAO_DIGITO, "");
  if (digitos.length > 11 && digitos.startsWith("55")) {
    return digitos.slice(2);
  }
  return digitos;
}
function validarTelefone(valor) {
  const digitos = normalizarTelefone(valor);
  if (digitos.length !== 10 && digitos.length !== 11) {
    return false;
  }
  const ddd = digitos.slice(0, 2);
  if (ddd.startsWith("0") || DDDS_INVALIDOS.has(ddd)) {
    return false;
  }
  const assinante = digitos.slice(2);
  return digitos.length === 11 ? assinante.startsWith("9") : /^[2-5]/.test(assinante);
}
function formatarTelefone(valor) {
  const digitos = normalizarTelefone(valor);
  if (digitos.length === 11) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7)}`;
  }
  if (digitos.length === 10) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  }
  return digitos;
}
var telefoneSchema = z.string({ error: "telefone \xE9 obrigat\xF3rio" }).trim().refine(validarTelefone, { error: "telefone inv\xE1lido \u2014 informe DDD e n\xFAmero" }).transform(normalizarTelefone);
var senhaSchema = z.string({ error: "senha \xE9 obrigat\xF3ria" }).min(SENHA_TAMANHO_MINIMO, {
  error: `senha precisa de pelo menos ${String(SENHA_TAMANHO_MINIMO)} caracteres`
}).max(SENHA_TAMANHO_MAXIMO, {
  error: `senha pode ter no m\xE1ximo ${String(SENHA_TAMANHO_MAXIMO)} caracteres`
}).refine((valor) => !SENHAS_PROIBIDAS.has(valor.toLowerCase()), {
  error: "senha f\xE1cil demais \u2014 escolha outra"
}).refine((valor) => new Set(valor).size > 1, {
  error: "senha n\xE3o pode ser um \xFAnico caractere repetido"
});
var MENSAGEM_CONFIRMACAO_DE_SENHA = "a confirma\xE7\xE3o n\xE3o confere com a senha";
function senhasConferem(dados) {
  return dados.senha === dados.confirmacaoDeSenha;
}
function idadeEmAnos(nascimento, referencia = /* @__PURE__ */ new Date()) {
  let anos = referencia.getUTCFullYear() - nascimento.getUTCFullYear();
  const mes = referencia.getUTCMonth() - nascimento.getUTCMonth();
  if (mes < 0 || mes === 0 && referencia.getUTCDate() < nascimento.getUTCDate()) {
    anos -= 1;
  }
  return anos;
}
var dataDeNascimentoSchema = z.iso.date({ error: "data de nascimento precisa estar no formato aaaa-mm-dd" }).transform((valor) => /* @__PURE__ */ new Date(`${valor}T00:00:00.000Z`)).refine((data) => idadeEmAnos(data) >= IDADE_MINIMA_TITULAR, {
  error: `o titular precisa ter pelo menos ${String(IDADE_MINIMA_TITULAR)} anos`
}).refine((data) => idadeEmAnos(data) <= IDADE_MAXIMA_TITULAR, {
  error: "data de nascimento improv\xE1vel \u2014 confira o valor"
});
var TAMANHO_MAXIMO_DE_PAGINA = 100;
var paginacaoSchema = z.object({
  pagina: z.coerce.number({ error: "p\xE1gina precisa ser um n\xFAmero" }).int({ error: "p\xE1gina precisa ser inteira" }).min(1, { error: "p\xE1gina come\xE7a em 1" }).default(1),
  tamanho: z.coerce.number({ error: "tamanho precisa ser um n\xFAmero" }).int({ error: "tamanho precisa ser inteiro" }).min(1, { error: "tamanho m\xEDnimo \xE9 1" }).max(TAMANHO_MAXIMO_DE_PAGINA, {
    error: `tamanho m\xE1ximo \xE9 ${String(TAMANHO_MAXIMO_DE_PAGINA)}`
  }).default(20),
  busca: z.string().trim().max(120, { error: "busca pode ter no m\xE1ximo 120 caracteres" }).optional()
});
var CPF_TAMANHO = 11;
var CNPJ_TAMANHO = 14;
var APENAS_DIGITOS = /^\d+$/;
var APENAS_ALFANUMERICO_MAIUSCULO = /^[0-9A-Z]+$/;
var SEPARADORES = /[^0-9A-Za-z]/g;
function valorDoCaractere(caractere) {
  return caractere.charCodeAt(0) - 48;
}
function pesoCnpj(indice, tamanho) {
  return (tamanho - 1 - indice) % 8 + 2;
}
function digitoDoModulo11(soma) {
  const resto = soma % 11;
  return resto < 2 ? 0 : 11 - resto;
}
function todosIguais(valor) {
  return new Set(valor).size === 1;
}
function normalizarCpf(valor) {
  return valor.replace(SEPARADORES, "");
}
function digitosVerificadoresDoCpf(base) {
  const tamanhoBase = base.length;
  let soma = 0;
  base.forEach((valor, indice) => {
    soma += valor * (tamanhoBase + 1 - indice);
  });
  const primeiro2 = digitoDoModulo11(soma);
  const comPrimeiro = [...base, primeiro2];
  soma = 0;
  comPrimeiro.forEach((valor, indice) => {
    soma += valor * (tamanhoBase + 2 - indice);
  });
  const segundo = digitoDoModulo11(soma);
  return [primeiro2, segundo];
}
function validarCpf(valor) {
  const cpf = normalizarCpf(valor);
  if (cpf.length !== CPF_TAMANHO || !APENAS_DIGITOS.test(cpf)) {
    return false;
  }
  if (todosIguais(cpf.slice(0, 9))) {
    return false;
  }
  const numeros = Array.from(cpf, (caractere) => Number(caractere));
  const base = numeros.slice(0, 9);
  const [primeiro2, segundo] = digitosVerificadoresDoCpf(base);
  return numeros[9] === primeiro2 && numeros[10] === segundo;
}
function formatarCpf(valor) {
  const cpf = normalizarCpf(valor);
  if (cpf.length !== CPF_TAMANHO) {
    return cpf;
  }
  return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
}
function mascararCpf(valor) {
  const cpf = normalizarCpf(valor);
  if (cpf.length !== CPF_TAMANHO) {
    return "***";
  }
  return `***.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-**`;
}
function normalizarCnpj(valor) {
  return valor.replace(SEPARADORES, "").toUpperCase();
}
function digitosVerificadoresDoCnpj(base) {
  const valores = Array.from(base, valorDoCaractere);
  let soma = 0;
  valores.forEach((valor, indice) => {
    soma += valor * pesoCnpj(indice, valores.length);
  });
  const primeiro2 = digitoDoModulo11(soma);
  const comPrimeiro = [...valores, primeiro2];
  soma = 0;
  comPrimeiro.forEach((valor, indice) => {
    soma += valor * pesoCnpj(indice, comPrimeiro.length);
  });
  const segundo = digitoDoModulo11(soma);
  return [primeiro2, segundo];
}
function validarCnpj(valor) {
  const cnpj = normalizarCnpj(valor);
  if (cnpj.length !== CNPJ_TAMANHO || !APENAS_ALFANUMERICO_MAIUSCULO.test(cnpj)) {
    return false;
  }
  const base = cnpj.slice(0, 12);
  if (todosIguais(base)) {
    return false;
  }
  const informados = cnpj.slice(12);
  if (!APENAS_DIGITOS.test(informados)) {
    return false;
  }
  const [primeiro2, segundo] = digitosVerificadoresDoCnpj(base);
  return informados === `${String(primeiro2)}${String(segundo)}`;
}
function cnpjEhAlfanumerico(valor) {
  const cnpj = normalizarCnpj(valor);
  return cnpj.length === CNPJ_TAMANHO && !APENAS_DIGITOS.test(cnpj);
}
function formatarCnpj(valor) {
  const cnpj = normalizarCnpj(valor);
  if (cnpj.length !== CNPJ_TAMANHO) {
    return cnpj;
  }
  return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12)}`;
}
var cpfSchema = z.string({ error: "CPF \xE9 obrigat\xF3rio" }).trim().refine(validarCpf, { error: "CPF inv\xE1lido" }).transform(normalizarCpf);
var cnpjSchema = z.string({ error: "CNPJ \xE9 obrigat\xF3rio" }).trim().refine(validarCnpj, { error: "CNPJ inv\xE1lido" }).transform(normalizarCnpj);
var inscricaoEstadualSchema = z.string().trim().toUpperCase().max(20, { error: "inscri\xE7\xE3o estadual pode ter no m\xE1ximo 20 caracteres" }).refine((valor) => valor === "" || valor === "ISENTO" || /^[0-9.\-/]+$/.test(valor), {
  error: "inscri\xE7\xE3o estadual inv\xE1lida \u2014 use apenas n\xFAmeros ou a palavra ISENTO"
});
var CODIGOS_DE_PAPEL = [
  "SUPER_ADMIN",
  "GESTOR",
  "OPERACIONAL",
  "FINANCEIRO",
  "ATENDIMENTO",
  "CLIENTE",
  "VISITANTE"
];
var codigoDePapelSchema = z.enum(CODIGOS_DE_PAPEL, { error: "papel desconhecido" });
var PAPEIS = {
  SUPER_ADMIN: {
    codigo: "SUPER_ADMIN",
    nome: "Super administrador",
    descricao: "Acesso total, incluindo configura\xE7\xE3o do tenant e edi\xE7\xE3o do pr\xF3prio RBAC.",
    ehInterno: true,
    atribuivel: true
  },
  GESTOR: {
    codigo: "GESTOR",
    nome: "Gestor",
    descricao: "Tudo o que \xE9 operacional e financeiro. N\xE3o altera o cat\xE1logo de permiss\xF5es.",
    ehInterno: true,
    atribuivel: true
  },
  OPERACIONAL: {
    codigo: "OPERACIONAL",
    nome: "Operacional",
    descricao: "Frota, vistoria, aloca\xE7\xE3o e status. Sem acesso a faturamento.",
    ehInterno: true,
    atribuivel: true
  },
  FINANCEIRO: {
    codigo: "FINANCEIRO",
    nome: "Financeiro",
    descricao: "Custos, faturamento e relat\xF3rios. N\xE3o edita a frota.",
    ehInterno: true,
    atribuivel: true
  },
  ATENDIMENTO: {
    codigo: "ATENDIMENTO",
    nome: "Atendimento",
    descricao: "Reservas e clientes, com leitura da frota e da tabela de pre\xE7o.",
    ehInterno: true,
    atribuivel: true
  },
  CLIENTE: {
    codigo: "CLIENTE",
    nome: "Cliente",
    descricao: "Usu\xE1rio do app. Enxerga apenas os dados da pr\xF3pria conta.",
    ehInterno: false,
    atribuivel: true
  },
  VISITANTE: {
    codigo: "VISITANTE",
    nome: "Visitante",
    descricao: "Sess\xE3o an\xF4nima do site p\xFAblico. Cota e adota a pr\xF3pria cota\xE7\xE3o; n\xE3o tem usu\xE1rio nem conta.",
    ehInterno: false,
    atribuivel: false
  }
};
var PAPEIS_ATRIBUIVEIS = CODIGOS_DE_PAPEL.filter(
  (codigo) => PAPEIS[codigo].atribuivel
);
function ehPapelAtribuivel(codigo) {
  return PAPEIS[codigo].atribuivel;
}
var PAPEIS_INTERNOS = CODIGOS_DE_PAPEL.filter(
  (codigo) => PAPEIS[codigo].ehInterno
);
var PAPEIS_QUE_EXIGEM_2FA = PAPEIS_INTERNOS;
function exige2fa(papeis) {
  return papeis.some((papel) => PAPEIS_QUE_EXIGEM_2FA.includes(papel));
}
var RECURSOS = [
  "tenant",
  "account",
  "user",
  "role",
  "permission",
  "audit_log",
  "vehicle",
  "vehicle_category",
  "location",
  "vehicle_cost",
  "maintenance_plan",
  "cost_policy",
  "inspection",
  "price_table",
  "extra",
  "coupon",
  "quote",
  "service_provider",
  "waitlist",
  "rental_policy",
  "overbooking",
  "availability",
  "reservation",
  "allocation",
  "contract",
  "settlement",
  "incident",
  "traffic_fine",
  "telemetry",
  "invoice",
  "payment",
  "report",
  "dashboard",
  /**
   * A pesquisa de satisfação (Fase 6).
   *
   * Recurso próprio, e não uma ação de `report`, porque são duas capacidades
   * distintas: LER o resultado é auditoria — quem atende precisa saber que nota
   * levou — e EDITAR a configuração mexe na alavanca comercial (o limiar que
   * decide quem é mandado para a review pública do Google). Espremer as duas em
   * `report.read` daria a alavanca a quem só precisava do número.
   */
  "survey",
  /**
   * A superfície do SITE PÚBLICO, e um namespace só dela.
   *
   * Ela não reusa `vehicle_category` de propósito: essa MESMA chave
   * (`vehicle_category.read`) abre `GET /categories/:id`, que devolve a
   * contagem de frota por status. Dar ao visitante a permissão de "ler
   * categoria" para ele poder cotar entregaria, de brinde, quantos carros
   * blindados a locadora tem e quantos estão parados — um dado que nem o
   * cliente cadastrado vê. Namespace separado é o que impede o RBAC do painel
   * de ser herdado por carona.
   */
  "storefront",
  /**
   * A camada de IA (Fase 7) — recurso próprio, e não uma ação de `report`.
   *
   * Mesmo argumento do `survey`, com uma diferença que pesa mais: LER um
   * insight é ler uma interpretação dos relatórios, e MANDAR GERAR um gasta o
   * teto diário de tokens do tenant. A segunda é uma alavanca com custo — quem
   * a tem pode esgotar o orçamento do dia e deixar o briefing da manhã seguinte
   * sem gerar. Espremer as duas em `report.read` daria essa alavanca a todo
   * mundo que só queria ver o número.
   */
  "ai_insight",
  /**
   * Ocupação e ociosidade da frota SEM dinheiro — recurso próprio, e não uma
   * ação de `report`.
   *
   * A necessidade é da operação: saber qual carro ficou parado e quanto. O
   * caminho óbvio seria `report.read`, e ele já foi concedido e RETIRADO do
   * OPERACIONAL uma vez: `report.read` abre `GET /metrics/fleet`, que traz
   * receita, custo, margem e payback POR VEÍCULO. Ser ação de `report` também
   * não serviria — `permissoesDosRecursos(['report'])` a entregaria junto com o
   * resto do recurso a quem já tem relatório, e o dia em que alguém listar
   * `report` para um papel "sem financeiro" a porta abre de carona.
   *
   * O que a chave abre é `GET /metrics/fleet-occupancy`, cujo contrato é uma
   * lista FECHADA de campos sem valor monetário — e cujo serviço nem consulta
   * receita, custo ou overbooking.
   */
  "fleet_occupancy",
  /**
   * A análise cadastral do locatário no Mira Score
   * (`docs/integracao-mira-score.md`) — recurso próprio, por dois motivos.
   *
   * LER é ver dado de crédito de uma pessoa (restrições, processos, situação da
   * CNH): não cabe em `reservation.read`, que o OPERACIONAL e o FINANCEIRO têm.
   * PEDIR e CONFIGURAR têm custo — cada consulta é cobrada da locadora, e a
   * chave é a conta dela no Mira. Por isso nascem só com quem aprova reserva.
   */
  "credit_analysis"
];
var ACOES = [
  "create",
  "read",
  "update",
  "delete",
  "approve",
  "export",
  "quote",
  "adopt",
  // `cost` não é CRUD pelo mesmo motivo que `quote` e `adopt` não são: ela não
  // abre uma ROTA, abre um CAMPO. Ver `service_provider.cost` no catálogo.
  "cost"
];
var CATALOGO = [
  { recurso: "tenant", acao: "read", descricao: "Ver a configura\xE7\xE3o da locadora" },
  { recurso: "tenant", acao: "update", descricao: "Alterar a configura\xE7\xE3o da locadora" },
  { recurso: "account", acao: "create", descricao: "Cadastrar conta de cliente (PF ou PJ)" },
  { recurso: "account", acao: "read", descricao: "Consultar contas de cliente" },
  { recurso: "account", acao: "update", descricao: "Editar conta de cliente" },
  { recurso: "account", acao: "delete", descricao: "Encerrar conta de cliente" },
  { recurso: "user", acao: "create", descricao: "Criar usu\xE1rio interno" },
  { recurso: "user", acao: "read", descricao: "Consultar usu\xE1rios" },
  { recurso: "user", acao: "update", descricao: "Editar usu\xE1rio e atribuir pap\xE9is" },
  { recurso: "user", acao: "delete", descricao: "Bloquear ou remover usu\xE1rio" },
  { recurso: "role", acao: "read", descricao: "Consultar pap\xE9is" },
  { recurso: "role", acao: "update", descricao: "Alterar as permiss\xF5es de um papel" },
  { recurso: "permission", acao: "read", descricao: "Consultar o cat\xE1logo de permiss\xF5es" },
  { recurso: "audit_log", acao: "read", descricao: "Consultar a trilha de auditoria" },
  { recurso: "audit_log", acao: "export", descricao: "Exportar a trilha de auditoria" },
  { recurso: "vehicle", acao: "create", descricao: "Cadastrar ve\xEDculo" },
  { recurso: "vehicle", acao: "read", descricao: "Consultar a frota" },
  { recurso: "vehicle", acao: "update", descricao: "Editar ve\xEDculo e mudar seu status" },
  { recurso: "vehicle", acao: "delete", descricao: "Baixar ve\xEDculo da frota" },
  { recurso: "vehicle_category", acao: "create", descricao: "Criar categoria de ve\xEDculo" },
  { recurso: "vehicle_category", acao: "read", descricao: "Consultar categorias" },
  { recurso: "vehicle_category", acao: "update", descricao: "Editar categoria" },
  { recurso: "vehicle_category", acao: "delete", descricao: "Remover categoria" },
  { recurso: "location", acao: "create", descricao: "Cadastrar ponto de retirada" },
  { recurso: "location", acao: "read", descricao: "Consultar pontos de retirada" },
  { recurso: "location", acao: "update", descricao: "Editar ponto de retirada" },
  { recurso: "location", acao: "delete", descricao: "Desativar ponto de retirada" },
  { recurso: "vehicle_cost", acao: "create", descricao: "Lan\xE7ar custo de ve\xEDculo" },
  { recurso: "vehicle_cost", acao: "read", descricao: "Consultar custos da frota" },
  { recurso: "vehicle_cost", acao: "update", descricao: "Corrigir lan\xE7amento de custo" },
  { recurso: "vehicle_cost", acao: "delete", descricao: "Estornar lan\xE7amento de custo" },
  { recurso: "vehicle_cost", acao: "export", descricao: "Exportar custos da frota" },
  { recurso: "maintenance_plan", acao: "create", descricao: "Criar plano de revis\xE3o" },
  { recurso: "maintenance_plan", acao: "read", descricao: "Consultar planos de revis\xE3o" },
  { recurso: "maintenance_plan", acao: "update", descricao: "Editar plano de revis\xE3o" },
  { recurso: "maintenance_plan", acao: "delete", descricao: "Remover plano de revis\xE3o" },
  { recurso: "cost_policy", acao: "read", descricao: "Ver os par\xE2metros de custo indireto" },
  { recurso: "cost_policy", acao: "update", descricao: "Alterar os par\xE2metros de custo indireto" },
  { recurso: "inspection", acao: "create", descricao: "Registrar vistoria" },
  { recurso: "inspection", acao: "read", descricao: "Consultar vistorias" },
  { recurso: "inspection", acao: "update", descricao: "Corrigir vistoria" },
  { recurso: "price_table", acao: "create", descricao: "Criar tabela de pre\xE7o" },
  { recurso: "price_table", acao: "read", descricao: "Consultar tabelas de pre\xE7o" },
  { recurso: "price_table", acao: "update", descricao: "Editar tabela de pre\xE7o" },
  { recurso: "price_table", acao: "delete", descricao: "Remover tabela de pre\xE7o" },
  { recurso: "extra", acao: "create", descricao: "Criar extra (prote\xE7\xE3o, acess\xF3rio, servi\xE7o)" },
  { recurso: "extra", acao: "read", descricao: "Consultar extras" },
  { recurso: "extra", acao: "update", descricao: "Editar extra" },
  { recurso: "extra", acao: "delete", descricao: "Desativar extra" },
  { recurso: "coupon", acao: "create", descricao: "Criar cupom de desconto" },
  { recurso: "coupon", acao: "read", descricao: "Consultar cupons" },
  { recurso: "coupon", acao: "update", descricao: "Editar cupom" },
  { recurso: "coupon", acao: "delete", descricao: "Desativar cupom" },
  { recurso: "quote", acao: "create", descricao: "Cotar um per\xEDodo" },
  { recurso: "quote", acao: "read", descricao: "Consultar cota\xE7\xF5es emitidas" },
  { recurso: "service_provider", acao: "create", descricao: "Cadastrar motorista ou seguran\xE7a" },
  { recurso: "service_provider", acao: "read", descricao: "Consultar prestadores e sua escala" },
  {
    recurso: "service_provider",
    acao: "update",
    descricao: "Editar prestador e sua disponibilidade"
  },
  { recurso: "service_provider", acao: "delete", descricao: "Inativar prestador" },
  // ⚠️ CHAVE DE CAMPO, não de rota — a única do catálogo, e por isso explicada.
  //
  // `service_provider.read` abre `GET /providers`, e a resposta trazia o
  // `custoDiario` do motorista para todo mundo que alcançasse a rota: o
  // OPERACIONAL (que recebe o recurso inteiro) e o ATENDIMENTO. Isso contradiz
  // de frente a decisão logo abaixo, no bloco do OPERACIONAL, de NEGAR
  // `report.read` porque ele entrega margem por veículo: o papel é definido
  // como "sem financeiro" e recebia o custo da diária de cada prestador — que é
  // financeiro pelo mesmo critério, só que numa tela que ninguém olhou.
  //
  // Separar em chave própria, e não reusar `vehicle_cost.read`, é deliberado:
  // se um dia a operação precisar lançar custo de veículo, conceder aquela
  // chave reabriria esta porta sem que ninguém percebesse. Foi assim que
  // `report.read` quase entrou.
  { recurso: "service_provider", acao: "cost", descricao: "Ver o custo de di\xE1ria do prestador" },
  { recurso: "waitlist", acao: "create", descricao: "Registrar interesse sem disponibilidade" },
  { recurso: "waitlist", acao: "read", descricao: "Consultar a demanda reprimida" },
  { recurso: "waitlist", acao: "update", descricao: "Atualizar item da lista de espera" },
  { recurso: "rental_policy", acao: "read", descricao: "Ver a pol\xEDtica de contagem de di\xE1ria" },
  {
    recurso: "rental_policy",
    acao: "update",
    descricao: "Alterar a pol\xEDtica de contagem de di\xE1ria"
  },
  { recurso: "overbooking", acao: "create", descricao: "Registrar um overbooking exercido" },
  { recurso: "overbooking", acao: "read", descricao: "Consultar os overbookings e seu custo" },
  { recurso: "availability", acao: "read", descricao: "Consultar disponibilidade" },
  { recurso: "availability", acao: "update", descricao: "Bloquear ou liberar disponibilidade" },
  { recurso: "reservation", acao: "create", descricao: "Criar reserva" },
  { recurso: "reservation", acao: "read", descricao: "Consultar reservas" },
  { recurso: "reservation", acao: "update", descricao: "Alterar reserva" },
  { recurso: "reservation", acao: "delete", descricao: "Cancelar reserva" },
  { recurso: "reservation", acao: "approve", descricao: "Aprovar reserva que exige an\xE1lise" },
  {
    recurso: "credit_analysis",
    acao: "read",
    descricao: "Ver a an\xE1lise cadastral do locat\xE1rio (Mira Score)"
  },
  {
    recurso: "credit_analysis",
    acao: "create",
    descricao: "Pedir uma an\xE1lise cadastral fora da regra autom\xE1tica (tem custo)"
  },
  {
    recurso: "credit_analysis",
    acao: "update",
    descricao: "Configurar a chave do Mira Score da locadora"
  },
  { recurso: "allocation", acao: "create", descricao: "Alocar ve\xEDculo a uma reserva" },
  { recurso: "allocation", acao: "read", descricao: "Consultar aloca\xE7\xF5es" },
  { recurso: "allocation", acao: "update", descricao: "Trocar o ve\xEDculo alocado" },
  { recurso: "contract", acao: "create", descricao: "Gerar contrato" },
  { recurso: "contract", acao: "read", descricao: "Consultar contratos" },
  { recurso: "contract", acao: "export", descricao: "Baixar o PDF do contrato" },
  {
    recurso: "settlement",
    acao: "create",
    descricao: "Fechar a loca\xE7\xE3o (atraso, km, combust\xEDvel, avarias, cau\xE7\xE3o)"
  },
  { recurso: "settlement", acao: "read", descricao: "Consultar fechamentos" },
  { recurso: "incident", acao: "create", descricao: "Registrar sinistro" },
  { recurso: "incident", acao: "read", descricao: "Consultar sinistros" },
  { recurso: "incident", acao: "update", descricao: "Atualizar o andamento de um sinistro" },
  // Telemetria é o dado mais perigoso do sistema (Adendo E): a permissão
  // existe à parte de `vehicle.read` de propósito, e NENHUM papel a recebe por
  // padrão além do GESTOR (via "todas") — conceder é decisão explícita no
  // painel, e toda consulta é auditada.
  { recurso: "telemetry", acao: "read", descricao: "Consultar posi\xE7\xE3o e telemetria da frota" },
  {
    recurso: "telemetry",
    acao: "update",
    descricao: "Sincronizar frota e posi\xE7\xF5es com o rastreador"
  },
  { recurso: "traffic_fine", acao: "create", descricao: "Registrar multa de tr\xE2nsito" },
  { recurso: "traffic_fine", acao: "read", descricao: "Consultar multas" },
  {
    recurso: "traffic_fine",
    acao: "update",
    descricao: "Vincular multa, indicar condutor e registrar cobran\xE7a"
  },
  { recurso: "invoice", acao: "create", descricao: "Emitir fatura" },
  { recurso: "invoice", acao: "read", descricao: "Consultar faturas" },
  { recurso: "invoice", acao: "update", descricao: "Alterar fatura" },
  { recurso: "invoice", acao: "export", descricao: "Exportar faturas" },
  { recurso: "payment", acao: "create", descricao: "Registrar cobran\xE7a ou pr\xE9-autoriza\xE7\xE3o" },
  { recurso: "payment", acao: "read", descricao: "Consultar pagamentos" },
  { recurso: "payment", acao: "update", descricao: "Estornar ou ajustar pagamento" },
  { recurso: "report", acao: "read", descricao: "Ver relat\xF3rios gerenciais" },
  { recurso: "report", acao: "export", descricao: "Exportar relat\xF3rios" },
  // Fase 6 — a pesquisa de satisfação. NÃO existe `survey.create`: quem cria a
  // pesquisa é o fechamento da locação, e o cliente responde pelo LINK, sem
  // sessão e sem permissão nenhuma no caminho.
  {
    recurso: "survey",
    acao: "read",
    descricao: "Ver o resultado da pesquisa de satisfa\xE7\xE3o e o NPS"
  },
  {
    recurso: "survey",
    acao: "update",
    descricao: "Configurar a pesquisa \u2014 crit\xE9rios, escala e o limiar que envia \xE0 avalia\xE7\xE3o p\xFAblica"
  },
  { recurso: "dashboard", acao: "read", descricao: "Ver o painel de indicadores" },
  {
    recurso: "fleet_occupancy",
    acao: "read",
    descricao: "Ver ocupa\xE7\xE3o e ociosidade por ve\xEDculo, sem valores em dinheiro"
  },
  { recurso: "ai_insight", acao: "read", descricao: "Ver os insights gerados por IA" },
  {
    recurso: "ai_insight",
    acao: "create",
    descricao: "Mandar gerar um insight (consome o teto de IA)"
  },
  // Fase 6 — o site público. NÃO existe `storefront.read`: a LEITURA da vitrine
  // é anônima e cacheável, sem permissão nenhuma no caminho. O que precisa de
  // permissão é o que ESCREVE linha.
  {
    recurso: "storefront",
    acao: "quote",
    descricao: "Cotar pelo site p\xFAblico, sem conta (sess\xE3o de visitante)"
  },
  {
    recurso: "storefront",
    acao: "adopt",
    descricao: "Transferir a cota\xE7\xE3o an\xF4nima para a conta criada no fim do funil"
  }
];
var PERMISSOES = CATALOGO.map((entrada) => ({
  chave: `${entrada.recurso}.${entrada.acao}`,
  recurso: entrada.recurso,
  acao: entrada.acao,
  descricao: entrada.descricao
}));
var CHAVES_DE_PERMISSAO = PERMISSOES.map((item) => item.chave);
var CONJUNTO_DE_PERMISSOES = new Set(CHAVES_DE_PERMISSAO);
function ehPermissao(valor) {
  return CONJUNTO_DE_PERMISSOES.has(valor);
}
var permissaoSchema = z.string({ error: "permiss\xE3o \xE9 obrigat\xF3ria" }).refine(ehPermissao, { error: "permiss\xE3o desconhecida" });
function partesDaPermissao(chave) {
  const definicao = PERMISSOES.find((item) => item.chave === chave);
  if (definicao === void 0) {
    throw new Error(`Permiss\xE3o fora do cat\xE1logo: ${chave}`);
  }
  return { recurso: definicao.recurso, acao: definicao.acao };
}
function permissoesDosRecursos(recursos) {
  return PERMISSOES.filter((item) => recursos.includes(item.recurso)).map((item) => item.chave);
}
function exceto(base, remover) {
  return base.filter((chave) => !remover.includes(chave));
}
var TODAS = CHAVES_DE_PERMISSAO;
var PERMISSOES_POR_PAPEL = {
  // Único papel com o RBAC nas mãos — quem pode reescrever as permissões pode
  // se dar qualquer permissão, então essa capacidade fica em um papel só.
  SUPER_ADMIN: TODAS,
  GESTOR: exceto(TODAS, ["tenant.update", "role.update"]),
  // Frota, vistoria e status. O CLAUDE.md diz "sem financeiro", e custo É
  // financeiro: quem lança combustível é o FINANCEIRO. Se a operação precisar
  // lançar, o gestor concede `vehicle_cost.create` pelo painel — que é
  // exatamente para isso que o RBAC vive em tabela.
  OPERACIONAL: [
    // ⚠️ O `exceto` NÃO é decoração: `permissoesDosRecursos` expande o recurso
    // para TODAS as ações dele, então a chave nova entraria aqui sozinha — e a
    // correção que ela existe para fazer nasceria desfeita.
    ...exceto(
      permissoesDosRecursos([
        "vehicle",
        "vehicle_category",
        "maintenance_plan",
        "inspection",
        "allocation",
        "availability",
        "service_provider"
      ]),
      ["service_provider.cost"]
    ),
    "account.read",
    "location.read",
    "extra.read",
    "quote.read",
    "waitlist.read",
    "reservation.read",
    "reservation.update",
    "contract.read",
    // Fase 4 — a operação fecha a locação e cuida do sinistro; a multa ela lê.
    "settlement.create",
    "settlement.read",
    "incident.create",
    "incident.read",
    "incident.update",
    "traffic_fine.read",
    "dashboard.read",
    // ⚠️ O OPERACIONAL NÃO TEM `report.read` — e a tentação de dar é real.
    //
    // Numa versão anterior desta fase eu concedi, com o argumento de que a
    // operação precisa saber qual carro está parado e qual vai para manutenção.
    // O argumento é verdadeiro e a conclusão estava errada: `report.read` abre
    // `GET /metrics/fleet`, e a resposta desse endpoint traz `receita`,
    // `custoDireto`, `margem`, `margemPercentual`, `custoPorKm` e `payback`
    // POR VEÍCULO.
    //
    // O CLAUDE.md define este papel como "frota, vistoria, status — SEM
    // FINANCEIRO". Conceder `report.read` para obter ociosidade entrega a
    // rentabilidade da locadora inteira junto, sem exploit nenhum: basta abrir
    // a tela.
    //
    // A necessidade continua de pé e a resposta certa é outra: uma visão de
    // ocupação e ociosidade SEM dinheiro, com chave própria — que é esta.
    "fleet_occupancy.read"
  ],
  FINANCEIRO: [
    ...permissoesDosRecursos([
      "invoice",
      "payment",
      "report",
      "vehicle_cost",
      "cost_policy",
      "overbooking"
    ]),
    "account.read",
    "account.update",
    "audit_log.read",
    "audit_log.export",
    "contract.read",
    "contract.export",
    "price_table.read",
    "price_table.update",
    "reservation.read",
    "vehicle.read",
    "vehicle_category.read",
    "location.read",
    "maintenance_plan.read",
    "extra.read",
    "coupon.read",
    "quote.read",
    "rental_policy.read",
    "service_provider.read",
    // Quem lança e concilia custo vê o custo. É o mesmo critério que dá a este
    // papel `vehicle_cost` inteiro e nega `report.read` ao OPERACIONAL.
    "service_provider.cost",
    "waitlist.read",
    // Fase 4 — quem cobra a multa do cliente é o financeiro; o fechamento e o
    // sinistro ele lê para conciliar.
    "traffic_fine.create",
    "traffic_fine.read",
    "traffic_fine.update",
    "settlement.read",
    "incident.read",
    "dashboard.read",
    // ⚠️ `ai_insight` NÃO entra por `permissoesDosRecursos(['report'])` acima —
    // é recurso próprio, e por isso precisa desta linha. Só `read`: o
    // financeiro acompanha o custo da camada, não decide quando ela roda.
    "ai_insight.read"
  ],
  ATENDIMENTO: [
    "account.create",
    "account.read",
    "account.update",
    "reservation.create",
    "reservation.read",
    "reservation.update",
    "reservation.delete",
    "allocation.read",
    "availability.read",
    "contract.create",
    "contract.read",
    "contract.export",
    "inspection.read",
    "invoice.read",
    "price_table.read",
    "vehicle.read",
    "vehicle_category.read",
    "location.read",
    // Atendimento cota, e cotar é criar `QuoteSnapshot` — daí o `create`.
    "extra.read",
    "coupon.read",
    "quote.create",
    "quote.read",
    "service_provider.read",
    // Quem atende precisa saber a nota que levou — é o insumo do próprio
    // trabalho. Configurar a pesquisa, não: o limiar de redirecionamento à
    // review pública é alavanca comercial, e ela fica com o gestor.
    "survey.read",
    "waitlist.create",
    "waitlist.read",
    "waitlist.update",
    // Fase 4 — o atendimento explica a conta e a multa ao cliente; não as cria.
    "settlement.read",
    "traffic_fine.read",
    "dashboard.read",
    // O atendente cota pelo site quando monta a reserva assistida do Gap 13.
    "storefront.quote"
  ],
  // O escopo "só os próprios dados" NÃO é expresso aqui: permissão responde
  // "pode ler reserva?", propriedade responde "qual reserva?". A segunda
  // pergunta é da camada de serviço, com o `accountId` do token.
  CLIENTE: [
    "account.read",
    "account.update",
    "availability.read",
    "contract.read",
    "contract.export",
    "invoice.read",
    "payment.create",
    "payment.read",
    // ⚠️ SEM `price_table.read`. As 12 rotas que essa chave abre são o CRUD de
    // preço do painel — tabelas, tarifas, sazonalidade, dia da semana,
    // antecedência e ocupação —, e listar tabelas pelo NOME entrega a estrutura
    // comercial da locadora, inclusive a existência de acordo negociado com o
    // concorrente do próprio cliente.
    //
    // O app nunca precisou dela: ele consulta `/public/pricing/from`
    // (`apps/mobile/lib/rotas.ts:91`), que é rota de vitrine, e o preço da
    // reserva vem congelado no `QuoteSnapshot`.
    "reservation.create",
    "reservation.read",
    "reservation.update",
    // Fase 4 — o cliente vê o fechamento da própria locação no app.
    "settlement.read",
    "vehicle_category.read",
    // O app precisa listar onde retirar; sem isto a Fase 6 nasce sem mapa.
    "location.read",
    // O cliente cota e, sem disponibilidade, deixa a intenção registrada.
    "extra.read",
    "quote.create",
    "quote.read",
    "waitlist.create",
    // O cliente que já entrou pode adotar a cotação que fez antes de logar
    // (rota 12) — a mesma prova por assinatura, agora com token de usuário.
    "storefront.quote"
  ],
  /**
   * O visitante do site: TRÊS permissões, e nenhuma delas lê catálogo.
   *
   * A leitura da vitrine é anônima (`@RotaDeVitrine`), então não há nada a
   * conceder ali. O que ele pode é gravar as três linhas que o funil exige:
   * a cotação, a adoção dela pela conta recém-criada e a intenção na waitlist
   * quando não há carro na data.
   *
   * `vehicle_category.read` fica FORA de propósito — ver o comentário do
   * recurso `storefront` em `RECURSOS`.
   */
  VISITANTE: ["storefront.quote", "storefront.adopt", "waitlist.create"]
};
function permissoesDosPapeis(papeis) {
  const uniao = /* @__PURE__ */ new Set();
  for (const papel of papeis) {
    for (const chave of PERMISSOES_POR_PAPEL[papel]) {
      uniao.add(chave);
    }
  }
  return CHAVES_DE_PERMISSAO.filter((chave) => uniao.has(chave));
}

// src/frota.ts
var frota_exports = {};
__export(frota_exports, {
  ANO_MAXIMO_DE_FABRICACAO: () => ANO_MAXIMO_DE_FABRICACAO,
  ANO_MINIMO_DE_FABRICACAO: () => ANO_MINIMO_DE_FABRICACAO,
  DIAS_DA_SEMANA: () => DIAS_DA_SEMANA,
  DIAS_PARA_ALERTA_CRITICO: () => DIAS_PARA_ALERTA_CRITICO,
  DIAS_PARA_ALERTA_DE_ATENCAO: () => DIAS_PARA_ALERTA_DE_ATENCAO,
  GRAVIDADES_DE_ALERTA: () => GRAVIDADES_DE_ALERTA,
  IDADE_MAXIMA_ADMITIDA: () => IDADE_MAXIMA_ADMITIDA,
  IDADE_MINIMA_LEGAL_PARA_DIRIGIR: () => IDADE_MINIMA_LEGAL_PARA_DIRIGIR,
  MESES_DE_RATEIO_MAXIMO: () => MESES_DE_RATEIO_MAXIMO,
  NIVEIS_DE_BLINDAGEM: () => NIVEIS_DE_BLINDAGEM,
  ORIGENS_DE_CUSTO: () => ORIGENS_DE_CUSTO,
  PONTOS_BASE_MAXIMOS: () => PONTOS_BASE_MAXIMOS,
  ROTULO_DO_ALERTA: () => ROTULO_DO_ALERTA,
  ROTULO_DO_CAMBIO: () => ROTULO_DO_CAMBIO,
  ROTULO_DO_DIA: () => ROTULO_DO_DIA,
  ROTULO_DO_NIVEL_DE_BLINDAGEM: () => ROTULO_DO_NIVEL_DE_BLINDAGEM,
  ROTULO_DO_STATUS_DE_VEICULO: () => ROTULO_DO_STATUS_DE_VEICULO,
  ROTULO_DO_TIPO_DE_CUSTO: () => ROTULO_DO_TIPO_DE_CUSTO,
  ROTULO_DO_TIPO_DE_LOCAL: () => ROTULO_DO_TIPO_DE_LOCAL,
  STATUS_CONTROLADOS_PELA_RESERVA: () => STATUS_CONTROLADOS_PELA_RESERVA,
  STATUS_DE_VEICULO: () => STATUS_DE_VEICULO,
  STATUS_OPERACIONAIS: () => STATUS_OPERACIONAIS,
  TIPOS_DE_ALERTA: () => TIPOS_DE_ALERTA,
  TIPOS_DE_CAMBIO: () => TIPOS_DE_CAMBIO,
  TIPOS_DE_CUSTO_DE_VEICULO: () => TIPOS_DE_CUSTO_DE_VEICULO,
  TIPOS_DE_CUSTO_LANCAVEIS: () => TIPOS_DE_CUSTO_LANCAVEIS,
  TIPOS_DE_LOCAL: () => TIPOS_DE_LOCAL,
  TIPOS_RATEAVEIS: () => TIPOS_RATEAVEIS,
  TIPOS_SEM_CONTRAPARTIDA_EM_CAIXA: () => TIPOS_SEM_CONTRAPARTIDA_EM_CAIXA,
  UNIDADES_FEDERATIVAS: () => UNIDADES_FEDERATIVAS,
  atualizarCategoriaSchema: () => atualizarCategoriaSchema,
  atualizarCustoSchema: () => atualizarCustoSchema,
  atualizarLocalSchema: () => atualizarLocalSchema,
  atualizarVeiculoSchema: () => atualizarVeiculoSchema,
  cepSchema: () => cepSchema,
  chassiSchema: () => chassiSchema,
  chaveDeFotoDeCategoriaSchema: () => chaveDeFotoDeCategoriaSchema,
  criarCategoriaSchema: () => criarCategoriaSchema,
  criarLocalSchema: () => criarLocalSchema,
  criarVeiculoSchema: () => criarVeiculoSchema,
  faixaDeHorarioSchema: () => faixaDeHorarioSchema,
  filtroDeAlertaSchema: () => filtroDeAlertaSchema,
  filtroDeCategoriaSchema: () => filtroDeCategoriaSchema,
  filtroDeCustoSchema: () => filtroDeCustoSchema,
  filtroDeLocalSchema: () => filtroDeLocalSchema,
  filtroDeVeiculoSchema: () => filtroDeVeiculoSchema,
  formatarCep: () => formatarCep,
  formatarPlaca: () => formatarPlaca,
  fotoDaGaleriaSchema: () => fotoDaGaleriaSchema,
  gerarSlug: () => gerarSlug,
  gravidadeDoVencimento: () => gravidadeDoVencimento,
  horariosSchema: () => horariosSchema,
  lancarCustoSchema: () => lancarCustoSchema,
  mudarStatusDoVeiculoSchema: () => mudarStatusDoVeiculoSchema,
  nivelDeBlindagemSchema: () => nivelDeBlindagemSchema,
  normalizarCep: () => normalizarCep,
  normalizarChassi: () => normalizarChassi,
  normalizarPlaca: () => normalizarPlaca,
  normalizarRenavam: () => normalizarRenavam,
  origemDoCustoSchema: () => origemDoCustoSchema,
  placaSchema: () => placaSchema,
  renavamSchema: () => renavamSchema,
  salvarPlanoDeManutencaoSchema: () => salvarPlanoDeManutencaoSchema,
  salvarPoliticaDeCustoSchema: () => salvarPoliticaDeCustoSchema,
  statusDoVeiculoSchema: () => statusDoVeiculoSchema,
  tipoDeCambioSchema: () => tipoDeCambioSchema,
  tipoDeCustoDeVeiculoSchema: () => tipoDeCustoDeVeiculoSchema,
  tipoDeLocalSchema: () => tipoDeLocalSchema,
  ufSchema: () => ufSchema,
  validarChassi: () => validarChassi,
  validarPlaca: () => validarPlaca
});
var NIVEIS_DE_BLINDAGEM = ["I", "II_A", "II", "III_A", "III", "IV"];
var nivelDeBlindagemSchema = z.enum(NIVEIS_DE_BLINDAGEM, {
  error: "n\xEDvel de blindagem inv\xE1lido"
});
var ROTULO_DO_NIVEL_DE_BLINDAGEM = {
  I: "N\xEDvel I",
  II_A: "N\xEDvel II-A",
  II: "N\xEDvel II",
  III_A: "N\xEDvel III-A",
  III: "N\xEDvel III",
  IV: "N\xEDvel IV"
};
var TIPOS_DE_CAMBIO = ["MANUAL", "AUTOMATICO", "AUTOMATIZADO"];
var tipoDeCambioSchema = z.enum(TIPOS_DE_CAMBIO, { error: "tipo de c\xE2mbio inv\xE1lido" });
var ROTULO_DO_CAMBIO = {
  MANUAL: "Manual",
  AUTOMATICO: "Autom\xE1tico",
  AUTOMATIZADO: "Automatizado"
};
var STATUS_DE_VEICULO = [
  "DISPONIVEL",
  "RESERVADO",
  "EM_LOCACAO",
  "LIMPEZA",
  "MANUTENCAO_PREVENTIVA",
  "MANUTENCAO_CORRETIVA",
  "FUNILARIA",
  "SINISTRO",
  "DOCUMENTACAO",
  "INDISPONIVEL",
  "EM_VENDA"
];
var statusDoVeiculoSchema = z.enum(STATUS_DE_VEICULO, {
  error: "status de ve\xEDculo inv\xE1lido"
});
var STATUS_CONTROLADOS_PELA_RESERVA = [
  "RESERVADO",
  "EM_LOCACAO"
];
var STATUS_OPERACIONAIS = STATUS_DE_VEICULO.filter(
  (status) => !STATUS_CONTROLADOS_PELA_RESERVA.includes(status)
);
var ROTULO_DO_STATUS_DE_VEICULO = {
  DISPONIVEL: { rotulo: "Dispon\xEDvel", intencao: "positiva" },
  RESERVADO: { rotulo: "Reservado", intencao: "informativa" },
  EM_LOCACAO: { rotulo: "Em loca\xE7\xE3o", intencao: "informativa" },
  LIMPEZA: { rotulo: "Limpeza", intencao: "neutra" },
  MANUTENCAO_PREVENTIVA: { rotulo: "Manuten\xE7\xE3o preventiva", intencao: "atencao" },
  MANUTENCAO_CORRETIVA: { rotulo: "Manuten\xE7\xE3o corretiva", intencao: "atencao" },
  FUNILARIA: { rotulo: "Funilaria", intencao: "atencao" },
  SINISTRO: { rotulo: "Sinistro", intencao: "critica" },
  DOCUMENTACAO: { rotulo: "Documenta\xE7\xE3o", intencao: "atencao" },
  INDISPONIVEL: { rotulo: "Indispon\xEDvel", intencao: "neutra" },
  EM_VENDA: { rotulo: "Em venda", intencao: "neutra" }
};
var TIPOS_DE_CUSTO_DE_VEICULO = [
  "COMBUSTIVEL",
  "LAVAGEM",
  "MANUTENCAO",
  "PNEU",
  "SEGURO",
  "IPVA",
  "LICENCIAMENTO",
  "MULTA",
  "BLINDAGEM",
  "DEPRECIACAO",
  "CUSTO_DE_CAPITAL",
  "OVERHEAD",
  "OUTRO"
];
var tipoDeCustoDeVeiculoSchema = z.enum(TIPOS_DE_CUSTO_DE_VEICULO, {
  error: "tipo de custo inv\xE1lido"
});
var ROTULO_DO_TIPO_DE_CUSTO = {
  COMBUSTIVEL: "Combust\xEDvel",
  LAVAGEM: "Lavagem",
  MANUTENCAO: "Manuten\xE7\xE3o",
  PNEU: "Pneu",
  SEGURO: "Seguro",
  IPVA: "IPVA",
  LICENCIAMENTO: "Licenciamento",
  MULTA: "Multa",
  BLINDAGEM: "Blindagem",
  DEPRECIACAO: "Deprecia\xE7\xE3o",
  CUSTO_DE_CAPITAL: "Custo de capital",
  OVERHEAD: "Overhead",
  OUTRO: "Outro"
};
var ORIGENS_DE_CUSTO = ["LANCAMENTO_MANUAL", "CALCULO_AUTOMATICO"];
var origemDoCustoSchema = z.enum(ORIGENS_DE_CUSTO, { error: "origem de custo inv\xE1lida" });
var TIPOS_SEM_CONTRAPARTIDA_EM_CAIXA = [
  "DEPRECIACAO",
  "CUSTO_DE_CAPITAL",
  "OVERHEAD"
];
var TIPOS_DE_CUSTO_LANCAVEIS = TIPOS_DE_CUSTO_DE_VEICULO.filter((tipo) => !TIPOS_SEM_CONTRAPARTIDA_EM_CAIXA.includes(tipo));
var TIPOS_RATEAVEIS = [
  "IPVA",
  "LICENCIAMENTO",
  "SEGURO",
  "BLINDAGEM"
];
var TIPOS_DE_LOCAL = ["SEDE", "FILIAL", "AEROPORTO", "PONTO_DE_ENTREGA"];
var tipoDeLocalSchema = z.enum(TIPOS_DE_LOCAL, { error: "tipo de local inv\xE1lido" });
var ROTULO_DO_TIPO_DE_LOCAL = {
  SEDE: "Sede",
  FILIAL: "Filial",
  AEROPORTO: "Aeroporto",
  PONTO_DE_ENTREGA: "Ponto de entrega"
};
var NAO_ALFANUMERICO = /[^A-Za-z0-9]/g;
var APENAS_DIGITOS2 = /[^0-9]/g;
var PLACA_PATTERN = /^[A-Z]{3}[0-9][0-9A-Z][0-9]{2}$/;
var CHASSI_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;
var RENAVAM_PATTERN = /^[0-9]{9,11}$/;
var CEP_PATTERN = /^[0-9]{8}$/;
var HORA_PATTERN = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;
function normalizarPlaca(valor) {
  return valor.replace(NAO_ALFANUMERICO, "").toUpperCase();
}
function validarPlaca(valor) {
  return PLACA_PATTERN.test(normalizarPlaca(valor));
}
function formatarPlaca(valor) {
  const placa = normalizarPlaca(valor);
  return placa.length === 7 ? `${placa.slice(0, 3)}-${placa.slice(3)}` : placa;
}
function normalizarChassi(valor) {
  return valor.replace(NAO_ALFANUMERICO, "").toUpperCase();
}
function validarChassi(valor) {
  return CHASSI_PATTERN.test(normalizarChassi(valor));
}
function normalizarRenavam(valor) {
  return valor.replace(APENAS_DIGITOS2, "");
}
function normalizarCep(valor) {
  return valor.replace(APENAS_DIGITOS2, "");
}
function formatarCep(valor) {
  const cep = normalizarCep(valor);
  return cep.length === 8 ? `${cep.slice(0, 5)}-${cep.slice(5)}` : cep;
}
var placaSchema = z.string({ error: "placa \xE9 obrigat\xF3ria" }).transform(normalizarPlaca).refine((valor) => PLACA_PATTERN.test(valor), {
  error: "placa inv\xE1lida \u2014 use o padr\xE3o ABC1234 ou ABC1D23"
});
var chassiSchema = z.string({ error: "chassi \xE9 obrigat\xF3rio" }).transform(normalizarChassi).refine((valor) => CHASSI_PATTERN.test(valor), {
  error: "chassi inv\xE1lido \u2014 s\xE3o 17 caracteres, sem as letras I, O e Q"
});
var renavamSchema = z.string({ error: "RENAVAM \xE9 obrigat\xF3rio" }).transform(normalizarRenavam).refine((valor) => RENAVAM_PATTERN.test(valor), {
  error: "RENAVAM inv\xE1lido \u2014 s\xE3o 9 a 11 d\xEDgitos"
});
var cepSchema = z.string({ error: "CEP \xE9 obrigat\xF3rio" }).transform(normalizarCep).refine((valor) => CEP_PATTERN.test(valor), { error: "CEP inv\xE1lido \u2014 s\xE3o 8 d\xEDgitos" });
var UNIDADES_FEDERATIVAS = [
  "AC",
  "AL",
  "AM",
  "AP",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MG",
  "MS",
  "MT",
  "PA",
  "PB",
  "PE",
  "PI",
  "PR",
  "RJ",
  "RN",
  "RO",
  "RR",
  "RS",
  "SC",
  "SE",
  "SP",
  "TO"
];
var ufSchema = z.string({ error: "UF \xE9 obrigat\xF3ria" }).transform((valor) => valor.trim().toUpperCase()).refine(
  (valor) => UNIDADES_FEDERATIVAS.includes(valor),
  { error: "UF inv\xE1lida" }
);
var DIAS_DA_SEMANA = [
  "domingo",
  "segunda",
  "terca",
  "quarta",
  "quinta",
  "sexta",
  "sabado"
];
var ROTULO_DO_DIA = {
  domingo: "Domingo",
  segunda: "Segunda",
  terca: "Ter\xE7a",
  quarta: "Quarta",
  quinta: "Quinta",
  sexta: "Sexta",
  sabado: "S\xE1bado"
};
var horaSchema = z.string({ error: "hor\xE1rio \xE9 obrigat\xF3rio" }).regex(HORA_PATTERN, { error: "hor\xE1rio inv\xE1lido \u2014 use HH:MM" });
var faixaDeHorarioSchema = z.object({ abre: horaSchema, fecha: horaSchema }).refine((faixa) => faixa.abre < faixa.fecha, {
  error: "o hor\xE1rio de fechamento precisa ser depois do de abertura",
  path: ["fecha"]
});
var horariosSchema = z.object(
  Object.fromEntries(
    DIAS_DA_SEMANA.map((dia) => [dia, z.array(faixaDeHorarioSchema).default([])])
  )
);
var SLUG_PATTERN = /^[a-z0-9-]+$/;
function gerarSlug(valor) {
  return valor.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+/, "").replace(/-+$/, "");
}
var IDADE_MINIMA_LEGAL_PARA_DIRIGIR = 18;
var IDADE_MAXIMA_ADMITIDA = 120;
var PONTOS_BASE_MAXIMOS = 1e4;
var inteiroPositivo = (rotulo) => z.coerce.number({ error: `${rotulo} \xE9 obrigat\xF3rio` }).int({ error: `${rotulo} precisa ser inteiro` }).positive({ error: `${rotulo} precisa ser maior que zero` });
var inteiroNaoNegativo = (rotulo) => z.coerce.number({ error: `${rotulo} \xE9 obrigat\xF3rio` }).int({ error: `${rotulo} precisa ser inteiro` }).min(0, { error: `${rotulo} n\xE3o pode ser negativo` });
var pontosBaseSchema = z.coerce.number({ error: "valor em pontos-base \xE9 obrigat\xF3rio" }).int({ error: "pontos-base precisam ser inteiros" }).min(0, { error: "pontos-base n\xE3o podem ser negativos" }).max(PONTOS_BASE_MAXIMOS, { error: `o m\xE1ximo \xE9 ${String(PONTOS_BASE_MAXIMOS)} (100%)` });
var chaveDeFotoDeCategoriaSchema = z.string({ error: "a foto da galeria \xE9 obrigat\xF3ria" }).regex(/^tenants\/[0-9a-f-]{36}\/categorias\/[0-9a-f-]{36}\.(jpg|png|webp)$/, {
  error: "chave de foto inv\xE1lida \u2014 use a chave devolvida pelo upload assinado"
});
var fotoDaGaleriaSchema = z.union([chaveDeFotoDeCategoriaSchema, z.url()], {
  error: "a galeria aceita a chave devolvida pelo upload ou uma URL completa"
});
var criarCategoriaSchema = z.object({
  nome: z.string({ error: "nome \xE9 obrigat\xF3rio" }).trim().min(2, { error: "nome precisa ter ao menos 2 caracteres" }).max(120, { error: "nome muito longo" }),
  /** Ausente, é derivado do nome — o gestor não precisa saber o que é slug. */
  slug: z.string().trim().toLowerCase().regex(SLUG_PATTERN, { error: "slug aceita apenas letras min\xFAsculas, n\xFAmeros e h\xEDfen" }).optional(),
  /**
   * `nullish` e não `optional`: sem o `null`, a descrição pode ser SUBSTITUÍDA
   * mas nunca esvaziada — quem escreveu um texto errado fica preso a ele. Na
   * edição, ausente significa "não mexa" e `null` significa "apague".
   */
  descricao: z.string().trim().max(2e3, { error: "descri\xE7\xE3o muito longa" }).nullish(),
  nivelDeBlindagem: nivelDeBlindagemSchema,
  lugares: inteiroPositivo("n\xFAmero de lugares"),
  portas: inteiroPositivo("n\xFAmero de portas"),
  malas: inteiroNaoNegativo("n\xFAmero de malas"),
  cambio: tipoDeCambioSchema,
  modelosRepresentativos: z.string({ error: "modelos representativos \xE9 obrigat\xF3rio" }).trim().min(2, { error: "informe ao menos um modelo de refer\xEAncia" }).max(300, { error: "texto muito longo" }),
  galeria: z.array(fotoDaGaleriaSchema).max(20).default([]),
  idadeMinimaDoCondutor: z.coerce.number({ error: "idade m\xEDnima \xE9 obrigat\xF3ria" }).int({ error: "idade m\xEDnima precisa ser inteira" }).min(IDADE_MINIMA_LEGAL_PARA_DIRIGIR, {
    error: `a idade m\xEDnima n\xE3o pode ser menor que ${String(IDADE_MINIMA_LEGAL_PARA_DIRIGIR)}`
  }).max(IDADE_MAXIMA_ADMITIDA, { error: "idade m\xEDnima implaus\xEDvel" }),
  tempoMinimoDeCnhEmAnos: inteiroNaoNegativo("tempo m\xEDnimo de CNH"),
  caucaoBase: moneySchemaNonNegative,
  kmDiarioIncluso: inteiroNaoNegativo("km di\xE1rio incluso"),
  valorDoKmExcedente: moneySchemaNonNegative,
  /**
   * O que custa encher o tanque (Fase 4): base da cobrança proporcional de
   * combustível no fechamento. Ausente ou zero = não cobra combustível na
   * devolução. `optional` e não `.default(0n)`: um default `bigint` não
   * sobrevive à serialização do OpenAPI — quem aplica o zero é o banco.
   */
  valorDoTanqueCheio: moneySchemaNonNegative.optional(),
  /**
   * Parcelamento configurável POR CATEGORIA (Fase 5): quantas parcelas a venda
   * aceita (1 = à vista) e o piso de cada parcela. O teto de produto (12) e o
   * plano determinístico moram em `pagamento.ts` — a categoria só declara a
   * política. `optional`: quem aplica o default é o banco (1 e 0).
   */
  maximoDeParcelas: z.coerce.number({ error: "m\xE1ximo de parcelas \xE9 obrigat\xF3rio" }).int({ error: "parcelas \xE9 um n\xFAmero inteiro" }).min(1, { error: "no m\xEDnimo 1 (\xE0 vista)" }).max(12, { error: "no m\xE1ximo 12 parcelas" }).optional(),
  valorMinimoDaParcela: moneySchemaNonNegative.optional(),
  /** `null` herda da política do tenant. Ausente é o mesmo que `null`. */
  vidaUtilEmMeses: inteiroPositivo("vida \xFAtil em meses").nullish(),
  residualEmPontosBase: pontosBaseSchema.nullish(),
  ordemDeExibicao: inteiroNaoNegativo("ordem de exibi\xE7\xE3o").default(0),
  ativo: z.boolean().default(true)
});
var atualizarCategoriaSchema = criarCategoriaSchema.partial();
var filtroDeCategoriaSchema = paginacaoSchema.extend({
  busca: z.string().trim().max(120).optional(),
  nivelDeBlindagem: nivelDeBlindagemSchema.optional(),
  ativo: z.stringbool().optional()
});
var criarLocalSchema = z.object({
  nome: z.string({ error: "nome \xE9 obrigat\xF3rio" }).trim().min(2, { error: "nome precisa ter ao menos 2 caracteres" }).max(120, { error: "nome muito longo" }),
  tipo: tipoDeLocalSchema,
  logradouro: z.string({ error: "logradouro \xE9 obrigat\xF3rio" }).trim().min(2).max(200),
  numero: z.string({ error: "n\xFAmero \xE9 obrigat\xF3rio" }).trim().min(1).max(20),
  complemento: z.string().trim().max(100).optional(),
  bairro: z.string({ error: "bairro \xE9 obrigat\xF3rio" }).trim().min(2).max(100),
  cidade: z.string({ error: "cidade \xE9 obrigat\xF3ria" }).trim().min(2).max(100),
  uf: ufSchema,
  cep: cepSchema,
  latitude: z.coerce.number().min(-90).max(90).nullish(),
  longitude: z.coerce.number().min(-180).max(180).nullish(),
  horarios: horariosSchema.optional(),
  taxaDeEntrega: moneySchemaNonNegative.default(zero),
  taxaDeDevolucao: moneySchemaNonNegative.default(zero),
  ativo: z.boolean().default(true)
});
var atualizarLocalSchema = criarLocalSchema.partial();
var filtroDeLocalSchema = paginacaoSchema.extend({
  busca: z.string().trim().max(120).optional(),
  tipo: tipoDeLocalSchema.optional(),
  ativo: z.stringbool().optional()
});
var ANO_MINIMO_DE_FABRICACAO = 1900;
var ANO_MAXIMO_DE_FABRICACAO = 2200;
var anoSchema = z.coerce.number({ error: "ano \xE9 obrigat\xF3rio" }).int({ error: "ano precisa ser inteiro" }).min(ANO_MINIMO_DE_FABRICACAO, { error: "ano implaus\xEDvel" }).max(ANO_MAXIMO_DE_FABRICACAO, { error: "ano implaus\xEDvel" });
var dataSimplesSchema = z.iso.date({ error: "data inv\xE1lida \u2014 use o formato AAAA-MM-DD" });
var criarVeiculoSchema = z.object({
  categoryId: uuidSchema,
  locationId: uuidSchema.nullish(),
  marca: z.string({ error: "marca \xE9 obrigat\xF3ria" }).trim().min(1).max(60),
  modelo: z.string({ error: "modelo \xE9 obrigat\xF3rio" }).trim().min(1).max(80),
  anoFabricacao: anoSchema,
  anoModelo: anoSchema,
  placa: placaSchema,
  renavam: renavamSchema,
  chassi: chassiSchema,
  cor: z.string({ error: "cor \xE9 obrigat\xF3ria" }).trim().min(2).max(40),
  certificadoDeBlindagemNumero: z.string().trim().max(60).nullish(),
  certificadoDeBlindagemValidade: dataSimplesSchema.nullish(),
  laudoDeBlindagemUrl: z.url({ error: "o laudo precisa ser uma URL" }).nullish(),
  hodometroAtual: inteiroNaoNegativo("hod\xF4metro").default(0),
  dataDeAquisicao: dataSimplesSchema,
  valorDeAquisicao: moneySchemaNonNegative.refine((valor) => valor > 0n, {
    error: "o valor de aquisi\xE7\xE3o precisa ser maior que zero"
  }),
  valorAtualEstimado: moneySchemaNonNegative.nullish(),
  vidaUtilEmMeses: inteiroPositivo("vida \xFAtil em meses").nullish(),
  residualEmPontosBase: pontosBaseSchema.nullish(),
  seguradora: z.string().trim().max(80).nullish(),
  apolice: z.string().trim().max(60).nullish(),
  apoliceVencimento: dataSimplesSchema.nullish(),
  ipvaVencimento: dataSimplesSchema.nullish(),
  licenciamentoVencimento: dataSimplesSchema.nullish(),
  prioridadeDeAlocacao: z.coerce.number().int({ error: "a prioridade precisa ser inteira" }).min(0, { error: "a prioridade n\xE3o pode ser negativa" }).max(1e3, { error: "a prioridade m\xE1xima \xE9 1000" }).default(0),
  notasInternas: z.string().trim().max(2e3).nullish()
}).refine((v) => v.anoModelo >= v.anoFabricacao && v.anoModelo <= v.anoFabricacao + 1, {
  error: "o ano-modelo precisa ser o de fabrica\xE7\xE3o ou o seguinte",
  path: ["anoModelo"]
});
var atualizarVeiculoSchema = z.object({
  categoryId: uuidSchema.optional(),
  locationId: uuidSchema.nullish(),
  marca: z.string().trim().min(1).max(60).optional(),
  modelo: z.string().trim().min(1).max(80).optional(),
  anoFabricacao: anoSchema.optional(),
  anoModelo: anoSchema.optional(),
  placa: placaSchema.optional(),
  renavam: renavamSchema.optional(),
  chassi: chassiSchema.optional(),
  cor: z.string().trim().min(2).max(40).optional(),
  certificadoDeBlindagemNumero: z.string().trim().max(60).nullish(),
  certificadoDeBlindagemValidade: dataSimplesSchema.nullish(),
  laudoDeBlindagemUrl: z.url().nullish(),
  hodometroAtual: inteiroNaoNegativo("hod\xF4metro").optional(),
  dataDeAquisicao: dataSimplesSchema.optional(),
  valorDeAquisicao: moneySchemaNonNegative.optional(),
  valorAtualEstimado: moneySchemaNonNegative.nullish(),
  vidaUtilEmMeses: inteiroPositivo("vida \xFAtil em meses").nullish(),
  residualEmPontosBase: pontosBaseSchema.nullish(),
  seguradora: z.string().trim().max(80).nullish(),
  apolice: z.string().trim().max(60).nullish(),
  apoliceVencimento: dataSimplesSchema.nullish(),
  ipvaVencimento: dataSimplesSchema.nullish(),
  licenciamentoVencimento: dataSimplesSchema.nullish(),
  prioridadeDeAlocacao: z.coerce.number().int().min(0).max(1e3).optional(),
  notasInternas: z.string().trim().max(2e3).nullish(),
  dataDeBaixa: dataSimplesSchema.nullish()
});
var filtroDeVeiculoSchema = paginacaoSchema.extend({
  busca: z.string().trim().max(120).optional(),
  categoryId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  status: statusDoVeiculoSchema.optional(),
  /** Só os veículos cujo certificado vence dentro de N dias. */
  blindagemVencendoEmDias: z.coerce.number().int().min(0).max(3650).optional(),
  incluirBaixados: z.stringbool().default(false)
});
var mudarStatusDoVeiculoSchema = z.object({
  status: statusDoVeiculoSchema.refine(
    (status) => !STATUS_CONTROLADOS_PELA_RESERVA.includes(status),
    { error: "este status \xE9 definido pela reserva, n\xE3o pela opera\xE7\xE3o" }
  ),
  motivo: z.string({ error: "motivo \xE9 obrigat\xF3rio" }).trim().min(3, { error: "descreva o motivo em ao menos 3 caracteres" }).max(500, { error: "motivo muito longo" })
});
var MESES_DE_RATEIO_MAXIMO = 60;
var lancarCustoSchema = z.object({
  vehicleId: uuidSchema,
  tipo: tipoDeCustoDeVeiculoSchema.refine(
    (tipo) => !TIPOS_SEM_CONTRAPARTIDA_EM_CAIXA.includes(tipo),
    { error: "este custo \xE9 calculado pelo sistema e n\xE3o pode ser lan\xE7ado \xE0 m\xE3o" }
  ),
  valor: moneySchemaNonNegative.refine((valor) => valor > 0n, {
    error: "o valor precisa ser maior que zero"
  }),
  data: dataSimplesSchema,
  /**
   * Em quantos meses ratear. `1` (padrão) lança tudo na competência da data.
   * Acima disso, o sistema gera as parcelas apontando para este lançamento —
   * é assim que o IPVA de janeiro para de fazer janeiro parecer prejuízo.
   */
  ratearEmMeses: z.coerce.number().int({ error: "o n\xFAmero de meses precisa ser inteiro" }).min(1, { error: "o rateio come\xE7a em 1 m\xEAs" }).max(MESES_DE_RATEIO_MAXIMO, {
    error: `o rateio vai at\xE9 ${String(MESES_DE_RATEIO_MAXIMO)} meses`
  }).default(1),
  fornecedor: z.string().trim().max(120).nullish(),
  notaFiscal: z.string().trim().max(60).nullish(),
  hodometro: inteiroNaoNegativo("hod\xF4metro").nullish(),
  anexoUrl: z.url({ error: "o comprovante precisa ser uma URL" }).nullish(),
  observacoes: z.string().trim().max(1e3).nullish()
}).refine((custo) => custo.ratearEmMeses === 1 || TIPOS_RATEAVEIS.includes(custo.tipo), {
  error: "este tipo de custo n\xE3o costuma ser rateado \u2014 lance no m\xEAs em que ocorreu",
  path: ["ratearEmMeses"]
});
var atualizarCustoSchema = z.object({
  fornecedor: z.string().trim().max(120).nullish(),
  notaFiscal: z.string().trim().max(60).nullish(),
  hodometro: inteiroNaoNegativo("hod\xF4metro").nullish(),
  anexoUrl: z.url().nullish(),
  observacoes: z.string().trim().max(1e3).nullish()
});
var filtroDeCustoSchema = paginacaoSchema.extend({
  vehicleId: uuidSchema.optional(),
  tipo: tipoDeCustoDeVeiculoSchema.optional(),
  origem: origemDoCustoSchema.optional(),
  /** Primeiro e último mês de competência, inclusive. */
  competenciaDe: dataSimplesSchema.optional(),
  competenciaAte: dataSimplesSchema.optional()
});
var salvarPlanoDeManutencaoSchema = z.object({
  proximaRevisaoKm: inteiroNaoNegativo("km da pr\xF3xima revis\xE3o").nullish(),
  proximaRevisaoData: dataSimplesSchema.nullish(),
  alertaKmAntes: inteiroNaoNegativo("anteced\xEAncia em km").default(1e3),
  alertaDiasAntes: inteiroNaoNegativo("anteced\xEAncia em dias").default(30),
  observacoes: z.string().trim().max(1e3).nullish(),
  ativo: z.boolean().default(true)
}).refine(
  (plano) => plano.proximaRevisaoKm !== null && plano.proximaRevisaoKm !== void 0 ? true : plano.proximaRevisaoData !== null && plano.proximaRevisaoData !== void 0,
  {
    error: "informe ao menos um gatilho: km ou data da pr\xF3xima revis\xE3o",
    path: ["proximaRevisaoKm"]
  }
);
var salvarPoliticaDeCustoSchema = z.object({
  vidaUtilEmMeses: inteiroPositivo("vida \xFAtil padr\xE3o em meses"),
  residualEmPontosBase: pontosBaseSchema,
  custoDeCapitalAnualEmPontosBase: pontosBaseSchema,
  overheadMensal: moneySchemaNonNegative
});
var TIPOS_DE_ALERTA = [
  "BLINDAGEM",
  "APOLICE",
  "IPVA",
  "LICENCIAMENTO",
  "REVISAO_POR_DATA",
  "REVISAO_POR_KM"
];
var ROTULO_DO_ALERTA = {
  BLINDAGEM: "Certificado de blindagem",
  APOLICE: "Ap\xF3lice de seguro",
  IPVA: "IPVA",
  LICENCIAMENTO: "Licenciamento",
  REVISAO_POR_DATA: "Revis\xE3o (data)",
  REVISAO_POR_KM: "Revis\xE3o (quilometragem)"
};
var GRAVIDADES_DE_ALERTA = ["VENCIDO", "CRITICO", "ATENCAO"];
var DIAS_PARA_ALERTA_CRITICO = 15;
var DIAS_PARA_ALERTA_DE_ATENCAO = 45;
function gravidadeDoVencimento(diasRestantes) {
  if (diasRestantes < 0) return "VENCIDO";
  if (diasRestantes <= DIAS_PARA_ALERTA_CRITICO) return "CRITICO";
  if (diasRestantes <= DIAS_PARA_ALERTA_DE_ATENCAO) return "ATENCAO";
  return null;
}
var filtroDeAlertaSchema = z.object({
  gravidade: z.enum(GRAVIDADES_DE_ALERTA).optional(),
  tipo: z.enum(TIPOS_DE_ALERTA).optional()
});

// src/endereco.ts
var criarEnderecoDaContaSchema = z.object({
  cep: cepSchema,
  logradouro: z.string({ error: "logradouro \xE9 obrigat\xF3rio" }).trim().min(2).max(200),
  numero: z.string({ error: "n\xFAmero \xE9 obrigat\xF3rio" }).trim().min(1).max(20),
  complemento: z.string().trim().max(100).optional(),
  bairro: z.string({ error: "bairro \xE9 obrigat\xF3rio" }).trim().min(2).max(100),
  cidade: z.string({ error: "cidade \xE9 obrigat\xF3ria" }).trim().min(2).max(100),
  uf: ufSchema,
  /**
   * O endereço padrão da conta. No máximo um por conta — quem garante é o
   * índice único PARCIAL da migration (`WHERE principal`), não este campo:
   * validação de formulário não arbitra corrida entre dois requests.
   */
  principal: z.boolean().default(true)
});
var atualizarEnderecoDaContaSchema = criarEnderecoDaContaSchema.partial();
var enderecoDaContaSchema = criarEnderecoDaContaSchema.extend({
  id: uuidSchema,
  accountId: uuidSchema,
  principal: z.boolean()
});

// src/auth.ts
var STATUS_DE_USUARIO = ["ATIVO", "PENDENTE", "BLOQUEADO"];
var statusDoUsuarioSchema = z.enum(STATUS_DE_USUARIO, { error: "status inv\xE1lido" });
var PROVEDORES_OAUTH = ["GOOGLE", "APPLE"];
var provedorOAuthSchema = z.enum(PROVEDORES_OAUTH, { error: "provedor n\xE3o suportado" });
var MOTIVOS_DE_REVOGACAO = [
  "ROTACAO",
  "LOGOUT",
  "LOGOUT_GLOBAL",
  "REUSO_DETECTADO",
  "SENHA_ALTERADA",
  "REVOGADO_POR_ADMIN",
  "USUARIO_BLOQUEADO"
];
var motivoDeRevogacaoSchema = z.enum(MOTIVOS_DE_REVOGACAO, { error: "motivo inv\xE1lido" });
var TTL_VERIFICACAO_DE_EMAIL_MINUTOS = 60 * 24;
var TTL_RESET_DE_SENHA_MINUTOS = 30;
var TTL_DESAFIO_2FA_MINUTOS = 5;
var PARAMETROS_ARGON2ID = {
  /** Memória em KiB. 19456 KiB = 19 MiB. */
  memoryCost: 19456,
  /** Iterações. */
  timeCost: 2,
  /** Faixas paralelas. */
  parallelism: 1,
  /** Bytes de saída. */
  outputLen: 32
};
var TOTP_DIGITOS = 6;
var TOTP_PERIODO_SEGUNDOS = 30;
var TOTP_JANELAS_TOLERADAS = 1;
var TOTP_ALGORITMO = "SHA1";
var QUANTIDADE_DE_CODIGOS_DE_RECUPERACAO = 10;
var TAMANHO_DO_CODIGO_DE_RECUPERACAO = 10;
var APENAS_DIGITOS_TOTP = /^\d{6}$/;
var SEPARADORES_DE_CODIGO = /[\s-]/g;
var ALFABETO_DE_RECUPERACAO = /^[A-HJ-KMNP-TV-Z2-9]{10}$/;
var codigoTotpSchema = z.string({ error: "c\xF3digo \xE9 obrigat\xF3rio" }).trim().transform((valor) => valor.replace(SEPARADORES_DE_CODIGO, "")).refine((valor) => APENAS_DIGITOS_TOTP.test(valor), {
  error: `o c\xF3digo do autenticador tem ${String(TOTP_DIGITOS)} d\xEDgitos`
});
var codigoDeRecuperacaoSchema = z.string({ error: "c\xF3digo de recupera\xE7\xE3o \xE9 obrigat\xF3rio" }).trim().toUpperCase().transform((valor) => valor.replace(SEPARADORES_DE_CODIGO, "")).refine((valor) => ALFABETO_DE_RECUPERACAO.test(valor), {
  error: "c\xF3digo de recupera\xE7\xE3o inv\xE1lido"
});
var POLITICA_DE_LOCKOUT = {
  /** Janela de contagem das tentativas falhas, em minutos. */
  janelaEmMinutos: 15,
  /** Tentativas livres antes do primeiro atraso. */
  tentativasAntesDoBloqueio: 5,
  /** Atrasos aplicados a partir da 6ª falha, em segundos. */
  atrasosEmSegundos: [30, 60, 300, 900, 1800],
  /** Teto: nenhum bloqueio automático passa disto. */
  atrasoMaximoEmSegundos: 3600
};
function segundosDeBloqueio(falhasNaJanela) {
  const excedente = falhasNaJanela - POLITICA_DE_LOCKOUT.tentativasAntesDoBloqueio;
  if (excedente <= 0) {
    return 0;
  }
  const atrasos = POLITICA_DE_LOCKOUT.atrasosEmSegundos;
  const indice = Math.min(excedente, atrasos.length) - 1;
  const atraso = atrasos[indice] ?? POLITICA_DE_LOCKOUT.atrasoMaximoEmSegundos;
  return Math.min(atraso, POLITICA_DE_LOCKOUT.atrasoMaximoEmSegundos);
}
var registroSchema = z.object({
  nome: nomeSchema,
  email: emailSchema,
  senha: senhaSchema,
  confirmacaoDeSenha: z.string(),
  telefone: telefoneSchema.optional(),
  /**
   * Aceite explícito. `literal(true)` e não `boolean`: `false` precisa ser
   * erro de validação, não um registro gravado sem consentimento.
   */
  aceiteDosTermos: z.literal(true, { error: "\xE9 preciso aceitar os termos de uso" }),
  /**
   * Os dois campos que a `Account` PF não dispensa. Nome e e-mail de cobrança
   * saem do próprio registro — pedi-los duas vezes na mesma tela é como um
   * cadastro nasce com o e-mail digitado errado só na segunda.
   */
  conta: z.object({
    cpf: cpfSchema,
    dataDeNascimento: dataDeNascimentoSchema,
    endereco: criarEnderecoDaContaSchema.optional()
  }).optional()
}).refine(senhasConferem, {
  error: MENSAGEM_CONFIRMACAO_DE_SENHA,
  path: ["confirmacaoDeSenha"]
});
var loginSchema = z.object({
  email: emailSchema,
  senha: z.string({ error: "senha \xE9 obrigat\xF3ria" }).min(1, { error: "informe a senha" }),
  codigoTotp: codigoTotpSchema.optional()
});
var desafio2faSchema = z.object({
  desafioId: uuidSchema,
  codigoTotp: codigoTotpSchema.optional(),
  codigoDeRecuperacao: codigoDeRecuperacaoSchema.optional()
}).refine(
  (dados) => dados.codigoTotp === void 0 !== (dados.codigoDeRecuperacao === void 0),
  { error: "informe o c\xF3digo do autenticador OU um c\xF3digo de recupera\xE7\xE3o" }
);
var refreshSchema = z.object({
  refreshToken: z.string({ error: "refresh token \xE9 obrigat\xF3rio" }).trim().min(20, { error: "refresh token inv\xE1lido" }).max(512, { error: "refresh token inv\xE1lido" }).optional()
});
var logoutSchema = z.object({
  refreshToken: z.string().trim().min(20).max(512).optional(),
  todasAsSessoes: z.boolean().default(false)
});
var tokenOpacoSchema = z.string({ error: "token \xE9 obrigat\xF3rio" }).trim().min(20, { error: "token inv\xE1lido" }).max(256, { error: "token inv\xE1lido" });
var solicitarVerificacaoDeEmailSchema = z.object({ email: emailSchema });
var confirmarVerificacaoDeEmailSchema = z.object({ token: tokenOpacoSchema });
var solicitarResetDeSenhaSchema = z.object({ email: emailSchema });
var confirmarResetDeSenhaSchema = z.object({
  token: tokenOpacoSchema,
  senha: senhaSchema,
  confirmacaoDeSenha: z.string()
}).refine(senhasConferem, {
  error: MENSAGEM_CONFIRMACAO_DE_SENHA,
  path: ["confirmacaoDeSenha"]
});
var alterarSenhaSchema = z.object({
  senhaAtual: z.string({ error: "senha atual \xE9 obrigat\xF3ria" }).min(1, { error: "informe a senha atual" }),
  senha: senhaSchema,
  confirmacaoDeSenha: z.string()
}).refine(senhasConferem, {
  error: MENSAGEM_CONFIRMACAO_DE_SENHA,
  path: ["confirmacaoDeSenha"]
}).refine((dados) => dados.senha !== dados.senhaAtual, {
  error: "a nova senha precisa ser diferente da atual",
  path: ["senha"]
});
var setupTotpRespostaSchema = z.object({
  segredoBase32: z.string().min(16),
  /** `otpauth://totp/...` — o que vira QR Code na tela. */
  otpauthUrl: z.string().startsWith("otpauth://totp/"),
  /** Também aparecem uma única vez; o banco guarda só o hash de cada um. */
  codigosDeRecuperacao: z.array(z.string()).length(QUANTIDADE_DE_CODIGOS_DE_RECUPERACAO)
});
var confirmarTotpSchema = z.object({ codigoTotp: codigoTotpSchema });
var desativarTotpSchema = z.object({
  senha: z.string({ error: "senha \xE9 obrigat\xF3ria" }).min(1, { error: "informe a senha" }),
  codigoTotp: codigoTotpSchema
});
var callbackOAuthSchema = z.object({
  provedor: provedorOAuthSchema,
  codigo: z.string({ error: "c\xF3digo de autoriza\xE7\xE3o \xE9 obrigat\xF3rio" }).min(1).max(2048),
  state: z.string({ error: "state \xE9 obrigat\xF3rio" }).min(8).max(256),
  codeVerifier: z.string().min(43).max(128).optional(),
  redirectUri: z.url({ error: "redirectUri inv\xE1lida" }).optional()
});
var vincularOAuthSchema = z.object({
  provedor: provedorOAuthSchema,
  codigo: z.string().min(1).max(2048),
  state: z.string().min(8).max(256),
  codeVerifier: z.string().min(43).max(128).optional()
});
var desvincularOAuthSchema = z.object({ provedor: provedorOAuthSchema });
var provedoresDisponiveisSchema = z.object({
  provedores: z.array(provedorOAuthSchema)
});
var usuarioAutenticadoSchema = z.object({
  id: uuidSchema,
  nome: z.string(),
  email: z.email(),
  /** `null` para usuário interno da Locaveri, que não é cliente. */
  accountId: uuidSchema.nullable(),
  emailVerificado: z.boolean(),
  papeis: z.array(codigoDePapelSchema),
  /** Já resolvidas (união dos papéis) — a UI não recalcula RBAC. */
  permissoes: z.array(permissaoSchema),
  /** `true` quando o papel exige 2FA e o usuário ainda não configurou. */
  precisaConfigurarTotp: z.boolean(),
  totpAtivo: z.boolean()
});
var sessaoSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  /** Segundos até o access token expirar. */
  expiraEmSegundos: z.number().int().positive(),
  tipoDeToken: z.literal("Bearer"),
  usuario: usuarioAutenticadoSchema
});
var desafioPendenteSchema = z.object({
  status: z.literal("2FA_OBRIGATORIO"),
  desafioId: uuidSchema,
  expiraEmSegundos: z.number().int().positive()
});
var desafioDeSetupDe2faSchema = z.object({
  status: z.literal("2FA_SETUP_OBRIGATORIO"),
  desafioId: uuidSchema,
  expiraEmSegundos: z.number().int().positive()
});
var respostaDeLoginSchema = z.union([
  sessaoSchema,
  desafioPendenteSchema,
  desafioDeSetupDe2faSchema
]);
var payloadDoAccessTokenSchema = z.object({
  sub: uuidSchema,
  /** Tenant do usuário (invariante nº 10). */
  tid: uuidSchema,
  /** Account do cliente, `null` para usuário interno. */
  acc: uuidSchema.nullable(),
  papeis: z.array(codigoDePapelSchema),
  perms: z.array(permissaoSchema),
  /** `true` se esta sessão passou pelo segundo fator. */
  totp: z.boolean(),
  /** Identificador do token, para correlação em log e auditoria. */
  jti: uuidSchema,
  iat: z.number().int(),
  exp: z.number().int(),
  iss: z.string(),
  aud: z.string()
});
var ESCOPO_DE_VISITANTE = "VISITANTE";
var payloadDoTokenDeVisitanteSchema = z.object({
  /** Sujeito da sessão anônima. É o `visitor_session_id` dos snapshots. */
  vst: uuidSchema,
  /** Tenant da sessão (invariante nº 10). */
  tid: uuidSchema,
  /** Escopo literal — o token não negocia o que pode fazer. */
  esc: z.literal(ESCOPO_DE_VISITANTE),
  /** Identificador do token: é ele que a adoção queima no Redis. */
  jti: uuidSchema,
  iat: z.number().int(),
  exp: z.number().int(),
  iss: z.string(),
  aud: z.string()
});
var TIPOS_DE_CONTA = ["PF", "PJ"];
var tipoDeContaSchema = z.enum(TIPOS_DE_CONTA, { error: "tipo de conta inv\xE1lido" });
var STATUS_DE_CONTA = ["ATIVA", "PENDENTE", "SUSPENSA", "ENCERRADA"];
var statusDaContaSchema = z.enum(STATUS_DE_CONTA, { error: "status de conta inv\xE1lido" });
var REGIMES_DE_FATURAMENTO = ["PRE_PAGO", "FATURADO_MENSAL"];
var regimeDeFaturamentoSchema = z.enum(REGIMES_DE_FATURAMENTO, {
  error: "regime de faturamento inv\xE1lido"
});
var DIA_MAXIMO_DE_FECHAMENTO = 28;
var camposComunsDaConta = {
  /** Para onde vai a fatura. Pode ser diferente do e-mail de login. */
  emailDeCobranca: emailSchema,
  telefone: telefoneSchema.optional(),
  regimeDeFaturamento: regimeDeFaturamentoSchema.default("PRE_PAGO"),
  diaDeFechamento: z.coerce.number().int({ error: "dia de fechamento precisa ser inteiro" }).min(1, { error: "dia de fechamento come\xE7a em 1" }).max(DIA_MAXIMO_DE_FECHAMENTO, {
    error: `dia de fechamento vai at\xE9 ${String(DIA_MAXIMO_DE_FECHAMENTO)}`
  }).optional(),
  /**
   * Teto de crédito em centavos (invariante nº 1). Só faz sentido com
   * `FATURADO_MENSAL`; em `PRE_PAGO` fica nulo.
   */
  limiteDeCredito: moneySchemaNonNegative.optional(),
  /**
   * Tabela de preço negociada. A entidade só nasce na Fase 3 — aqui existe o
   * vínculo, e nenhuma regra de preço.
   */
  tabelaDePrecoNegociadaId: uuidSchema.optional(),
  observacoes: z.string().trim().max(2e3, { error: "observa\xE7\xF5es: m\xE1ximo de 2000 caracteres" }).optional()
};
var criarContaPessoaFisicaSchema = z.object({
  tipo: z.literal("PF"),
  cpf: cpfSchema,
  nome: nomeSchema,
  dataDeNascimento: dataDeNascimentoSchema,
  ...camposComunsDaConta
});
var criarContaPessoaJuridicaSchema = z.object({
  tipo: z.literal("PJ"),
  cnpj: cnpjSchema,
  razaoSocial: nomeSchema,
  nomeFantasia: nomeSchema.optional(),
  inscricaoEstadual: inscricaoEstadualSchema.optional(),
  centroDeCusto: z.string().trim().max(60, { error: "centro de custo: m\xE1ximo de 60 caracteres" }).optional(),
  ...camposComunsDaConta
});
var criarContaSchema = z.discriminatedUnion("tipo", [
  criarContaPessoaFisicaSchema,
  criarContaPessoaJuridicaSchema
]);
var atualizarContaPessoaFisicaSchema = criarContaPessoaFisicaSchema.omit({ tipo: true, cpf: true }).partial().extend({ status: statusDaContaSchema.optional() });
var atualizarContaPessoaJuridicaSchema = criarContaPessoaJuridicaSchema.omit({ tipo: true, cnpj: true }).partial().extend({ status: statusDaContaSchema.optional() });
var listarContasQuerySchema = paginacaoSchema.extend({
  tipo: tipoDeContaSchema.optional(),
  status: statusDaContaSchema.optional(),
  regimeDeFaturamento: regimeDeFaturamentoSchema.optional()
});
var criarUsuarioInternoSchema = z.object({
  nome: nomeSchema,
  email: emailSchema,
  telefone: telefoneSchema.optional(),
  papeis: z.array(codigoDePapelSchema).min(1, { error: "informe pelo menos um papel" }).refine((papeis) => new Set(papeis).size === papeis.length, {
    error: "papel repetido na lista"
  }).refine((papeis) => !papeis.includes("CLIENTE"), {
    error: "CLIENTE n\xE3o \xE9 papel de usu\xE1rio interno"
  }),
  enviarConvite: z.boolean().default(true)
});
var atualizarUsuarioInternoSchema = criarUsuarioInternoSchema.omit({ email: true, enviarConvite: true }).partial().extend({ status: statusDoUsuarioSchema.optional() }).refine((dados) => Object.keys(dados).length > 0, {
  error: "informe pelo menos um campo para alterar"
});
var atribuirPapeisSchema = z.object({
  papeis: z.array(codigoDePapelSchema).min(1, { error: "informe pelo menos um papel" }).refine((papeis) => new Set(papeis).size === papeis.length, {
    error: "papel repetido na lista"
  })
});
var listarUsuariosQuerySchema = paginacaoSchema.extend({
  status: statusDoUsuarioSchema.optional(),
  papel: codigoDePapelSchema.optional(),
  accountId: uuidSchema.optional(),
  /** `true` lista só a equipe interna (sem `accountId`); `false`, só clientes. */
  apenasInternos: z.stringbool().optional()
});
var convidarCondutorSchema = z.object({
  nome: nomeSchema,
  email: emailSchema,
  telefone: telefoneSchema.optional(),
  /** Rateio interno do cliente: sobrescreve o centro de custo da conta. */
  centroDeCusto: z.string().trim().max(60, { error: "centro de custo: m\xE1ximo de 60 caracteres" }).optional()
});
var PRAZO_DE_RETENCAO_FISCAL_EM_ANOS = 5;
var ITENS_APAGADOS_NA_EXCLUSAO = [
  "LOGIN",
  "CONTATO",
  "SEGUNDO_FATOR",
  "LOGIN_SOCIAL",
  "SESSOES",
  "LISTA_DE_ESPERA"
];
var DESCRICAO_DO_ITEM_APAGADO = {
  LOGIN: "E-mail e senha de acesso \u2014 a conta deixa de autenticar.",
  CONTATO: "Nome, telefone e endere\xE7os cadastrados.",
  SEGUNDO_FATOR: "Segundo fator e c\xF3digos de recupera\xE7\xE3o.",
  LOGIN_SOCIAL: "V\xEDnculos com Google e Apple.",
  SESSOES: "Todas as sess\xF5es abertas, em todos os aparelhos.",
  // ⚠️ ANONIMIZADA, não apagada — e a frase diz as duas metades.
  //
  // A lista de espera é a única superfície ANÔNIMA que coleta contato, por
  // desenho. O contato sai; o PEDIDO fica, sem nada que ligue a você. Dizer só
  // "apagamos" seria mentira (a linha continua lá), e dizer só "mantemos" faria
  // parecer que o contato ficou.
  LISTA_DE_ESPERA: "O contato deixado na lista de espera. O pedido em si continua, sem nada que identifique voc\xEA."
};
var ITENS_RETIDOS_NA_EXCLUSAO = [
  "RESERVAS",
  "CONTRATOS",
  "FATURAS_E_PAGAMENTOS",
  "DOCUMENTO_FISCAL",
  "TRILHA_DE_AUDITORIA"
];
var DESCRICAO_DO_ITEM_RETIDO = {
  RESERVAS: "O hist\xF3rico de loca\xE7\xF5es j\xE1 realizadas.",
  CONTRATOS: "Os contratos assinados de cada loca\xE7\xE3o.",
  FATURAS_E_PAGAMENTOS: "Faturas, recibos e transa\xE7\xF5es de pagamento.",
  DOCUMENTO_FISCAL: "O CPF, enquanto ele identificar uma nota fiscal emitida.",
  TRILHA_DE_AUDITORIA: "O registro de auditoria das opera\xE7\xF5es da conta."
};
var RETIDOS_SEM_DADO_PESSOAL = ["TRILHA_DE_AUDITORIA"];
function apagaDadosPessoais(dadosRetidos) {
  return dadosRetidos.every((item) => RETIDOS_SEM_DADO_PESSOAL.includes(item));
}
var IMPEDIMENTOS_DE_EXCLUSAO = ["RESERVA_ATIVA", "USUARIO_INTERNO"];
var DESCRICAO_DO_IMPEDIMENTO = {
  RESERVA_ATIVA: "H\xE1 loca\xE7\xE3o em aberto. Conclua ou cancele antes de excluir a conta.",
  USUARIO_INTERNO: "Contas da equipe s\xE3o encerradas pela administra\xE7\xE3o, n\xE3o pelo pr\xF3prio usu\xE1rio."
};
var previaDaExclusaoDeContaSchema = z.object({
  /** `false` quando há pelo menos um impedimento. */
  podeExcluir: z.boolean(),
  impedimentos: z.array(z.enum(IMPEDIMENTOS_DE_EXCLUSAO)),
  /**
   * Quantas locações ainda estão em aberto. A tela mostra o NÚMERO junto do
   * impedimento: "há 2 locações em aberto" responde a pergunta seguinte do
   * cliente sem ele precisar abrir outra tela.
   */
  reservasAtivas: z.number().int().nonnegative(),
  /**
   * `true` quando a conta tem senha e ela será exigida na confirmação.
   *
   * Quem entrou só por Google ou Apple não tem senha para digitar, e pedir uma
   * seria um beco sem saída dentro do requisito da loja. Nesse caso a prova de
   * identidade é a sessão viva mais a palavra de confirmação.
   */
  exigeSenha: z.boolean(),
  seraApagado: z.array(z.enum(ITENS_APAGADOS_NA_EXCLUSAO)),
  seraRetido: z.array(z.enum(ITENS_RETIDOS_NA_EXCLUSAO)),
  /** O prazo de guarda, em anos, que a tela precisa citar. */
  retencaoEmAnos: z.number().int().positive()
});
var CONFIRMACAO_DE_EXCLUSAO = "EXCLUIR";
var excluirContaSchema = z.object({
  /**
   * A senha atual, quando a conta tem uma.
   *
   * Opcional no SCHEMA e obrigatória no SERVIÇO, e a diferença é deliberada:
   * só o servidor sabe se aquele usuário tem `senha_hash` (a conta criada por
   * login social não tem). Exigir aqui deixaria o cliente social sem caminho;
   * o serviço recusa com `CREDENCIAIS_INVALIDAS` quem tem senha e não a
   * mandou — a mesma recusa de quem a mandou errada, para não virar um oráculo
   * de "esta conta usa login social".
   */
  senha: z.string().min(1, { error: "informe sua senha" }).optional(),
  confirmacao: z.literal(CONFIRMACAO_DE_EXCLUSAO, {
    error: `digite ${CONFIRMACAO_DE_EXCLUSAO} para confirmar`
  })
}).strict();
var resultadoDaExclusaoDeContaSchema = z.object({
  /** Instante ISO em UTC. A tela mostra no fuso local. */
  excluidoEm: z.iso.datetime(),
  /**
   * `true` quando a CONTA (o cadastro de cliente) também foi encerrada.
   *
   * Nem sempre é: numa PJ com vários condutores autorizados, sair da empresa é
   * excluir o SEU login — a conta da empresa e as locações dos colegas seguem
   * de pé. Encerrar a conta inteira porque um motorista saiu apagaria o
   * faturamento de todo mundo.
   */
  contaEncerrada: z.boolean(),
  /**
   * O que de fato ficou retido NESTA conta — a lista real, não a genérica da
   * prévia. Uma conta que nunca alugou nada sai com a lista vazia, e é
   * importante que a tela consiga dizer isso.
   */
  dadosRetidos: z.array(z.enum(ITENS_RETIDOS_NA_EXCLUSAO)),
  retencaoEmAnos: z.number().int().positive()
});

// src/custos.ts
var custos_exports = {};
__export(custos_exports, {
  PONTOS_BASE_CEM_PORCENTO: () => PONTOS_BASE_CEM_PORCENTO,
  cronogramaDeDepreciacao: () => cronogramaDeDepreciacao,
  custoDeCapitalDoMes: () => custoDeCapitalDoMes,
  custoIndiretoDoMes: () => custoIndiretoDoMes,
  depreciacaoDoMes: () => depreciacaoDoMes,
  indiceDoMesDeOperacao: () => indiceDoMesDeOperacao,
  ratearCustoAnual: () => ratearCustoAnual,
  ratearOverhead: () => ratearOverhead,
  resolverPremissas: () => resolverPremissas,
  valorContabilNoMes: () => valorContabilNoMes,
  valorDepreciavel: () => valorDepreciavel,
  valorResidual: () => valorResidual
});
var PONTOS_BASE_CEM_PORCENTO = 1e4;
var MESES_POR_ANO = 12;
function exigirInteiroPositivo(valor, rotulo) {
  if (!Number.isInteger(valor) || valor <= 0) {
    throw new RangeError(`${rotulo} inv\xE1lido: ${String(valor)}. Informe um inteiro positivo.`);
  }
}
function exigirPontosBase(valor, rotulo) {
  if (!Number.isInteger(valor) || valor < 0 || valor > PONTOS_BASE_CEM_PORCENTO) {
    throw new RangeError(
      `${rotulo} inv\xE1lido: ${String(valor)}. Informe pontos-base inteiros entre 0 e ${String(PONTOS_BASE_CEM_PORCENTO)}.`
    );
  }
}
function fracaoDePontosBase(pontosBase, divisorExtra = 1) {
  return {
    numerator: BigInt(pontosBase),
    denominator: BigInt(PONTOS_BASE_CEM_PORCENTO) * BigInt(divisorExtra)
  };
}
function valorResidual(premissas) {
  exigirPontosBase(premissas.residualEmPontosBase, "Residual");
  return mulRate(premissas.valorDeAquisicao, fracaoDePontosBase(premissas.residualEmPontosBase));
}
function valorDepreciavel(premissas) {
  return sub(premissas.valorDeAquisicao, valorResidual(premissas));
}
function cronogramaDeDepreciacao(premissas) {
  exigirInteiroPositivo(premissas.vidaUtilEmMeses, "Vida \xFAtil em meses");
  const depreciavel = valorDepreciavel(premissas);
  const parcelas = allocate(
    depreciavel,
    Array.from({ length: premissas.vidaUtilEmMeses }, () => 1)
  );
  const cronograma = [];
  let acumulado = zero;
  for (const [indiceDoMes, parcela] of parcelas.entries()) {
    acumulado = fromCents(acumulado + parcela);
    cronograma.push({
      indiceDoMes,
      valor: parcela,
      acumulado,
      valorContabil: sub(premissas.valorDeAquisicao, acumulado)
    });
  }
  return cronograma;
}
function depreciacaoDoMes(premissas, indiceDoMes) {
  if (!Number.isInteger(indiceDoMes) || indiceDoMes < 0) {
    throw new RangeError(
      `\xCDndice de m\xEAs inv\xE1lido: ${String(indiceDoMes)}. Informe um inteiro n\xE3o negativo.`
    );
  }
  if (indiceDoMes >= premissas.vidaUtilEmMeses) {
    return zero;
  }
  return cronogramaDeDepreciacao(premissas)[indiceDoMes]?.valor ?? zero;
}
function valorContabilNoMes(premissas, indiceDoMes) {
  if (!Number.isInteger(indiceDoMes) || indiceDoMes < 0) {
    throw new RangeError(
      `\xCDndice de m\xEAs inv\xE1lido: ${String(indiceDoMes)}. Informe um inteiro n\xE3o negativo.`
    );
  }
  if (indiceDoMes >= premissas.vidaUtilEmMeses) {
    return valorResidual(premissas);
  }
  return cronogramaDeDepreciacao(premissas)[indiceDoMes]?.valorContabil ?? premissas.valorDeAquisicao;
}
function indiceDoMesDeOperacao(dataDeAquisicao, competencia) {
  const meses = (competencia.getUTCFullYear() - dataDeAquisicao.getUTCFullYear()) * MESES_POR_ANO + (competencia.getUTCMonth() - dataDeAquisicao.getUTCMonth());
  return meses < 0 ? -1 : meses;
}
function custoDeCapitalDoMes(valorContabil, taxaAnualEmPontosBase) {
  exigirPontosBase(taxaAnualEmPontosBase, "Custo de capital anual");
  return mulRate(valorContabil, fracaoDePontosBase(taxaAnualEmPontosBase, MESES_POR_ANO));
}
function ratearOverhead(overheadMensal, quantidadeDeVeiculosAtivos) {
  if (!Number.isInteger(quantidadeDeVeiculosAtivos) || quantidadeDeVeiculosAtivos < 0) {
    throw new RangeError(
      `Quantidade de ve\xEDculos inv\xE1lida: ${String(quantidadeDeVeiculosAtivos)}. Informe um inteiro n\xE3o negativo.`
    );
  }
  if (quantidadeDeVeiculosAtivos === 0) {
    return [];
  }
  return allocate(
    overheadMensal,
    Array.from({ length: quantidadeDeVeiculosAtivos }, () => 1)
  );
}
function ratearCustoAnual(valor, meses = MESES_POR_ANO) {
  exigirInteiroPositivo(meses, "N\xFAmero de meses do rateio");
  return allocate(
    valor,
    Array.from({ length: meses }, () => 1)
  );
}
function custoIndiretoDoMes(premissas, politica, indiceDoMes, overheadDoVeiculo) {
  const depreciacao = depreciacaoDoMes(premissas, indiceDoMes);
  const custoDeCapital = custoDeCapitalDoMes(
    valorContabilNoMes(premissas, indiceDoMes),
    politica.custoDeCapitalAnualEmPontosBase
  );
  return {
    depreciacao,
    custoDeCapital,
    overhead: overheadDoVeiculo,
    total: sumAll([depreciacao, custoDeCapital, overheadDoVeiculo])
  };
}
function resolverPremissas(entrada) {
  return {
    valorDeAquisicao: entrada.valorDeAquisicao,
    vidaUtilEmMeses: entrada.vidaUtilDoVeiculo ?? entrada.vidaUtilDaCategoria ?? entrada.politica.vidaUtilEmMeses,
    residualEmPontosBase: entrada.residualDoVeiculo ?? entrada.residualDaCategoria ?? entrada.politica.residualEmPontosBase
  };
}

// src/precos.ts
var precos_exports = {};
__export(precos_exports, {
  COBRANCAS_DE_EXTRA: () => COBRANCAS_DE_EXTRA,
  EFEITOS_DE_REGRA: () => EFEITOS_DE_REGRA,
  FAIXAS_DE_DURACAO: () => FAIXAS_DE_DURACAO,
  PONTOS_BASE_NEUTROS: () => PONTOS_BASE_NEUTROS,
  TIPOS_DE_DESCONTO: () => TIPOS_DE_DESCONTO,
  TIPOS_DE_LINHA: () => TIPOS_DE_LINHA,
  antecedenciaAplicavel: () => antecedenciaAplicavel,
  antecedenciaEmDias: () => antecedenciaEmDias,
  breakdownFecha: () => breakdownFecha,
  cotar: () => cotar,
  descontoDoCupom: () => descontoDoCupom,
  faixaDaDuracao: () => faixaDaDuracao,
  ocupacaoAplicavel: () => ocupacaoAplicavel,
  sazonalidadeDoDia: () => sazonalidadeDoDia,
  valorDoExtra: () => valorDoExtra
});
var PONTOS_BASE_NEUTROS = 1e4;
var FAIXAS_DE_DURACAO = [
  { chave: "ATE_2", rotulo: "1 a 2 di\xE1rias", minimo: 1, maximo: 2 },
  { chave: "DE_3_A_6", rotulo: "3 a 6 di\xE1rias", minimo: 3, maximo: 6 },
  { chave: "DE_7_A_14", rotulo: "7 a 14 di\xE1rias", minimo: 7, maximo: 14 },
  { chave: "DE_15_A_29", rotulo: "15 a 29 di\xE1rias", minimo: 15, maximo: 29 },
  { chave: "DE_30", rotulo: "30 di\xE1rias ou mais", minimo: 30, maximo: null }
];
function faixaDaDuracao(diarias) {
  if (!Number.isInteger(diarias) || diarias < 1) {
    return void 0;
  }
  return FAIXAS_DE_DURACAO.find(
    (faixa) => diarias >= faixa.minimo && (faixa.maximo === null || diarias <= faixa.maximo)
  )?.chave;
}
var EFEITOS_DE_REGRA = ["MULTIPLICADOR", "FIXO"];
var TIPOS_DE_DESCONTO = ["PERCENTUAL", "VALOR"];
var COBRANCAS_DE_EXTRA = ["POR_DIARIA", "POR_RESERVA", "POR_UNIDADE"];
var TIPOS_DE_LINHA = [
  "DIARIAS",
  "SAZONALIDADE",
  "DIA_DA_SEMANA",
  "LIMITE",
  "ANTECEDENCIA",
  "OCUPACAO",
  "EXTRA",
  "CUPOM"
];
function fracao(pontosBase) {
  return { numerator: BigInt(pontosBase), denominator: BigInt(PONTOS_BASE_NEUTROS) };
}
function exigirPontosBase2(valor, rotulo) {
  if (!Number.isInteger(valor) || valor < 0) {
    throw new RangeError(`${rotulo} inv\xE1lido: ${String(valor)}. Informe pontos-base inteiros.`);
  }
}
function diasDaLocacao(entrada) {
  const primeiro2 = toZoned(entrada.retirada, entrada.timeZone);
  return Array.from(
    { length: entrada.diarias },
    (_, indice) => localDayKey(addDays(primeiro2, indice), entrada.timeZone)
  );
}
function antecedenciaEmDias(agora, retirada, timeZone) {
  const hoje = localDayKey(agora, timeZone);
  const dia = localDayKey(retirada, timeZone);
  const diferenca = Date.parse(`${dia}T00:00:00Z`) - Date.parse(`${hoje}T00:00:00Z`);
  return Math.round(diferenca / 864e5);
}
function duracaoDaSazonalidade(regra) {
  return Math.max(
    1,
    Math.round(
      (Date.parse(`${regra.fim}T00:00:00Z`) - Date.parse(`${regra.inicio}T00:00:00Z`)) / 864e5
    ) + 1
  );
}
function sazonalidadeDoDia(dia, regras) {
  const candidatas = regras.filter((regra) => dia >= regra.inicio && dia <= regra.fim);
  if (candidatas.length === 0) {
    return void 0;
  }
  return [...candidatas].sort((a, b) => {
    if (a.prioridade !== b.prioridade) return b.prioridade - a.prioridade;
    const duracaoA = duracaoDaSazonalidade(a);
    const duracaoB = duracaoDaSazonalidade(b);
    if (duracaoA !== duracaoB) return duracaoA - duracaoB;
    return a.id < b.id ? -1 : 1;
  })[0];
}
function antecedenciaAplicavel(dias, regras) {
  const candidatas = regras.filter(
    (regra) => dias >= regra.minimoEmDias && (regra.maximoEmDias === null || dias <= regra.maximoEmDias)
  );
  return [...candidatas].sort((a, b) => {
    const larguraA = (a.maximoEmDias ?? Number.MAX_SAFE_INTEGER) - a.minimoEmDias;
    const larguraB = (b.maximoEmDias ?? Number.MAX_SAFE_INTEGER) - b.minimoEmDias;
    if (larguraA !== larguraB) return larguraA - larguraB;
    return a.id < b.id ? -1 : 1;
  })[0];
}
function ocupacaoAplicavel(ocupacaoEmPontosBase, regras) {
  if (ocupacaoEmPontosBase === null) {
    return void 0;
  }
  return regras.find(
    (regra) => ocupacaoEmPontosBase >= regra.ocupacaoMinimaEmPontosBase && ocupacaoEmPontosBase <= regra.ocupacaoMaximaEmPontosBase
  );
}
function aplicarLimites(valor, limites) {
  if (limites.diariaMinima !== null && valor < limites.diariaMinima) {
    return { valor: limites.diariaMinima, limitado: true };
  }
  if (limites.diariaMaxima !== null && valor > limites.diariaMaxima) {
    return { valor: limites.diariaMaxima, limitado: true };
  }
  return { valor, limitado: false };
}
function calcularDia(dia, entrada) {
  const ajustes = [];
  let valor = entrada.diariaBase;
  const sazonalidade = sazonalidadeDoDia(dia, entrada.sazonalidades);
  if (sazonalidade !== void 0) {
    const antes = valor;
    if (sazonalidade.efeito === "FIXO" && sazonalidade.valorFixo !== null) {
      valor = sazonalidade.valorFixo;
    } else {
      exigirPontosBase2(sazonalidade.multiplicadorEmPontosBase, "Multiplicador de sazonalidade");
      valor = mulRate(valor, fracao(sazonalidade.multiplicadorEmPontosBase));
    }
    ajustes.push({
      origem: "SAZONALIDADE",
      regraId: sazonalidade.id,
      nome: sazonalidade.nome,
      efeito: sazonalidade.efeito,
      multiplicadorEmPontosBase: sazonalidade.multiplicadorEmPontosBase,
      delta: sub(valor, antes)
    });
  }
  const diaDaSemana = dayOfWeekFromKey(dia);
  const regraDeSemana = entrada.diasDaSemana.find(
    (regra) => regra.diasDaSemana.includes(diaDaSemana)
  );
  if (regraDeSemana !== void 0) {
    exigirPontosBase2(regraDeSemana.multiplicadorEmPontosBase, "Multiplicador de dia da semana");
    const antes = valor;
    valor = mulRate(valor, fracao(regraDeSemana.multiplicadorEmPontosBase));
    ajustes.push({
      origem: "DIA_DA_SEMANA",
      regraId: regraDeSemana.id,
      nome: regraDeSemana.nome,
      efeito: "MULTIPLICADOR",
      multiplicadorEmPontosBase: regraDeSemana.multiplicadorEmPontosBase,
      // Medido sobre o valor que a sazonalidade já produziu — ver `AjusteDoDia`.
      delta: sub(valor, antes)
    });
  }
  const limitado = aplicarLimites(valor, entrada.limites);
  return {
    data: dia,
    base: entrada.diariaBase,
    ajustes,
    valor: limitado.valor,
    limitado: limitado.limitado,
    deltaDoLimite: sub(limitado.valor, valor)
  };
}
function valorDoExtra(extra, diarias) {
  const unidades = extra.cobranca === "POR_DIARIA" ? diarias * extra.quantidade : extra.cobranca === "POR_UNIDADE" ? extra.quantidade : 1;
  return fromCents(extra.valorUnitario * BigInt(Math.max(0, Math.trunc(unidades))));
}
function descontoDoCupom(cupom, base) {
  if (base <= 0n) {
    return zero;
  }
  const bruto = cupom.tipo === "VALOR" ? cupom.valorFixo ?? zero : mulRate(base, fracao(cupom.valorEmPontosBase));
  const comTeto = cupom.tipo === "PERCENTUAL" && cupom.descontoMaximo !== null && bruto > cupom.descontoMaximo ? cupom.descontoMaximo : bruto;
  return comTeto > base ? base : comTeto;
}
function cotar(entrada) {
  const faixa = faixaDaDuracao(entrada.diarias);
  if (faixa === void 0) {
    throw new RangeError(
      `Dura\xE7\xE3o inv\xE1lida: ${String(entrada.diarias)} di\xE1rias. Toda loca\xE7\xE3o tem ao menos uma.`
    );
  }
  const porDia = diasDaLocacao(entrada).map((dia) => calcularDia(dia, entrada));
  const somaDosDias = sumAll(porDia.map((dia) => dia.valor));
  const linhas = [
    {
      tipo: "DIARIAS",
      descricao: `${String(entrada.diarias)} ${entrada.diarias === 1 ? "di\xE1ria" : "di\xE1rias"}`,
      regraId: null,
      quantidade: entrada.diarias,
      valor: fromCents(entrada.diariaBase * BigInt(entrada.diarias))
    }
  ];
  for (const origem of ["SAZONALIDADE", "DIA_DA_SEMANA"]) {
    const porRegra = /* @__PURE__ */ new Map();
    for (const dia of porDia) {
      for (const ajuste of dia.ajustes) {
        if (ajuste.origem !== origem) {
          continue;
        }
        const atual = porRegra.get(ajuste.regraId) ?? { nome: ajuste.nome, dias: 0, delta: [] };
        porRegra.set(ajuste.regraId, {
          nome: atual.nome,
          dias: atual.dias + 1,
          delta: [...atual.delta, ajuste.delta]
        });
      }
    }
    for (const [regraId, dados] of porRegra) {
      linhas.push({
        tipo: origem,
        descricao: `${dados.nome} \xB7 ${String(dados.dias)} ${dados.dias === 1 ? "dia" : "dias"}`,
        regraId,
        quantidade: dados.dias,
        valor: sumAll(dados.delta)
      });
    }
  }
  const diasLimitados = porDia.filter((dia) => dia.limitado);
  if (diasLimitados.length > 0) {
    linhas.push({
      tipo: "LIMITE",
      descricao: `Teto ou piso da categoria \xB7 ${String(diasLimitados.length)} ${diasLimitados.length === 1 ? "dia" : "dias"}`,
      regraId: null,
      quantidade: diasLimitados.length,
      valor: sumAll(diasLimitados.map((dia) => dia.deltaDoLimite))
    });
  }
  let subtotal = somaDosDias;
  const antecedencia = antecedenciaAplicavel(
    antecedenciaEmDias(entrada.agora, entrada.retirada, entrada.timeZone),
    entrada.antecedencias
  );
  if (antecedencia !== void 0) {
    exigirPontosBase2(antecedencia.multiplicadorEmPontosBase, "Multiplicador de anteced\xEAncia");
    const ajustado = mulRate(subtotal, fracao(antecedencia.multiplicadorEmPontosBase));
    linhas.push({
      tipo: "ANTECEDENCIA",
      descricao: antecedencia.nome,
      regraId: antecedencia.id,
      quantidade: 1,
      valor: sub(ajustado, subtotal)
    });
    subtotal = ajustado;
  }
  const ocupacao = ocupacaoAplicavel(entrada.ocupacaoPrevistaEmPontosBase, entrada.ocupacoes);
  if (ocupacao !== void 0) {
    exigirPontosBase2(ocupacao.multiplicadorEmPontosBase, "Multiplicador de ocupa\xE7\xE3o");
    const ajustado = mulRate(subtotal, fracao(ocupacao.multiplicadorEmPontosBase));
    linhas.push({
      tipo: "OCUPACAO",
      descricao: ocupacao.nome,
      regraId: ocupacao.id,
      quantidade: 1,
      valor: sub(ajustado, subtotal)
    });
    subtotal = ajustado;
  }
  const totalDeExtras = sumAll(
    entrada.extras.map((extra) => {
      const valor = valorDoExtra(extra, entrada.diarias);
      linhas.push({
        tipo: "EXTRA",
        descricao: extra.nome,
        regraId: extra.id,
        quantidade: extra.quantidade,
        valor
      });
      return valor;
    })
  );
  const antesDoDesconto = add(subtotal, totalDeExtras);
  const desconto = entrada.cupom === null ? zero : descontoDoCupom(entrada.cupom, antesDoDesconto);
  if (entrada.cupom !== null && desconto > 0n) {
    linhas.push({
      tipo: "CUPOM",
      descricao: `Cupom ${entrada.cupom.codigo}`,
      regraId: entrada.cupom.id,
      quantidade: 1,
      // Negativo: o breakdown soma, não subtrai em lugar nenhum.
      valor: fromCents(0n - desconto)
    });
  }
  const total = sub(antesDoDesconto, desconto);
  return {
    diarias: entrada.diarias,
    faixa,
    diariaBase: entrada.diariaBase,
    porDia,
    linhas,
    subtotalDasDiarias: subtotal,
    totalDeExtras,
    desconto,
    total,
    // Divisão inteira: a média é informativa, e arredondar para cima faria a
    // soma das "médias" passar do total.
    diariaMedia: fromCents(total / BigInt(entrada.diarias))
  };
}
function breakdownFecha(cotacao) {
  return sumAll(cotacao.linhas.map((linha) => linha.valor)) === cotacao.total;
}

// src/precificacao.ts
var precificacao_exports = {};
__export(precificacao_exports, {
  ANTECEDENCIA_MAXIMA_EM_DIAS: () => ANTECEDENCIA_MAXIMA_EM_DIAS,
  BUFFER_MAXIMO_EM_HORAS: () => BUFFER_MAXIMO_EM_HORAS,
  CHAVES_DE_FAIXA: () => CHAVES_DE_FAIXA,
  COBRANCAS_DE_EXTRA: () => COBRANCAS_DE_EXTRA,
  CODIGO_DE_CUPOM_TAMANHO_MAXIMO: () => CODIGO_DE_CUPOM_TAMANHO_MAXIMO,
  CODIGO_DE_CUPOM_TAMANHO_MINIMO: () => CODIGO_DE_CUPOM_TAMANHO_MINIMO,
  DIAS_DA_SEMANA_VALIDOS: () => DIAS_DA_SEMANA_VALIDOS,
  EFEITOS_DE_REGRA: () => EFEITOS_DE_REGRA,
  FAIXAS_DE_DURACAO: () => FAIXAS_DE_DURACAO,
  MULTIPLICADOR_MAXIMO_EM_PONTOS_BASE: () => MULTIPLICADOR_MAXIMO_EM_PONTOS_BASE,
  ORIGENS_DE_AJUSTE: () => ORIGENS_DE_AJUSTE,
  OVERBOOKING_MAXIMO_EM_PONTOS_BASE: () => OVERBOOKING_MAXIMO_EM_PONTOS_BASE,
  PONTOS_BASE_NEUTROS: () => PONTOS_BASE_NEUTROS,
  PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS: () => PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS,
  PRIORIDADE_MAXIMA: () => PRIORIDADE_MAXIMA,
  ROTULO_DA_COBRANCA: () => ROTULO_DA_COBRANCA,
  ROTULO_DA_FAIXA: () => ROTULO_DA_FAIXA,
  ROTULO_DA_ORIGEM_DO_AJUSTE: () => ROTULO_DA_ORIGEM_DO_AJUSTE,
  ROTULO_DO_EFEITO: () => ROTULO_DO_EFEITO,
  ROTULO_DO_TIPO_DE_DESCONTO: () => ROTULO_DO_TIPO_DE_DESCONTO,
  ROTULO_DO_TIPO_DE_EXTRA: () => ROTULO_DO_TIPO_DE_EXTRA,
  ROTULO_DO_TIPO_DE_LINHA: () => ROTULO_DO_TIPO_DE_LINHA,
  TIPOS_DE_DESCONTO: () => TIPOS_DE_DESCONTO,
  TIPOS_DE_EXTRA: () => TIPOS_DE_EXTRA,
  TIPOS_DE_EXTRA_COM_PRESTADOR: () => TIPOS_DE_EXTRA_COM_PRESTADOR,
  TIPOS_DE_LINHA: () => TIPOS_DE_LINHA,
  TIPO_DE_EXTRA_COM_FRANQUIA: () => TIPO_DE_EXTRA_COM_FRANQUIA,
  TIPO_DE_EXTRA_COM_KM: () => TIPO_DE_EXTRA_COM_KM,
  TOLERANCIA_MAXIMA_EM_MINUTOS: () => TOLERANCIA_MAXIMA_EM_MINUTOS,
  VALIDADE_MAXIMA_DA_COTACAO_EM_MINUTOS: () => VALIDADE_MAXIMA_DA_COTACAO_EM_MINUTOS,
  atualizarCupomSchema: () => atualizarCupomSchema,
  atualizarExtraSchema: () => atualizarExtraSchema,
  atualizarRegraDeAntecedenciaSchema: () => atualizarRegraDeAntecedenciaSchema,
  atualizarRegraDeDiaDaSemanaSchema: () => atualizarRegraDeDiaDaSemanaSchema,
  atualizarRegraDeOcupacaoSchema: () => atualizarRegraDeOcupacaoSchema,
  atualizarSazonalidadeSchema: () => atualizarSazonalidadeSchema,
  atualizarTabelaDePrecoSchema: () => atualizarTabelaDePrecoSchema,
  chaveDaFaixaSchema: () => chaveDaFaixaSchema,
  cobrancaDeExtraSchema: () => cobrancaDeExtraSchema,
  codigoDeCupomSchema: () => codigoDeCupomSchema,
  criarCupomSchema: () => criarCupomSchema,
  criarExtraSchema: () => criarExtraSchema,
  criarRegraDeAntecedenciaSchema: () => criarRegraDeAntecedenciaSchema,
  criarRegraDeDiaDaSemanaSchema: () => criarRegraDeDiaDaSemanaSchema,
  criarRegraDeOcupacaoSchema: () => criarRegraDeOcupacaoSchema,
  criarSazonalidadeSchema: () => criarSazonalidadeSchema,
  criarTabelaDePrecoSchema: () => criarTabelaDePrecoSchema,
  dataSimplesSchema: () => dataSimplesSchema2,
  descontoDoCupomCoerente: () => descontoDoCupomCoerente,
  efeitoDeRegraSchema: () => efeitoDeRegraSchema,
  efeitoDeSazonalidadeCoerente: () => efeitoDeSazonalidadeCoerente,
  extraEscolhidoSchema: () => extraEscolhidoSchema,
  faixasFaltantes: () => faixasFaltantes,
  filtroDeCupomSchema: () => filtroDeCupomSchema,
  filtroDeExtraSchema: () => filtroDeExtraSchema,
  filtroDeRegraDePrecoSchema: () => filtroDeRegraDePrecoSchema,
  filtroDeSazonalidadeSchema: () => filtroDeSazonalidadeSchema,
  filtroDeTabelaDePrecoSchema: () => filtroDeTabelaDePrecoSchema,
  filtroDeTarifaSchema: () => filtroDeTarifaSchema,
  franquiaCoerente: () => franquiaCoerente,
  kmPorUnidadeCoerente: () => kmPorUnidadeCoerente,
  multiplicadorEmPontosBaseSchema: () => multiplicadorEmPontosBaseSchema,
  normalizarCodigoDeCupom: () => normalizarCodigoDeCupom,
  origemDoAjusteSchema: () => origemDoAjusteSchema,
  prioridadeSchema: () => prioridadeSchema,
  salvarPoliticaDeLocacaoSchema: () => salvarPoliticaDeLocacaoSchema,
  salvarTarifasSchema: () => salvarTarifasSchema,
  simularToleranciaSchema: () => simularToleranciaSchema,
  tabelaNaoCrescente: () => tabelaNaoCrescente,
  tarifaDaFaixaSchema: () => tarifaDaFaixaSchema,
  tipoDeDescontoSchema: () => tipoDeDescontoSchema,
  tipoDeExtraSchema: () => tipoDeExtraSchema,
  tipoDeLinhaSchema: () => tipoDeLinhaSchema,
  validarCodigoDeCupom: () => validarCodigoDeCupom,
  vigenciaValida: () => vigenciaValida
});
var DIAS_MAXIMOS_DE_RETENCAO_DA_CAUCAO = 25;
var MARGEM_ANTES_DO_VENCIMENTO_EM_DIAS = 1;
var DIAS_MINIMOS_DE_AVISO = 1;
var DIAS_MAXIMOS_DE_AVISO = 15;
var TIPOS_DE_TAXA_DE_MULTA = ["NENHUMA", "PERCENTUAL", "FIXA"];
var ROTULO_DO_TIPO_DE_TAXA_DE_MULTA = {
  NENHUMA: "Sem taxa",
  PERCENTUAL: "Percentual sobre a multa",
  FIXA: "Valor fixo por multa"
};
var TAXA_PERCENTUAL_MAXIMA = 100;
var POLITICA_DE_MULTA_PADRAO = {
  diasDeRetencaoDaCaucao: 0,
  cobrancaPosteriorDeMulta: false,
  diasDeAvisoAntesDaCobranca: 3,
  taxaDeMultaTipo: "NENHUMA",
  taxaDeMultaPercentual: 0,
  taxaDeMultaFixa: fromWire("0")
};
var camposDaPoliticaDeMulta = {
  diasDeRetencaoDaCaucao: z.coerce.number({ error: "informe os dias de reten\xE7\xE3o da cau\xE7\xE3o" }).int({ error: "os dias de reten\xE7\xE3o s\xE3o um n\xFAmero inteiro" }).min(0, { error: "os dias de reten\xE7\xE3o n\xE3o podem ser negativos" }).max(DIAS_MAXIMOS_DE_RETENCAO_DA_CAUCAO, {
    error: `no m\xE1ximo ${String(DIAS_MAXIMOS_DE_RETENCAO_DA_CAUCAO)} dias \u2014 o bloqueio no cart\xE3o vence sozinho por volta de 28 dias depois de feito`
  }).default(0),
  cobrancaPosteriorDeMulta: z.boolean().default(false),
  diasDeAvisoAntesDaCobranca: z.coerce.number({ error: "informe os dias de aviso" }).int({ error: "os dias de aviso s\xE3o um n\xFAmero inteiro" }).min(DIAS_MINIMOS_DE_AVISO, { error: "o cliente precisa de pelo menos 1 dia de aviso" }).max(DIAS_MAXIMOS_DE_AVISO, {
    error: `no m\xE1ximo ${String(DIAS_MAXIMOS_DE_AVISO)} dias de aviso`
  }).default(3),
  taxaDeMultaTipo: z.enum(TIPOS_DE_TAXA_DE_MULTA).default("NENHUMA"),
  taxaDeMultaPercentual: z.coerce.number().int({ error: "o percentual da taxa \xE9 um n\xFAmero inteiro" }).min(0, { error: "o percentual da taxa n\xE3o pode ser negativo" }).max(TAXA_PERCENTUAL_MAXIMA, { error: "o percentual m\xE1ximo da taxa \xE9 100%" }).default(0),
  /** Centavos, em string (o fio do dinheiro). */
  taxaDeMultaFixa: z.string().regex(/^\d{1,12}$/, { error: "valor fixo inv\xE1lido \u2014 informe em centavos" }).default("0")
};
function calcularTaxaDeMulta(valorDaMulta, politica) {
  switch (politica.taxaDeMultaTipo) {
    case "NENHUMA":
      return fromWire("0");
    case "PERCENTUAL":
      return percentOf(valorDaMulta, String(politica.taxaDeMultaPercentual));
    case "FIXA":
      return politica.taxaDeMultaFixa;
  }
}
var MILISSEGUNDOS_POR_DIA = 864e5;
function retencaoDaCaucaoAte(entrada) {
  if (entrada.diasDeRetencao <= 0) {
    return null;
  }
  const pedido = entrada.fechadaEm.getTime() + entrada.diasDeRetencao * MILISSEGUNDOS_POR_DIA;
  const teto = entrada.autorizacaoExpiraEm === null ? pedido : entrada.autorizacaoExpiraEm.getTime() - MARGEM_ANTES_DO_VENCIMENTO_EM_DIAS * MILISSEGUNDOS_POR_DIA;
  const ate = Math.min(pedido, teto);
  return ate > entrada.fechadaEm.getTime() ? new Date(ate) : null;
}
var DESTINOS_DA_COBRANCA_DE_MULTA = ["CAUCAO", "CARTAO", "MANUAL"];
var ROTULO_DO_DESTINO_DA_COBRANCA = {
  CAUCAO: "Descontada da cau\xE7\xE3o",
  CARTAO: "Cobrada no cart\xE3o guardado no provedor",
  MANUAL: "Cobran\xE7a manual"
};
function destinoDaCobrancaDeMulta(entrada) {
  if (entrada.caucaoRetida !== null && entrada.total <= entrada.caucaoRetida.folga) {
    return "CAUCAO";
  }
  if (entrada.cobrancaPosteriorPermitida && entrada.temCartaoGuardado) {
    return "CARTAO";
  }
  return "MANUAL";
}
function cobrancaNoCartaoEm(avisadoEm, diasDeAviso) {
  return new Date(avisadoEm.getTime() + diasDeAviso * MILISSEGUNDOS_POR_DIA);
}
function politicaDeMultaNoFio(politica) {
  return { ...politica, taxaDeMultaFixa: toWire(politica.taxaDeMultaFixa) };
}

// src/precificacao.ts
var MULTIPLICADOR_MAXIMO_EM_PONTOS_BASE = 1e5;
var PRIORIDADE_MAXIMA = 1e3;
var TOLERANCIA_MAXIMA_EM_MINUTOS = 1439;
var BUFFER_MAXIMO_EM_HORAS = 168;
var OVERBOOKING_MAXIMO_EM_PONTOS_BASE = 5e3;
var VALIDADE_MAXIMA_DA_COTACAO_EM_MINUTOS = 43200;
var PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS = 720;
var CODIGO_DE_CUPOM_TAMANHO_MINIMO = 3;
var CODIGO_DE_CUPOM_TAMANHO_MAXIMO = 32;
var CHAVES_DE_FAIXA = FAIXAS_DE_DURACAO.map((faixa) => faixa.chave);
var chaveDaFaixaSchema = z.enum(CHAVES_DE_FAIXA, {
  error: "faixa de dura\xE7\xE3o inv\xE1lida"
});
var ROTULO_DA_FAIXA = Object.fromEntries(
  FAIXAS_DE_DURACAO.map((faixa) => [faixa.chave, faixa.rotulo])
);
var efeitoDeRegraSchema = z.enum(EFEITOS_DE_REGRA, { error: "efeito de regra inv\xE1lido" });
var ROTULO_DO_EFEITO = {
  MULTIPLICADOR: "Multiplicador",
  FIXO: "Valor fixo da di\xE1ria"
};
var tipoDeDescontoSchema = z.enum(TIPOS_DE_DESCONTO, {
  error: "tipo de desconto inv\xE1lido"
});
var ROTULO_DO_TIPO_DE_DESCONTO = {
  PERCENTUAL: "Percentual",
  VALOR: "Valor fixo"
};
var cobrancaDeExtraSchema = z.enum(COBRANCAS_DE_EXTRA, {
  error: "forma de cobran\xE7a do extra inv\xE1lida"
});
var ROTULO_DA_COBRANCA = {
  POR_DIARIA: "Por di\xE1ria",
  POR_RESERVA: "Por reserva",
  POR_UNIDADE: "Por unidade"
};
var TIPOS_DE_EXTRA = [
  "PROTECAO",
  "CONDUTOR_ADICIONAL",
  "ACESSORIO",
  "ENTREGA",
  "DEVOLUCAO",
  "MOTORISTA",
  "SEGURANCA",
  "KM_EXTRA",
  "OUTRO"
];
var tipoDeExtraSchema = z.enum(TIPOS_DE_EXTRA, { error: "tipo de extra inv\xE1lido" });
var ROTULO_DO_TIPO_DE_EXTRA = {
  PROTECAO: "Prote\xE7\xE3o",
  CONDUTOR_ADICIONAL: "Condutor adicional",
  ACESSORIO: "Acess\xF3rio",
  ENTREGA: "Entrega fora da base",
  DEVOLUCAO: "Devolu\xE7\xE3o fora da base",
  MOTORISTA: "Motorista",
  SEGURANCA: "Seguran\xE7a",
  KM_EXTRA: "Quilometragem extra",
  OUTRO: "Outro"
};
var TIPO_DE_EXTRA_COM_FRANQUIA = "PROTECAO";
var TIPO_DE_EXTRA_COM_KM = "KM_EXTRA";
var TIPOS_DE_EXTRA_COM_PRESTADOR = ["MOTORISTA", "SEGURANCA"];
var tipoDeLinhaSchema = z.enum(TIPOS_DE_LINHA, { error: "tipo de linha inv\xE1lido" });
var ROTULO_DO_TIPO_DE_LINHA = {
  DIARIAS: "Di\xE1rias",
  SAZONALIDADE: "Sazonalidade",
  DIA_DA_SEMANA: "Dia da semana",
  LIMITE: "Teto ou piso da categoria",
  ANTECEDENCIA: "Anteced\xEAncia",
  OCUPACAO: "Ocupa\xE7\xE3o",
  EXTRA: "Extra",
  CUPOM: "Cupom"
};
var ROTULO_DA_ORIGEM_DO_AJUSTE = {
  SAZONALIDADE: "Sazonalidade",
  DIA_DA_SEMANA: "Dia da semana"
};
var ORIGENS_DE_AJUSTE = Object.keys(
  ROTULO_DA_ORIGEM_DO_AJUSTE
);
var origemDoAjusteSchema = z.enum(ORIGENS_DE_AJUSTE, {
  error: "origem de ajuste inv\xE1lida"
});
var dataSimplesSchema2 = z.iso.date({
  error: "data inv\xE1lida \u2014 use o formato AAAA-MM-DD"
});
var nomeDeRegraSchema = z.string({ error: "nome \xE9 obrigat\xF3rio" }).trim().min(2, { error: "nome precisa ter ao menos 2 caracteres" }).max(120, { error: "nome muito longo" });
var multiplicadorEmPontosBaseSchema = z.coerce.number({ error: "multiplicador \xE9 obrigat\xF3rio" }).int({ error: "o multiplicador \xE9 inteiro, em pontos-base (12000 = 1,2\xD7)" }).min(1, {
  error: "o multiplicador precisa ser maior que zero \u2014 zero zeraria a cota\xE7\xE3o inteira"
}).max(MULTIPLICADOR_MAXIMO_EM_PONTOS_BASE, {
  error: `o multiplicador m\xE1ximo \xE9 ${String(MULTIPLICADOR_MAXIMO_EM_PONTOS_BASE)} pontos-base (10\xD7)`
});
var percentualSchema = z.coerce.number({ error: "percentual \xE9 obrigat\xF3rio" }).int({ error: "o percentual \xE9 inteiro, em pontos-base (1500 = 15%)" }).min(0, { error: "o percentual n\xE3o pode ser negativo" }).max(PONTOS_BASE_NEUTROS, {
  error: `o percentual m\xE1ximo \xE9 ${String(PONTOS_BASE_NEUTROS)} pontos-base (100%)`
});
var inteiroNaoNegativo2 = (rotulo) => z.coerce.number({ error: `${rotulo} \xE9 obrigat\xF3rio` }).int({ error: `${rotulo} precisa ser inteiro` }).min(0, { error: `${rotulo} n\xE3o pode ser negativo` });
var prioridadeSchema = inteiroNaoNegativo2("prioridade").max(PRIORIDADE_MAXIMA, {
  error: `a prioridade m\xE1xima \xE9 ${String(PRIORIDADE_MAXIMA)}`
});
var inteiroPositivo2 = (rotulo) => z.coerce.number({ error: `${rotulo} \xE9 obrigat\xF3rio` }).int({ error: `${rotulo} precisa ser inteiro` }).positive({ error: `${rotulo} precisa ser maior que zero` });
var salvarPoliticaDeLocacaoSchema = z.object({
  toleranciaEmMinutos: z.coerce.number({ error: "toler\xE2ncia \xE9 obrigat\xF3ria" }).int({ error: "a toler\xE2ncia \xE9 um n\xFAmero inteiro de minutos" }).min(0, { error: "a toler\xE2ncia n\xE3o pode ser negativa" }).max(TOLERANCIA_MAXIMA_EM_MINUTOS, {
    error: "a toler\xE2ncia precisa ser menor que um dia inteiro \u2014 24h dariam uma di\xE1ria de gra\xE7a"
  }).default(0),
  bufferEmHoras: inteiroNaoNegativo2("buffer entre loca\xE7\xF5es").max(BUFFER_MAXIMO_EM_HORAS, {
    error: `o buffer m\xE1ximo \xE9 ${String(BUFFER_MAXIMO_EM_HORAS)} horas`
  }).default(3),
  overbookingEmPontosBase: inteiroNaoNegativo2("overbooking").max(OVERBOOKING_MAXIMO_EM_PONTOS_BASE, {
    error: `o overbooking m\xE1ximo \xE9 ${String(OVERBOOKING_MAXIMO_EM_PONTOS_BASE)} pontos-base (50%)`
  }).default(0),
  /** Desligado por padrão, como o prompt da fase manda. */
  precoPorOcupacaoAtivo: z.boolean().default(false),
  validadeDaCotacaoEmMinutos: inteiroPositivo2("validade da cota\xE7\xE3o").max(VALIDADE_MAXIMA_DA_COTACAO_EM_MINUTOS, {
    error: `a validade m\xE1xima \xE9 ${String(VALIDADE_MAXIMA_DA_COTACAO_EM_MINUTOS)} minutos (30 dias)`
  }).default(30),
  /**
   * Horas para pagar depois de "enviar para pagamento"; vencido o prazo, a
   * reserva expira sozinha. Era constante de código (48) — virou política por
   * decisão de 03/09/2026.
   */
  prazoDePagamentoEmHoras: inteiroPositivo2("prazo de pagamento").max(PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS, {
    error: `o prazo m\xE1ximo \xE9 ${String(PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS)} horas (30 dias)`
  }).default(48),
  /**
   * % do valor das diárias NÃO usadas creditado ao cliente quando ele devolve
   * antes do prazo. 0 (padrão do setor) = sem reembolso — as diárias
   * reservadas recusaram outros clientes na malha. O percentual é CONGELADO
   * na reserva na criação, como o resto do snapshot financeiro.
   */
  reembolsoDeDevolucaoAntecipadaPercentual: inteiroNaoNegativo2("reembolso de devolu\xE7\xE3o antecipada").max(100, { error: "o reembolso m\xE1ximo \xE9 100%" }).default(0),
  /**
   * Horas de atraso na retirada até o SISTEMA marcar não comparecimento
   * sozinho. `null` (padrão) = marcar continua decisão humana do balcão — o
   * cliente pode estar no trânsito, ao telefone, na porta.
   */
  noShowAposHoras: inteiroPositivo2("prazo de no-show").max(PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS, {
    error: `o prazo m\xE1ximo \xE9 ${String(PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS)} horas (30 dias)`
  }).nullable().default(null),
  /**
   * MULTA QUE CHEGA DEPOIS DA DEVOLUÇÃO (decisão de 26/10/2026): retenção da
   * caução, cobrança posterior no cartão guardado no provedor, aviso e taxa.
   * As regras moram em `multa-pos-devolucao.ts`; aqui só se espalham.
   */
  ...camposDaPoliticaDeMulta
});
var simularToleranciaSchema = z.object({
  toleranciaEmMinutos: z.coerce.number().int().min(0).max(TOLERANCIA_MAXIMA_EM_MINUTOS, {
    error: "a toler\xE2ncia precisa ser menor que um dia inteiro"
  }),
  atrasoEmMinutos: inteiroNaoNegativo2("atraso").max(10080, {
    error: "simule um atraso de at\xE9 uma semana"
  }),
  diariasContratadas: inteiroPositivo2("di\xE1rias contratadas").max(365, {
    error: "simule uma loca\xE7\xE3o de at\xE9 365 di\xE1rias"
  })
});
var camposDaTabelaDePreco = {
  nome: nomeDeRegraSchema,
  /** `nullish` e não `optional`: sem o `null`, a descrição nunca se esvazia. */
  descricao: z.string().trim().max(2e3, { error: "descri\xE7\xE3o muito longa" }).nullish(),
  /**
   * A tabela da vitrine. UMA por tenant — garantido por índice único parcial na
   * migration, e não por disciplina do serviço: duas padrão fariam a cotação
   * depender da ordem em que o `findFirst` devolveu as linhas.
   */
  padrao: z.boolean().default(false),
  vigenteDe: dataSimplesSchema2,
  /** `null` = sem prazo. */
  vigenteAte: dataSimplesSchema2.nullish(),
  ativo: z.boolean().default(true)
};
function vigenciaValida(vigencia) {
  if (vigencia.vigenteDe === void 0 || vigencia.vigenteAte === null || vigencia.vigenteAte === void 0) {
    return true;
  }
  return vigencia.vigenteAte >= vigencia.vigenteDe;
}
var ERRO_DE_VIGENCIA = {
  error: "o fim da vig\xEAncia precisa ser igual ou posterior ao in\xEDcio",
  path: ["vigenteAte"]
};
var criarTabelaDePrecoSchema = z.object(camposDaTabelaDePreco).refine(vigenciaValida, ERRO_DE_VIGENCIA);
var atualizarTabelaDePrecoSchema = z.object(camposDaTabelaDePreco).partial().refine(vigenciaValida, ERRO_DE_VIGENCIA);
var filtroDeTabelaDePrecoSchema = paginacaoSchema.extend({
  ativo: z.stringbool().optional(),
  padrao: z.stringbool().optional(),
  /** Só as tabelas vigentes nesta data. */
  vigenteEm: dataSimplesSchema2.optional()
});
var tarifaDaFaixaSchema = z.object({
  faixa: chaveDaFaixaSchema,
  /** A diária cheia daquela faixa. Zero não é tarifa: é tabela sem preço. */
  diaria: moneySchemaPositive
});
function tabelaNaoCrescente(tarifas) {
  const naOrdemDoMotor = FAIXAS_DE_DURACAO.map(
    (faixa) => tarifas.find((tarifa) => tarifa.faixa === faixa.chave)
  ).filter((tarifa) => tarifa !== void 0);
  let anterior = null;
  for (const tarifa of naOrdemDoMotor) {
    if (anterior !== null && tarifa.diaria > anterior) {
      return false;
    }
    anterior = tarifa.diaria;
  }
  return true;
}
function faixasFaltantes(tarifas) {
  return CHAVES_DE_FAIXA.filter((chave) => !tarifas.some((tarifa) => tarifa.faixa === chave));
}
function semFaixaRepetida(tarifas) {
  return new Set(tarifas.map((tarifa) => tarifa.faixa)).size === tarifas.length;
}
var salvarTarifasSchema = z.object({
  categoryId: uuidSchema,
  tarifas: z.array(tarifaDaFaixaSchema).min(1, { error: "informe ao menos uma faixa de dura\xE7\xE3o" }).max(FAIXAS_DE_DURACAO.length, { error: "h\xE1 mais tarifas do que faixas de dura\xE7\xE3o" })
}).refine((entrada) => semFaixaRepetida(entrada.tarifas), {
  error: "h\xE1 duas tarifas para a mesma faixa de dura\xE7\xE3o",
  path: ["tarifas"]
}).refine((entrada) => tabelaNaoCrescente(entrada.tarifas), {
  error: "a di\xE1ria n\xE3o pode subir ao alongar a loca\xE7\xE3o \u2014 confira a ordem das faixas",
  path: ["tarifas"]
});
var filtroDeTarifaSchema = paginacaoSchema.extend({
  priceTableId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  faixa: chaveDaFaixaSchema.optional()
});
var camposDaSazonalidade = {
  /** `null` = vale em qualquer tabela de preço. */
  priceTableId: uuidSchema.nullish(),
  /** `null` = vale para todas as categorias. */
  categoryId: uuidSchema.nullish(),
  nome: nomeDeRegraSchema,
  inicio: dataSimplesSchema2,
  /** Inclusive: uma regra de 20 a 27 pega o dia 27. */
  fim: dataSimplesSchema2,
  efeito: efeitoDeRegraSchema,
  multiplicadorEmPontosBase: multiplicadorEmPontosBaseSchema.default(PONTOS_BASE_NEUTROS),
  /** A diária cheia quando o efeito é `FIXO`. Ignorada em `MULTIPLICADOR`. */
  valorFixo: moneySchemaPositive.nullish(),
  /** Maior vence. No empate, o período mais CURTO — o motor decide, não a API. */
  prioridade: prioridadeSchema.default(0),
  ativo: z.boolean().default(true)
};
function efeitoDeSazonalidadeCoerente(regra) {
  if (regra.efeito !== "FIXO") {
    return true;
  }
  return regra.valorFixo !== null && regra.valorFixo !== void 0 && regra.valorFixo > 0n;
}
function periodoDaSazonalidadeValido(regra) {
  if (regra.inicio === void 0 || regra.fim === void 0) {
    return true;
  }
  return regra.fim >= regra.inicio;
}
var ERRO_DE_PERIODO_DA_SAZONALIDADE = {
  error: "o fim do per\xEDodo precisa ser igual ou posterior ao in\xEDcio",
  path: ["fim"]
};
var ERRO_DE_EFEITO_FIXO = {
  error: "efeito FIXO exige o valor da di\xE1ria \u2014 sem ele a regra n\xE3o faz nada",
  path: ["valorFixo"]
};
var criarSazonalidadeSchema = z.object(camposDaSazonalidade).refine(periodoDaSazonalidadeValido, ERRO_DE_PERIODO_DA_SAZONALIDADE).refine(efeitoDeSazonalidadeCoerente, ERRO_DE_EFEITO_FIXO);
var atualizarSazonalidadeSchema = z.object(camposDaSazonalidade).partial().refine(periodoDaSazonalidadeValido, ERRO_DE_PERIODO_DA_SAZONALIDADE).refine(efeitoDeSazonalidadeCoerente, ERRO_DE_EFEITO_FIXO);
var filtroDeSazonalidadeSchema = paginacaoSchema.extend({
  priceTableId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  efeito: efeitoDeRegraSchema.optional(),
  ativo: z.stringbool().optional(),
  /** Só as regras que cobrem este dia — é como a tela explica um preço. */
  vigenteEm: dataSimplesSchema2.optional()
});
var DIAS_DA_SEMANA_VALIDOS = [0, 1, 2, 3, 4, 5, 6];
var camposDaRegraDeDiaDaSemana = {
  categoryId: uuidSchema.nullish(),
  nome: nomeDeRegraSchema,
  diasDaSemana: z.array(
    z.coerce.number({ error: "dia da semana \xE9 obrigat\xF3rio" }).int({ error: "o dia da semana \xE9 inteiro" }).min(0, { error: "o dia da semana vai de 0 (domingo) a 6 (s\xE1bado)" }).max(6, { error: "o dia da semana vai de 0 (domingo) a 6 (s\xE1bado)" })
  ).min(1, { error: "escolha ao menos um dia da semana" }).max(7, { error: "a semana tem sete dias" }).refine((dias) => new Set(dias).size === dias.length, {
    error: "h\xE1 dias repetidos \u2014 o multiplicador seria aplicado uma vez s\xF3 de qualquer forma"
  }),
  multiplicadorEmPontosBase: multiplicadorEmPontosBaseSchema,
  ativo: z.boolean().default(true)
};
var criarRegraDeDiaDaSemanaSchema = z.object(camposDaRegraDeDiaDaSemana);
var atualizarRegraDeDiaDaSemanaSchema = z.object(camposDaRegraDeDiaDaSemana).partial();
var ANTECEDENCIA_MAXIMA_EM_DIAS = 730;
var camposDaRegraDeAntecedencia = {
  categoryId: uuidSchema.nullish(),
  nome: nomeDeRegraSchema,
  minimoEmDias: inteiroNaoNegativo2("anteced\xEAncia m\xEDnima").max(ANTECEDENCIA_MAXIMA_EM_DIAS, {
    error: `a anteced\xEAncia m\xE1xima \xE9 ${String(ANTECEDENCIA_MAXIMA_EM_DIAS)} dias`
  }),
  /** `null` = sem teto — é o early bird "de 30 dias em diante". */
  maximoEmDias: inteiroNaoNegativo2("anteced\xEAncia m\xE1xima").max(ANTECEDENCIA_MAXIMA_EM_DIAS, {
    error: `a anteced\xEAncia m\xE1xima \xE9 ${String(ANTECEDENCIA_MAXIMA_EM_DIAS)} dias`
  }).nullish(),
  multiplicadorEmPontosBase: multiplicadorEmPontosBaseSchema,
  ativo: z.boolean().default(true)
};
function faixaDeAntecedenciaValida(regra) {
  if (regra.minimoEmDias === void 0 || regra.maximoEmDias === null || regra.maximoEmDias === void 0) {
    return true;
  }
  return regra.maximoEmDias >= regra.minimoEmDias;
}
var ERRO_DE_FAIXA_DE_ANTECEDENCIA = {
  error: "a anteced\xEAncia m\xE1xima precisa ser igual ou maior que a m\xEDnima",
  path: ["maximoEmDias"]
};
var criarRegraDeAntecedenciaSchema = z.object(camposDaRegraDeAntecedencia).refine(faixaDeAntecedenciaValida, ERRO_DE_FAIXA_DE_ANTECEDENCIA);
var atualizarRegraDeAntecedenciaSchema = z.object(camposDaRegraDeAntecedencia).partial().refine(faixaDeAntecedenciaValida, ERRO_DE_FAIXA_DE_ANTECEDENCIA);
var camposDaRegraDeOcupacao = {
  categoryId: uuidSchema.nullish(),
  nome: nomeDeRegraSchema,
  /** Faixa de ocupação prevista da categoria, em pontos-base (5000 = 50%). */
  ocupacaoMinimaEmPontosBase: percentualSchema,
  ocupacaoMaximaEmPontosBase: percentualSchema,
  multiplicadorEmPontosBase: multiplicadorEmPontosBaseSchema,
  ativo: z.boolean().default(true)
};
function faixaDeOcupacaoValida(regra) {
  if (regra.ocupacaoMinimaEmPontosBase === void 0 || regra.ocupacaoMaximaEmPontosBase === void 0) {
    return true;
  }
  return regra.ocupacaoMaximaEmPontosBase >= regra.ocupacaoMinimaEmPontosBase;
}
var ERRO_DE_FAIXA_DE_OCUPACAO = {
  error: "a ocupa\xE7\xE3o m\xE1xima precisa ser igual ou maior que a m\xEDnima",
  path: ["ocupacaoMaximaEmPontosBase"]
};
var criarRegraDeOcupacaoSchema = z.object(camposDaRegraDeOcupacao).refine(faixaDeOcupacaoValida, ERRO_DE_FAIXA_DE_OCUPACAO);
var atualizarRegraDeOcupacaoSchema = z.object(camposDaRegraDeOcupacao).partial().refine(faixaDeOcupacaoValida, ERRO_DE_FAIXA_DE_OCUPACAO);
var filtroDeRegraDePrecoSchema = paginacaoSchema.extend({
  categoryId: uuidSchema.optional(),
  ativo: z.stringbool().optional()
});
var camposDoExtra = {
  nome: nomeDeRegraSchema,
  descricao: z.string().trim().max(2e3, { error: "descri\xE7\xE3o muito longa" }).nullish(),
  tipo: tipoDeExtraSchema,
  cobranca: cobrancaDeExtraSchema,
  valorUnitario: moneySchemaNonNegative,
  /** Franquia em caso de sinistro. Só em `PROTECAO` — ver o CHECK da migration. */
  franquia: moneySchemaNonNegative.nullish(),
  /**
   * Quantos km cada unidade deste extra acrescenta à franquia da locação. Só
   * em `KM_EXTRA`: sem este número, o pacote de km é dinheiro que entra e
   * franquia que não cresce — o fechamento cobraria excedente de um km que o
   * cliente comprou.
   */
  kmPorUnidade: inteiroPositivo2("km por unidade").nullish(),
  /** Teto por reserva. `null` = sem teto. */
  quantidadeMaxima: inteiroPositivo2("quantidade m\xE1xima").nullish(),
  /**
   * `true` quando o extra depende de gente livre na escala: a cotação recusa o
   * extra se não houver prestador disponível no período.
   */
  exigePrestador: z.boolean().default(false),
  ordemDeExibicao: inteiroNaoNegativo2("ordem de exibi\xE7\xE3o").default(0),
  ativo: z.boolean().default(true)
};
function franquiaCoerente(extra) {
  if (extra.franquia === null || extra.franquia === void 0) {
    return true;
  }
  return extra.tipo === TIPO_DE_EXTRA_COM_FRANQUIA;
}
var ERRO_DE_FRANQUIA = {
  error: "franquia s\xF3 faz sentido em extra do tipo prote\xE7\xE3o",
  path: ["franquia"]
};
function kmPorUnidadeCoerente(extra) {
  if (extra.kmPorUnidade === null || extra.kmPorUnidade === void 0) {
    return extra.tipo !== TIPO_DE_EXTRA_COM_KM;
  }
  return extra.tipo === void 0 || extra.tipo === TIPO_DE_EXTRA_COM_KM;
}
var ERRO_DE_KM_POR_UNIDADE = {
  error: "km por unidade \xE9 obrigat\xF3rio em KM_EXTRA e n\xE3o faz sentido nos demais tipos",
  path: ["kmPorUnidade"]
};
var criarExtraSchema = z.object(camposDoExtra).refine(franquiaCoerente, ERRO_DE_FRANQUIA).refine(kmPorUnidadeCoerente, ERRO_DE_KM_POR_UNIDADE);
var atualizarExtraSchema = z.object(camposDoExtra).partial().refine(franquiaCoerente, ERRO_DE_FRANQUIA).refine(kmPorUnidadeCoerente, ERRO_DE_KM_POR_UNIDADE);
var filtroDeExtraSchema = paginacaoSchema.extend({
  tipo: tipoDeExtraSchema.optional(),
  cobranca: cobrancaDeExtraSchema.optional(),
  exigePrestador: z.stringbool().optional(),
  ativo: z.stringbool().optional()
});
var extraEscolhidoSchema = z.object({
  extraId: uuidSchema,
  quantidade: inteiroPositivo2("quantidade").max(99, { error: "quantidade implaus\xEDvel para um extra" }).default(1)
});
var CODIGO_DE_CUPOM_PATTERN = /^[A-Z0-9_-]{3,32}$/;
var ESPACO_EM_BRANCO = /\s+/g;
function normalizarCodigoDeCupom(valor) {
  return valor.replace(ESPACO_EM_BRANCO, "").toUpperCase();
}
function validarCodigoDeCupom(valor) {
  return CODIGO_DE_CUPOM_PATTERN.test(normalizarCodigoDeCupom(valor));
}
var codigoDeCupomSchema = z.string({ error: "c\xF3digo do cupom \xE9 obrigat\xF3rio" }).transform(normalizarCodigoDeCupom).refine((valor) => CODIGO_DE_CUPOM_PATTERN.test(valor), {
  error: `o c\xF3digo usa de ${String(CODIGO_DE_CUPOM_TAMANHO_MINIMO)} a ${String(CODIGO_DE_CUPOM_TAMANHO_MAXIMO)} caracteres entre letras, n\xFAmeros, h\xEDfen e sublinhado`
});
var camposDoCupom = {
  tipo: tipoDeDescontoSchema,
  /** Pontos-base quando `PERCENTUAL` (1500 = 15%). */
  valorEmPontosBase: percentualSchema.default(0),
  /** O valor quando `VALOR`. */
  valorFixo: moneySchemaPositive.nullish(),
  /** Teto do desconto em `PERCENTUAL`. `null` = sem teto. */
  descontoMaximo: moneySchemaPositive.nullish(),
  /** Piso da reserva para o cupom valer. */
  valorMinimoDaReserva: moneySchemaNonNegative.nullish(),
  vigenteDe: dataSimplesSchema2,
  vigenteAte: dataSimplesSchema2.nullish(),
  /** `null` = ilimitado. */
  usosMaximos: inteiroPositivo2("usos m\xE1ximos").nullish(),
  usosPorConta: inteiroPositivo2("usos por conta").nullish(),
  ativo: z.boolean().default(true)
};
function descontoDoCupomCoerente(cupom) {
  if (cupom.tipo === void 0) {
    return true;
  }
  if (cupom.tipo === "PERCENTUAL") {
    return cupom.valorEmPontosBase !== void 0 && cupom.valorEmPontosBase > 0 && cupom.valorEmPontosBase <= PONTOS_BASE_NEUTROS;
  }
  return cupom.valorFixo !== null && cupom.valorFixo !== void 0 && cupom.valorFixo > 0n;
}
var ERRO_DE_DESCONTO = {
  error: "cupom percentual exige o percentual; cupom de valor exige o valor",
  path: ["tipo"]
};
var criarCupomSchema = z.object({ codigo: codigoDeCupomSchema, ...camposDoCupom }).refine(vigenciaValida, ERRO_DE_VIGENCIA).refine(descontoDoCupomCoerente, ERRO_DE_DESCONTO);
var atualizarCupomSchema = z.object(camposDoCupom).partial().refine(vigenciaValida, ERRO_DE_VIGENCIA).refine(descontoDoCupomCoerente, ERRO_DE_DESCONTO);
var filtroDeCupomSchema = paginacaoSchema.extend({
  tipo: tipoDeDescontoSchema.optional(),
  ativo: z.stringbool().optional(),
  vigenteEm: dataSimplesSchema2.optional(),
  /** Só os cupons que ainda têm uso disponível. */
  apenasDisponiveis: z.stringbool().optional()
});

// src/disponibilidade.ts
var disponibilidade_exports = {};
__export(disponibilidade_exports, {
  DURACAO_MAXIMA_DE_BLOQUEIO_EM_DIAS: () => DURACAO_MAXIMA_DE_BLOQUEIO_EM_DIAS,
  DURACAO_MAXIMA_EM_DIAS: () => DURACAO_MAXIMA_EM_DIAS,
  JANELA_MAXIMA_DO_CALENDARIO_EM_DIAS: () => JANELA_MAXIMA_DO_CALENDARIO_EM_DIAS,
  MOTIVOS_CONTROLADOS_PELA_RESERVA: () => MOTIVOS_CONTROLADOS_PELA_RESERVA,
  MOTIVOS_DE_BLOQUEIO: () => MOTIVOS_DE_BLOQUEIO,
  MOTIVOS_OPERACIONAIS: () => MOTIVOS_OPERACIONAIS,
  ROTULO_DO_MOTIVO_DE_BLOQUEIO: () => ROTULO_DO_MOTIVO_DE_BLOQUEIO,
  ROTULO_DO_STATUS_DE_COTACAO: () => ROTULO_DO_STATUS_DE_COTACAO,
  ROTULO_DO_STATUS_DE_PRESTADOR: () => ROTULO_DO_STATUS_DE_PRESTADOR,
  ROTULO_DO_STATUS_DE_WAITLIST: () => ROTULO_DO_STATUS_DE_WAITLIST,
  ROTULO_DO_TIPO_DE_PRESTADOR: () => ROTULO_DO_TIPO_DE_PRESTADOR,
  STATUS_DE_COTACAO: () => STATUS_DE_COTACAO,
  STATUS_DE_PRESTADOR: () => STATUS_DE_PRESTADOR,
  STATUS_DE_WAITLIST: () => STATUS_DE_WAITLIST,
  TIPOS_DE_PRESTADOR: () => TIPOS_DE_PRESTADOR,
  ajusteDoDiaLidoSchema: () => ajusteDoDiaLidoSchema,
  atualizarBloqueioDeVeiculoSchema: () => atualizarBloqueioDeVeiculoSchema,
  atualizarItemDeWaitlistSchema: () => atualizarItemDeWaitlistSchema,
  atualizarPrestadorSchema: () => atualizarPrestadorSchema,
  calendarioDeDisponibilidadeSchema: () => calendarioDeDisponibilidadeSchema,
  consultarDisponibilidadeSchema: () => consultarDisponibilidadeSchema,
  cotacaoEmitidaSchema: () => cotacaoEmitidaSchema,
  cotacaoLidaSchema: () => cotacaoLidaSchema,
  criarBloqueioDePrestadorSchema: () => criarBloqueioDePrestadorSchema,
  criarBloqueioDeVeiculoSchema: () => criarBloqueioDeVeiculoSchema,
  criarItemDeWaitlistSchema: () => criarItemDeWaitlistSchema,
  criarPrestadorSchema: () => criarPrestadorSchema,
  devolucaoDepoisDaRetirada: () => devolucaoDepoisDaRetirada,
  diaDoCalendarioSchema: () => diaDoCalendarioSchema,
  diariaDoDiaLidaSchema: () => diariaDoDiaLidaSchema,
  dinheiroNoFioSchema: () => dinheiroNoFioSchema,
  disponibilidadeDaCategoriaSchema: () => disponibilidadeDaCategoriaSchema,
  duracaoEmDias: () => duracaoEmDias,
  filtroDeBloqueioSchema: () => filtroDeBloqueioSchema,
  filtroDeCotacaoSchema: () => filtroDeCotacaoSchema,
  filtroDeOverbookingSchema: () => filtroDeOverbookingSchema,
  filtroDePrestadorSchema: () => filtroDePrestadorSchema,
  filtroDeWaitlistSchema: () => filtroDeWaitlistSchema,
  fimDepoisDoInicio: () => fimDepoisDoInicio,
  hashDasRegrasSchema: () => hashDasRegrasSchema,
  instanteSchema: () => instanteSchema,
  linhaDaCotacaoLidaSchema: () => linhaDaCotacaoLidaSchema,
  motivoDeBloqueioSchema: () => motivoDeBloqueioSchema,
  pedirCotacaoSchema: () => pedirCotacaoSchema,
  periodoDentroDoTeto: () => periodoDentroDoTeto,
  periodoValidoSchema: () => periodoValidoSchema,
  registrarOverbookingSchema: () => registrarOverbookingSchema,
  statusDaCotacaoSchema: () => statusDaCotacaoSchema,
  statusDaWaitlistSchema: () => statusDaWaitlistSchema,
  statusDoPrestadorSchema: () => statusDoPrestadorSchema,
  tipoDePrestadorSchema: () => tipoDePrestadorSchema,
  waitlistTemContato: () => waitlistTemContato
});
var MILISSEGUNDOS_POR_DIA2 = 864e5;
var DURACAO_MAXIMA_EM_DIAS = 365;
var DURACAO_MAXIMA_DE_BLOQUEIO_EM_DIAS = 365;
var JANELA_MAXIMA_DO_CALENDARIO_EM_DIAS = 180;
var instanteSchema = z.iso.datetime({
  offset: true,
  error: "data e hora precisam vir em ISO 8601 com fuso expl\xEDcito, ex.: 2026-03-15T12:00:00Z"
}).transform((valor) => parseUtcIso(valor));
function duracaoEmDias(inicio, fim) {
  return (fim.getTime() - inicio.getTime()) / MILISSEGUNDOS_POR_DIA2;
}
function fimDepoisDoInicio(inicio, fim) {
  return fim.getTime() > inicio.getTime();
}
var camposDoPeriodo = {
  retirada: instanteSchema,
  devolucao: instanteSchema
};
function periodoJaConvertido(periodo) {
  return periodo.retirada instanceof Date && periodo.devolucao instanceof Date;
}
function devolucaoDepoisDaRetirada(periodo) {
  if (!periodoJaConvertido(periodo)) {
    return true;
  }
  return fimDepoisDoInicio(periodo.retirada, periodo.devolucao);
}
function periodoDentroDoTeto(periodo) {
  if (!periodoJaConvertido(periodo)) {
    return true;
  }
  return duracaoEmDias(periodo.retirada, periodo.devolucao) <= DURACAO_MAXIMA_EM_DIAS;
}
var ERRO_DE_ORDEM_DO_PERIODO = {
  error: "a devolu\xE7\xE3o precisa ser depois da retirada",
  path: ["devolucao"]
};
var ERRO_DE_TETO_DO_PERIODO = {
  error: `a loca\xE7\xE3o vai at\xE9 ${String(DURACAO_MAXIMA_EM_DIAS)} dias \u2014 confira o ano da devolu\xE7\xE3o`,
  path: ["devolucao"]
};
var periodoValidoSchema = z.object(camposDoPeriodo).refine(devolucaoDepoisDaRetirada, ERRO_DE_ORDEM_DO_PERIODO).refine(periodoDentroDoTeto, ERRO_DE_TETO_DO_PERIODO);
var MOTIVOS_DE_BLOQUEIO = [
  "MANUTENCAO",
  "LIMPEZA",
  "VISTORIA",
  "SINISTRO",
  "ALOCACAO",
  "RESERVA",
  "INDISPONIBILIDADE",
  "OUTRO"
];
var motivoDeBloqueioSchema = z.enum(MOTIVOS_DE_BLOQUEIO, {
  error: "motivo de bloqueio inv\xE1lido"
});
var ROTULO_DO_MOTIVO_DE_BLOQUEIO = {
  MANUTENCAO: "Manuten\xE7\xE3o",
  LIMPEZA: "Limpeza",
  VISTORIA: "Vistoria",
  SINISTRO: "Sinistro",
  ALOCACAO: "Aloca\xE7\xE3o",
  RESERVA: "Reserva",
  INDISPONIBILIDADE: "Indisponibilidade",
  OUTRO: "Outro"
};
var MOTIVOS_CONTROLADOS_PELA_RESERVA = ["ALOCACAO", "RESERVA"];
var MOTIVOS_OPERACIONAIS = MOTIVOS_DE_BLOQUEIO.filter(
  (motivo) => !MOTIVOS_CONTROLADOS_PELA_RESERVA.includes(motivo)
);
var TIPOS_DE_PRESTADOR = ["MOTORISTA", "SEGURANCA"];
var tipoDePrestadorSchema = z.enum(TIPOS_DE_PRESTADOR, {
  error: "tipo de prestador inv\xE1lido"
});
var ROTULO_DO_TIPO_DE_PRESTADOR = {
  MOTORISTA: "Motorista",
  SEGURANCA: "Seguran\xE7a"
};
var STATUS_DE_PRESTADOR = ["ATIVO", "INATIVO"];
var statusDoPrestadorSchema = z.enum(STATUS_DE_PRESTADOR, {
  error: "status de prestador inv\xE1lido"
});
var ROTULO_DO_STATUS_DE_PRESTADOR = {
  ATIVO: "Ativo",
  INATIVO: "Inativo"
};
var STATUS_DE_COTACAO = ["ABERTA", "EXPIRADA", "CONVERTIDA"];
var statusDaCotacaoSchema = z.enum(STATUS_DE_COTACAO, {
  error: "status de cota\xE7\xE3o inv\xE1lido"
});
var ROTULO_DO_STATUS_DE_COTACAO = {
  ABERTA: "Aberta",
  EXPIRADA: "Expirada",
  CONVERTIDA: "Convertida em reserva"
};
var STATUS_DE_WAITLIST = [
  "AGUARDANDO",
  "NOTIFICADO",
  "CONVERTIDO",
  "DESISTIU",
  "EXPIRADO"
];
var statusDaWaitlistSchema = z.enum(STATUS_DE_WAITLIST, {
  error: "status da lista de espera inv\xE1lido"
});
var ROTULO_DO_STATUS_DE_WAITLIST = {
  AGUARDANDO: "Aguardando",
  NOTIFICADO: "Notificado",
  CONVERTIDO: "Convertido em reserva",
  DESISTIU: "Desistiu",
  EXPIRADO: "Expirado"
};
function intervaloJaConvertido(intervalo) {
  return intervalo.inicio instanceof Date && intervalo.fim instanceof Date;
}
function intervaloOrdenado(intervalo) {
  if (!intervaloJaConvertido(intervalo)) {
    return true;
  }
  return fimDepoisDoInicio(intervalo.inicio, intervalo.fim);
}
function intervaloDentroDoTeto(intervalo) {
  if (!intervaloJaConvertido(intervalo)) {
    return true;
  }
  return duracaoEmDias(intervalo.inicio, intervalo.fim) <= DURACAO_MAXIMA_DE_BLOQUEIO_EM_DIAS;
}
function intervaloParcialValido(intervalo, predicado) {
  if (intervalo.inicio === void 0 || intervalo.fim === void 0) {
    return true;
  }
  return predicado({ inicio: intervalo.inicio, fim: intervalo.fim });
}
var ERRO_DE_ORDEM_DO_INTERVALO = {
  error: "o fim do bloqueio precisa ser depois do in\xEDcio",
  path: ["fim"]
};
var ERRO_DE_TETO_DO_INTERVALO = {
  error: `o bloqueio vai at\xE9 ${String(DURACAO_MAXIMA_DE_BLOQUEIO_EM_DIAS)} dias \u2014 para mais que isso, d\xEA baixa no ve\xEDculo`,
  path: ["fim"]
};
var camposDoBloqueio = {
  /**
   * `MANUTENCAO`, `LIMPEZA`, `VISTORIA`… mas nunca `ALOCACAO` nem `RESERVA`:
   * esses nascem da reserva, e criá-los à mão deixaria o carro ocupado sem
   * nenhuma reserva que explique por quê.
   */
  motivo: motivoDeBloqueioSchema.refine(
    (motivo) => !MOTIVOS_CONTROLADOS_PELA_RESERVA.includes(motivo),
    { error: "este motivo \xE9 definido pela reserva, n\xE3o pela opera\xE7\xE3o" }
  ),
  inicio: instanteSchema,
  fim: instanteSchema,
  observacoes: z.string().trim().max(1e3, { error: "observa\xE7\xE3o muito longa" }).nullish()
};
var criarBloqueioDeVeiculoSchema = z.object({ vehicleId: uuidSchema, ...camposDoBloqueio }).refine(intervaloOrdenado, ERRO_DE_ORDEM_DO_INTERVALO).refine(intervaloDentroDoTeto, ERRO_DE_TETO_DO_INTERVALO);
var atualizarBloqueioDeVeiculoSchema = z.object(camposDoBloqueio).partial().refine((bloqueio) => intervaloParcialValido(bloqueio, intervaloOrdenado), {
  ...ERRO_DE_ORDEM_DO_INTERVALO
}).refine((bloqueio) => intervaloParcialValido(bloqueio, intervaloDentroDoTeto), {
  ...ERRO_DE_TETO_DO_INTERVALO
});
var criarBloqueioDePrestadorSchema = z.object({ serviceProviderId: uuidSchema, ...camposDoBloqueio }).refine(intervaloOrdenado, ERRO_DE_ORDEM_DO_INTERVALO).refine(intervaloDentroDoTeto, ERRO_DE_TETO_DO_INTERVALO);
var filtroDeBloqueioSchema = paginacaoSchema.extend({
  vehicleId: uuidSchema.optional(),
  serviceProviderId: uuidSchema.optional(),
  categoryId: uuidSchema.optional(),
  motivo: motivoDeBloqueioSchema.optional(),
  /** Recorte de período: tudo que COLIDE com esta janela. */
  de: instanteSchema.optional(),
  ate: instanteSchema.optional()
});
var APENAS_DIGITOS3 = /[^0-9]/g;
var CPF_PATTERN = /^[0-9]{11}$/;
var camposDoPrestador = {
  nome: z.string({ error: "nome \xE9 obrigat\xF3rio" }).trim().min(2, { error: "nome precisa ter ao menos 2 caracteres" }).max(120, { error: "nome muito longo" }),
  tipo: tipoDePrestadorSchema,
  telefone: telefoneSchema.nullish(),
  email: emailSchema.nullish(),
  /**
   * Só dígitos, como o CHECK `service_providers_cpf_check` exige. Não passa por
   * `cpfSchema`: aqui o documento é opcional e serve de identificação interna
   * na escala, não de titularidade de contrato.
   */
  cpf: z.string().transform((valor) => valor.replace(APENAS_DIGITOS3, "")).refine((valor) => CPF_PATTERN.test(valor), { error: "CPF inv\xE1lido \u2014 s\xE3o 11 d\xEDgitos" }).nullish(),
  /**
   * O que a OPERAÇÃO paga por diária. Não é o preço ao cliente, que vem do
   * `Extra` — separar os dois é o que torna a margem do serviço visível na
   * Fase 7.
   */
  custoDiario: moneySchemaNonNegative,
  /** Base onde o prestador atende. `null` = atende de qualquer base. */
  locationId: uuidSchema.nullish(),
  status: statusDoPrestadorSchema.default("ATIVO"),
  observacoes: z.string().trim().max(1e3).nullish()
};
var criarPrestadorSchema = z.object(camposDoPrestador);
var atualizarPrestadorSchema = z.object(camposDoPrestador).partial();
var filtroDePrestadorSchema = paginacaoSchema.extend({
  tipo: tipoDePrestadorSchema.optional(),
  status: statusDoPrestadorSchema.optional(),
  locationId: uuidSchema.optional(),
  /** Só quem está livre no período inteiro — é a pergunta que a cotação faz. */
  livreDe: instanteSchema.optional(),
  livreAte: instanteSchema.optional()
});
function waitlistTemContato(item) {
  return item.accountId !== null && item.accountId !== void 0 || item.email !== null && item.email !== void 0 || item.telefone !== null && item.telefone !== void 0;
}
var criarItemDeWaitlistSchema = z.object({
  categoryId: uuidSchema,
  /** Preenchido pela API quando quem pede está autenticado. */
  accountId: uuidSchema.nullish(),
  ...camposDoPeriodo,
  nome: z.string().trim().min(2).max(120).nullish(),
  email: emailSchema.nullish(),
  telefone: telefoneSchema.nullish(),
  observacoes: z.string().trim().max(1e3).nullish()
}).refine(devolucaoDepoisDaRetirada, ERRO_DE_ORDEM_DO_PERIODO).refine(periodoDentroDoTeto, ERRO_DE_TETO_DO_PERIODO).refine(waitlistTemContato, {
  error: "informe ao menos um contato: conta, e-mail ou telefone",
  path: ["email"]
});
var atualizarItemDeWaitlistSchema = z.object({
  status: statusDaWaitlistSchema.optional(),
  observacoes: z.string().trim().max(1e3).nullish()
}).refine((dados) => Object.keys(dados).length > 0, {
  error: "informe pelo menos um campo para alterar"
});
var filtroDeWaitlistSchema = paginacaoSchema.extend({
  categoryId: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  status: statusDaWaitlistSchema.optional(),
  /** Recorte por data de retirada pretendida. */
  de: instanteSchema.optional(),
  ate: instanteSchema.optional()
});
var registrarOverbookingSchema = z.object({
  categoriaVendidaId: uuidSchema,
  /** `null` enquanto a entrega não foi decidida. */
  categoriaEntregueId: uuidSchema.nullish(),
  ...camposDoPeriodo,
  custoDoUpgrade: moneySchema,
  /**
   * ⚠️ O VEÍCULO QUE COBRIU O UPGRADE (Gap 11, completado na Fase 7).
   *
   * O custo já era registrado; o que faltava era dizer QUAL CARRO o absorveu.
   * Sem ele, a margem por veículo mostra o carro rendendo menos que os
   * vizinhos e não explica por quê — a receita atribuída a ele é a da
   * categoria VENDIDA, mais barata que a dele.
   *
   * `null` quando o registro é feito antes de o carro ser escolhido, que é
   * como o balcão costuma trabalhar: primeiro reconhece o overbooking, depois
   * decide o que entregar.
   */
  vehicleId: uuidSchema.nullish(),
  reservationId: uuidSchema.nullish(),
  observacoes: z.string().trim().max(1e3).nullish()
}).refine(devolucaoDepoisDaRetirada, ERRO_DE_ORDEM_DO_PERIODO).refine(periodoDentroDoTeto, ERRO_DE_TETO_DO_PERIODO);
var filtroDeOverbookingSchema = paginacaoSchema.extend({
  categoriaVendidaId: uuidSchema.optional(),
  de: instanteSchema.optional(),
  ate: instanteSchema.optional()
});
var consultarDisponibilidadeSchema = z.object({
  categoryId: uuidSchema.optional(),
  retiradaLocalId: uuidSchema.optional(),
  devolucaoLocalId: uuidSchema.optional(),
  ...camposDoPeriodo
}).refine(devolucaoDepoisDaRetirada, ERRO_DE_ORDEM_DO_PERIODO).refine(periodoDentroDoTeto, ERRO_DE_TETO_DO_PERIODO);
var calendarioDeDisponibilidadeSchema = z.object({
  categoryId: uuidSchema,
  locationId: uuidSchema.optional(),
  de: dataSimplesSchema2,
  ate: dataSimplesSchema2
}).refine((janela) => janela.ate >= janela.de, {
  error: "a data final precisa ser igual ou posterior \xE0 inicial",
  path: ["ate"]
}).refine(
  (janela) => duracaoEmDias(/* @__PURE__ */ new Date(`${janela.de}T00:00:00Z`), /* @__PURE__ */ new Date(`${janela.ate}T00:00:00Z`)) <= JANELA_MAXIMA_DO_CALENDARIO_EM_DIAS,
  {
    error: `o calend\xE1rio mostra at\xE9 ${String(JANELA_MAXIMA_DO_CALENDARIO_EM_DIAS)} dias por vez`,
    path: ["ate"]
  }
);
var pedirCotacaoSchema = z.object({
  categoryId: uuidSchema,
  ...camposDoPeriodo,
  retiradaLocalId: uuidSchema.nullish(),
  devolucaoLocalId: uuidSchema.nullish(),
  /**
   * A conta para quem se cota. Ausente, a API usa a do autenticado — o painel
   * a informa para cotar em nome de um cliente corporativo, que é o que puxa
   * a tabela negociada.
   */
  accountId: uuidSchema.nullish(),
  extras: z.array(extraEscolhidoSchema).max(20, { error: "h\xE1 extras demais nesta cota\xE7\xE3o" }).default([]),
  cupom: codigoDeCupomSchema.nullish()
}).refine(devolucaoDepoisDaRetirada, ERRO_DE_ORDEM_DO_PERIODO).refine(periodoDentroDoTeto, ERRO_DE_TETO_DO_PERIODO).refine(
  (pedido) => new Set(pedido.extras.map((extra) => extra.extraId)).size === pedido.extras.length,
  {
    error: "o mesmo extra aparece duas vezes \u2014 some a quantidade em vez de repetir",
    path: ["extras"]
  }
);
var filtroDeCotacaoSchema = paginacaoSchema.extend({
  categoryId: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  status: statusDaCotacaoSchema.optional(),
  de: instanteSchema.optional(),
  ate: instanteSchema.optional()
});
var dinheiroNoFioSchema = z.string({ error: "valor monet\xE1rio \xE9 obrigat\xF3rio" }).regex(/^-?\d+$/, { error: 'valor monet\xE1rio inv\xE1lido \u2014 esperava centavos, ex.: "123456"' });
var ajusteDoDiaLidoSchema = z.object({
  origem: origemDoAjusteSchema,
  regraId: z.string(),
  nome: z.string(),
  efeito: efeitoDeRegraSchema,
  multiplicadorEmPontosBase: z.number().int(),
  delta: dinheiroNoFioSchema
});
var diariaDoDiaLidaSchema = z.object({
  /** `AAAA-MM-DD` no fuso do ponto de retirada. */
  data: z.iso.date(),
  base: dinheiroNoFioSchema,
  ajustes: z.array(ajusteDoDiaLidoSchema),
  /** Depois dos ajustes, do teto e do piso. É este que entra na soma. */
  valor: dinheiroNoFioSchema,
  limitado: z.boolean(),
  deltaDoLimite: dinheiroNoFioSchema
});
var linhaDaCotacaoLidaSchema = z.object({
  tipo: tipoDeLinhaSchema,
  descricao: z.string(),
  regraId: z.string().nullable(),
  quantidade: z.number().int(),
  valor: dinheiroNoFioSchema
});
var cotacaoLidaSchema = z.object({
  diarias: z.number().int().positive(),
  faixa: chaveDaFaixaSchema,
  diariaBase: dinheiroNoFioSchema,
  porDia: z.array(diariaDoDiaLidaSchema),
  linhas: z.array(linhaDaCotacaoLidaSchema),
  subtotalDasDiarias: dinheiroNoFioSchema,
  totalDeExtras: dinheiroNoFioSchema,
  desconto: dinheiroNoFioSchema,
  total: dinheiroNoFioSchema,
  /** O número que o cliente compara entre categorias. */
  diariaMedia: dinheiroNoFioSchema
});
var hashDasRegrasSchema = z.string().regex(/^[0-9a-f]{64}$/, { error: "hash de regras inv\xE1lido" });
var cotacaoEmitidaSchema = cotacaoLidaSchema.extend({
  id: z.uuid(),
  categoryId: z.uuid(),
  accountId: z.uuid().nullable(),
  retiradaLocalId: z.uuid().nullable(),
  devolucaoLocalId: z.uuid().nullable(),
  /** ISO 8601 em UTC, como toda data de leitura da API. */
  retirada: z.string(),
  devolucao: z.string(),
  caucao: dinheiroNoFioSchema,
  hashDasRegras: hashDasRegrasSchema,
  /** A tolerância que valia no momento — a Fase 4 fatura com esta, não com a de hoje. */
  toleranciaEmMinutos: z.number().int().min(0),
  status: statusDaCotacaoSchema,
  expiraEm: z.string(),
  criadoEm: z.string()
});
var disponibilidadeDaCategoriaSchema = z.object({
  categoryId: z.uuid(),
  nome: z.string(),
  capacidade: z.number().int().min(0),
  disponiveis: z.number().int().min(0),
  /** Quantos a mais a política de overbooking permite vender. Zero = desligado. */
  overbookingDisponivel: z.number().int().min(0),
  cotacao: cotacaoLidaSchema.nullable()
});
var diaDoCalendarioSchema = z.object({
  data: z.iso.date(),
  capacidade: z.number().int().min(0),
  disponiveis: z.number().int().min(0),
  /** A diária daquele dia, já com sazonalidade e dia da semana. */
  diaria: dinheiroNoFioSchema.nullable()
});

// src/reserva.ts
var reserva_exports = {};
__export(reserva_exports, {
  ATORES_DE_TRANSICAO: () => ATORES_DE_TRANSICAO,
  ROTULO_DO_STATUS_DE_RESERVA: () => ROTULO_DO_STATUS_DE_RESERVA,
  STATUS_DE_RESERVA: () => STATUS_DE_RESERVA,
  STATUS_QUE_OCUPAM_FROTA: () => STATUS_QUE_OCUPAM_FROTA,
  STATUS_TERMINAIS: () => STATUS_TERMINAIS,
  TRANSICOES: () => TRANSICOES,
  ehTerminal: () => ehTerminal,
  encontrarTransicao: () => encontrarTransicao,
  estadosInalcancaveis: () => estadosInalcancaveis,
  estadosSemSaida: () => estadosSemSaida,
  exigeMotivo: () => exigeMotivo,
  ocupaFrota: () => ocupaFrota,
  podeTransicionar: () => podeTransicionar,
  transicoesPossiveis: () => transicoesPossiveis
});
var STATUS_DE_RESERVA = [
  "RASCUNHO",
  "AGUARDANDO_PAGAMENTO",
  "AGUARDANDO_APROVACAO",
  "APROVADA",
  "VEICULO_ALOCADO",
  "RETIRADA_PENDENTE",
  "EM_ANDAMENTO",
  "DEVOLUCAO_PENDENTE",
  "FINALIZADA",
  // Ramos terminais.
  "RECUSADA",
  "CANCELADA_CLIENTE",
  "CANCELADA_EMPRESA",
  "NO_SHOW",
  "EXPIRADA"
];
var STATUS_TERMINAIS = [
  "FINALIZADA",
  "RECUSADA",
  "CANCELADA_CLIENTE",
  "CANCELADA_EMPRESA",
  "NO_SHOW",
  "EXPIRADA"
];
var STATUS_QUE_OCUPAM_FROTA = [
  "AGUARDANDO_PAGAMENTO",
  "AGUARDANDO_APROVACAO",
  "APROVADA",
  "VEICULO_ALOCADO",
  "RETIRADA_PENDENTE",
  "EM_ANDAMENTO",
  "DEVOLUCAO_PENDENTE"
];
var ROTULO_DO_STATUS_DE_RESERVA = {
  RASCUNHO: { rotulo: "Rascunho", intencao: "neutra" },
  AGUARDANDO_PAGAMENTO: { rotulo: "Aguardando pagamento", intencao: "atencao" },
  AGUARDANDO_APROVACAO: { rotulo: "Aguardando aprova\xE7\xE3o", intencao: "atencao" },
  APROVADA: { rotulo: "Aprovada", intencao: "positiva" },
  VEICULO_ALOCADO: { rotulo: "Ve\xEDculo alocado", intencao: "positiva" },
  RETIRADA_PENDENTE: { rotulo: "Retirada pendente", intencao: "informativa" },
  EM_ANDAMENTO: { rotulo: "Em andamento", intencao: "informativa" },
  DEVOLUCAO_PENDENTE: { rotulo: "Devolu\xE7\xE3o pendente", intencao: "atencao" },
  FINALIZADA: { rotulo: "Finalizada", intencao: "positiva" },
  RECUSADA: { rotulo: "Recusada", intencao: "critica" },
  CANCELADA_CLIENTE: { rotulo: "Cancelada pelo cliente", intencao: "neutra" },
  CANCELADA_EMPRESA: { rotulo: "Cancelada pela empresa", intencao: "critica" },
  NO_SHOW: { rotulo: "N\xE3o compareceu", intencao: "critica" },
  EXPIRADA: { rotulo: "Expirada", intencao: "neutra" }
};
var ATORES_DE_TRANSICAO = ["CLIENTE", "EMPRESA", "SISTEMA"];
var TRANSICOES = [
  {
    de: "RASCUNHO",
    para: "AGUARDANDO_PAGAMENTO",
    atores: ["CLIENTE", "EMPRESA"],
    acao: "ENVIAR_PARA_PAGAMENTO",
    descricao: "A cota\xE7\xE3o virou pedido e espera o pagamento ou a pr\xE9-autoriza\xE7\xE3o.",
    exigeMotivo: false
  },
  {
    de: "RASCUNHO",
    para: "EXPIRADA",
    atores: ["SISTEMA"],
    acao: "EXPIRAR_RASCUNHO",
    descricao: "O rascunho passou da validade da cota\xE7\xE3o e o pre\xE7o n\xE3o vale mais.",
    exigeMotivo: false
  },
  {
    de: "RASCUNHO",
    para: "CANCELADA_CLIENTE",
    atores: ["CLIENTE"],
    acao: "DESISTIR",
    descricao: "O cliente desistiu antes de pagar. Nada a estornar.",
    exigeMotivo: false
  },
  {
    de: "AGUARDANDO_PAGAMENTO",
    para: "AGUARDANDO_APROVACAO",
    // `EMPRESA` também: até a Fase 5 ligar o gateway, o pagamento real chega
    // por TED e alguém do financeiro o confirma à mão — e essa confirmação
    // continuará legítima depois, para o pagamento que o gateway não enxerga.
    atores: ["SISTEMA", "EMPRESA"],
    acao: "PAGAMENTO_CONFIRMADO",
    descricao: "Pagamento aprovado ou cau\xE7\xE3o pr\xE9-autorizada. Vai para a fila do gestor.",
    exigeMotivo: false
  },
  {
    de: "AGUARDANDO_PAGAMENTO",
    para: "EXPIRADA",
    atores: ["SISTEMA"],
    acao: "EXPIRAR_PAGAMENTO",
    descricao: "O prazo de pagamento venceu sem confirma\xE7\xE3o.",
    exigeMotivo: false
  },
  {
    de: "AGUARDANDO_PAGAMENTO",
    para: "CANCELADA_CLIENTE",
    atores: ["CLIENTE"],
    acao: "CANCELAR_ANTES_DO_PAGAMENTO",
    descricao: "Cancelamento do cliente antes da confirma\xE7\xE3o do pagamento.",
    exigeMotivo: false
  },
  {
    de: "AGUARDANDO_APROVACAO",
    para: "APROVADA",
    atores: ["EMPRESA"],
    acao: "APROVAR",
    descricao: "O gestor aprovou. Blindado tem perfil de risco pr\xF3prio, e \xE9 por isso que esta etapa \xE9 obrigat\xF3ria.",
    exigeMotivo: false
  },
  {
    de: "AGUARDANDO_APROVACAO",
    para: "RECUSADA",
    atores: ["EMPRESA"],
    acao: "RECUSAR",
    descricao: "O gestor recusou. O motivo \xE9 obrigat\xF3rio e vai para a trilha.",
    exigeMotivo: true
  },
  {
    de: "AGUARDANDO_APROVACAO",
    para: "CANCELADA_CLIENTE",
    atores: ["CLIENTE"],
    acao: "CANCELAR_NA_ANALISE",
    descricao: "O cliente cancelou enquanto a reserva estava em an\xE1lise.",
    exigeMotivo: false
  },
  {
    de: "APROVADA",
    para: "VEICULO_ALOCADO",
    atores: ["EMPRESA", "SISTEMA"],
    acao: "ALOCAR_VEICULO",
    descricao: "Um ve\xEDculo espec\xEDfico foi atribu\xEDdo \xE0 reserva pelo algoritmo ou \xE0 m\xE3o.",
    exigeMotivo: false
  },
  {
    de: "APROVADA",
    para: "CANCELADA_EMPRESA",
    atores: ["EMPRESA"],
    acao: "CANCELAR_APROVADA",
    descricao: "A empresa cancelou depois de aprovar. Mexe em dinheiro: exige motivo.",
    exigeMotivo: true
  },
  {
    de: "APROVADA",
    para: "CANCELADA_CLIENTE",
    atores: ["CLIENTE"],
    acao: "CANCELAR_APROVADA_PELO_CLIENTE",
    descricao: "Cancelamento do cliente com a reserva j\xE1 aprovada.",
    exigeMotivo: false
  },
  {
    de: "VEICULO_ALOCADO",
    para: "RETIRADA_PENDENTE",
    atores: ["EMPRESA", "SISTEMA"],
    acao: "LIBERAR_PARA_RETIRADA",
    descricao: "Contrato assinado e documentos conferidos. O carro pode sair.",
    exigeMotivo: false
  },
  {
    de: "VEICULO_ALOCADO",
    para: "APROVADA",
    atores: ["EMPRESA"],
    acao: "DESFAZER_ALOCACAO",
    descricao: "Desfaz a atribui\xE7\xE3o do ve\xEDculo sem perder a reserva \u2014 o carro quebrou, ou entrou algo mais urgente.",
    exigeMotivo: true
  },
  {
    /**
     * O cliente desiste DEPOIS de o carro já estar separado.
     *
     * Faltava, e a falta era um beco: a máquina dá saída ao cliente em
     * RASCUNHO, AGUARDANDO_PAGAMENTO, AGUARDANDO_APROVACAO, APROVADA e
     * RETIRADA_PENDENTE — mas não em VEICULO_ALOCADO, que é justamente o
     * estado em que a reserva passa mais tempo antes da retirada. Quem
     * caísse ali só sairia ligando para o atendimento, e a tela de exclusão de
     * conta continuaria dizendo "conclua ou cancele antes de excluir" sobre
     * uma reserva que ele não conseguia cancelar.
     *
     * Destino CANCELADA_CLIENTE, e não CANCELADA_EMPRESA: a consequência
     * financeira não é a mesma, e é essa distinção que o par de estados existe
     * para preservar.
     */
    de: "VEICULO_ALOCADO",
    para: "CANCELADA_CLIENTE",
    atores: ["CLIENTE"],
    acao: "CANCELAR_ALOCADA_PELO_CLIENTE",
    descricao: "O cliente desiste com o ve\xEDculo j\xE1 separado. O carro volta para a frota na mesma transi\xE7\xE3o.",
    exigeMotivo: true
  },
  {
    de: "VEICULO_ALOCADO",
    para: "CANCELADA_EMPRESA",
    atores: ["EMPRESA"],
    acao: "CANCELAR_ALOCADA",
    descricao: "Cancelamento pela empresa com ve\xEDculo j\xE1 atribu\xEDdo.",
    exigeMotivo: true
  },
  {
    de: "RETIRADA_PENDENTE",
    para: "APROVADA",
    atores: ["EMPRESA"],
    acao: "DESFAZER_ALOCACAO",
    descricao: "O carro quebrou ENTRE o contrato assinado e a retirada. A reserva volta para a aloca\xE7\xE3o; o contrato (que cita a placa) \xE9 cancelado por distrato e um novo \xE9 gerado com o carro seguinte.",
    exigeMotivo: true
  },
  {
    de: "RETIRADA_PENDENTE",
    para: "EM_ANDAMENTO",
    atores: ["EMPRESA"],
    acao: "REGISTRAR_RETIRADA",
    descricao: "A vistoria de sa\xEDda foi conclu\xEDda e o cliente levou o carro.",
    exigeMotivo: false
  },
  {
    de: "RETIRADA_PENDENTE",
    para: "NO_SHOW",
    atores: ["EMPRESA", "SISTEMA"],
    acao: "MARCAR_NAO_COMPARECIMENTO",
    descricao: "O cliente n\xE3o apareceu para retirar dentro do prazo combinado.",
    exigeMotivo: true
  },
  {
    de: "RETIRADA_PENDENTE",
    para: "CANCELADA_CLIENTE",
    atores: ["CLIENTE"],
    acao: "CANCELAR_ANTES_DA_RETIRADA",
    descricao: "Cancelamento do cliente antes de retirar o ve\xEDculo.",
    exigeMotivo: false
  },
  {
    de: "EM_ANDAMENTO",
    para: "DEVOLUCAO_PENDENTE",
    atores: ["EMPRESA", "SISTEMA"],
    acao: "INICIAR_DEVOLUCAO",
    descricao: "O per\xEDodo acabou (ou o cliente chegou antes) e a devolu\xE7\xE3o est\xE1 em curso.",
    exigeMotivo: false
  },
  {
    // Auto-transição deliberada (Adendo C): a locação de 30/60/90 dias troca de
    // veículo NO MEIO mantendo a mesma reserva e o mesmo estado. O que muda é o
    // TRECHO (a alocação ativa), não a vida da reserva — e a troca entra na
    // trilha como qualquer transição, com motivo obrigatório.
    de: "EM_ANDAMENTO",
    para: "EM_ANDAMENTO",
    atores: ["EMPRESA"],
    acao: "TROCAR_VEICULO",
    descricao: "O ve\xEDculo foi trocado no meio da loca\xE7\xE3o (quebra, revis\xE3o, upgrade): o trecho antigo encerra com vistoria de retorno e o novo abre com vistoria de sa\xEDda.",
    exigeMotivo: true
  },
  {
    de: "DEVOLUCAO_PENDENTE",
    para: "FINALIZADA",
    atores: ["EMPRESA"],
    acao: "FINALIZAR",
    descricao: "Vistoria de retorno conclu\xEDda, fechamento calculado e cau\xE7\xE3o liberada ou retida.",
    exigeMotivo: false
  },
  {
    de: "DEVOLUCAO_PENDENTE",
    para: "EM_ANDAMENTO",
    atores: ["EMPRESA"],
    acao: "PRORROGAR",
    descricao: "O cliente prorrogou e o carro continua com ele. Volta ao andamento em vez de fechar e reabrir.",
    exigeMotivo: true
  }
];
function transicoesPossiveis(de, ator) {
  return TRANSICOES.filter(
    (transicao) => transicao.de === de && (ator === void 0 || transicao.atores.includes(ator))
  );
}
function encontrarTransicao(de, para, ator) {
  return TRANSICOES.find(
    (transicao) => transicao.de === de && transicao.para === para && transicao.atores.includes(ator)
  );
}
function podeTransicionar(de, para, ator) {
  return encontrarTransicao(de, para, ator) !== void 0;
}
function ehTerminal(status) {
  return STATUS_TERMINAIS.includes(status);
}
function ocupaFrota(status) {
  return STATUS_QUE_OCUPAM_FROTA.includes(status);
}
function exigeMotivo(de, para, ator) {
  return encontrarTransicao(de, para, ator)?.exigeMotivo ?? false;
}
function estadosSemSaida() {
  return STATUS_DE_RESERVA.filter(
    (status) => !ehTerminal(status) && transicoesPossiveis(status).length === 0
  );
}
function estadosInalcancaveis() {
  const alcancados = new Set(TRANSICOES.map((transicao) => transicao.para));
  return STATUS_DE_RESERVA.filter((status) => status !== "RASCUNHO" && !alcancados.has(status));
}

// src/alocacao.ts
var alocacao_exports = {};
__export(alocacao_exports, {
  ESCALA_DO_FATOR: () => ESCALA_DO_FATOR,
  EXPLICACAO_DO_BLOQUEIO: () => EXPLICACAO_DO_BLOQUEIO,
  FATORES_DE_ALOCACAO: () => FATORES_DE_ALOCACAO,
  MOTIVOS_DE_BLOQUEIO_DE_ALOCACAO: () => MOTIVOS_DE_BLOQUEIO_DE_ALOCACAO,
  PESOS_PADRAO: () => PESOS_PADRAO,
  ROTULO_DO_FATOR: () => ROTULO_DO_FATOR,
  bloqueiosDoCandidato: () => bloqueiosDoCandidato,
  escolherVeiculo: () => escolherVeiculo,
  normalizar: () => normalizar,
  pontuarCandidatos: () => pontuarCandidatos
});
var ESCALA_DO_FATOR = 1e3;
var MOTIVOS_DE_BLOQUEIO_DE_ALOCACAO = [
  "CATEGORIA_DIFERENTE",
  "VEICULO_BAIXADO",
  "STATUS_IMPEDE",
  "PERIODO_OCUPADO",
  "BLINDAGEM_VENCE_NO_PERIODO",
  "DOCUMENTO_VENCE_NO_PERIODO",
  "REVISAO_VENCE_NO_PERIODO"
];
var EXPLICACAO_DO_BLOQUEIO = {
  CATEGORIA_DIFERENTE: "O ve\xEDculo n\xE3o \xE9 da categoria contratada.",
  VEICULO_BAIXADO: "O ve\xEDculo saiu da frota.",
  STATUS_IMPEDE: "O status operacional n\xE3o permite aloca\xE7\xE3o.",
  PERIODO_OCUPADO: "O ve\xEDculo j\xE1 tem compromisso que se sobrep\xF5e ao per\xEDodo.",
  BLINDAGEM_VENCE_NO_PERIODO: "O certificado de blindagem vence antes do fim da loca\xE7\xE3o \u2014 bloqueio duro, n\xE3o alerta.",
  DOCUMENTO_VENCE_NO_PERIODO: "Licenciamento ou ap\xF3lice vencem durante a loca\xE7\xE3o.",
  REVISAO_VENCE_NO_PERIODO: "A revis\xE3o vence dentro do per\xEDodo e a quilometragem prevista n\xE3o cabe at\xE9 l\xE1."
};
function bloqueiosDoCandidato(candidato, pedido) {
  const bloqueios = [];
  if (candidato.categoryId !== pedido.categoryId) {
    bloqueios.push({
      motivo: "CATEGORIA_DIFERENTE",
      detalhe: EXPLICACAO_DO_BLOQUEIO.CATEGORIA_DIFERENTE
    });
  }
  if (candidato.baixado) {
    bloqueios.push({ motivo: "VEICULO_BAIXADO", detalhe: EXPLICACAO_DO_BLOQUEIO.VEICULO_BAIXADO });
  }
  if (!candidato.statusPermiteAlocar) {
    bloqueios.push({ motivo: "STATUS_IMPEDE", detalhe: EXPLICACAO_DO_BLOQUEIO.STATUS_IMPEDE });
  }
  if (candidato.periodoOcupado) {
    bloqueios.push({ motivo: "PERIODO_OCUPADO", detalhe: EXPLICACAO_DO_BLOQUEIO.PERIODO_OCUPADO });
  }
  if (candidato.blindagemValidaAte === null || candidato.blindagemValidaAte.getTime() < pedido.devolucao.getTime()) {
    bloqueios.push({
      motivo: "BLINDAGEM_VENCE_NO_PERIODO",
      detalhe: candidato.blindagemValidaAte === null ? "O ve\xEDculo n\xE3o tem certificado de blindagem cadastrado." : EXPLICACAO_DO_BLOQUEIO.BLINDAGEM_VENCE_NO_PERIODO
    });
  }
  const documentoVencido = [candidato.licenciamentoValidoAte, candidato.apoliceValidaAte].some(
    (validade) => validade !== null && validade.getTime() < pedido.devolucao.getTime()
  );
  if (documentoVencido) {
    bloqueios.push({
      motivo: "DOCUMENTO_VENCE_NO_PERIODO",
      detalhe: EXPLICACAO_DO_BLOQUEIO.DOCUMENTO_VENCE_NO_PERIODO
    });
  }
  const revisaoPorData = candidato.proximaRevisaoData !== null && candidato.proximaRevisaoData.getTime() < pedido.devolucao.getTime();
  const revisaoPorKm = candidato.proximaRevisaoKm !== null && candidato.hodometroAtual + pedido.kmPrevisto > candidato.proximaRevisaoKm;
  if (revisaoPorData || revisaoPorKm) {
    bloqueios.push({
      motivo: "REVISAO_VENCE_NO_PERIODO",
      detalhe: EXPLICACAO_DO_BLOQUEIO.REVISAO_VENCE_NO_PERIODO
    });
  }
  return bloqueios;
}
var FATORES_DE_ALOCACAO = [
  "PRIORIDADE_MANUAL",
  "RODIZIO_DE_USO",
  "FOLGA_ATE_A_REVISAO",
  "PROXIMIDADE_DA_BASE",
  "CUSTO_POR_KM",
  "FOLGA_DA_BLINDAGEM",
  "ENCAIXE_NA_MALHA"
];
var ROTULO_DO_FATOR = {
  PRIORIDADE_MANUAL: "Prioridade definida no cadastro",
  RODIZIO_DE_USO: "Rod\xEDzio de uso (equalizar km)",
  FOLGA_ATE_A_REVISAO: "Folga at\xE9 a pr\xF3xima revis\xE3o",
  PROXIMIDADE_DA_BASE: "Est\xE1 na base da retirada",
  CUSTO_POR_KM: "Custo acumulado por km",
  FOLGA_DA_BLINDAGEM: "Folga do certificado de blindagem",
  ENCAIXE_NA_MALHA: "Encaixe na malha de reservas"
};
var PESOS_PADRAO = {
  PRIORIDADE_MANUAL: 20,
  RODIZIO_DE_USO: 30,
  FOLGA_ATE_A_REVISAO: 15,
  PROXIMIDADE_DA_BASE: 15,
  CUSTO_POR_KM: 10,
  FOLGA_DA_BLINDAGEM: 5,
  ENCAIXE_NA_MALHA: 5
};
function limitar(valor) {
  if (!Number.isFinite(valor)) return 0;
  if (valor < 0) return 0;
  if (valor > ESCALA_DO_FATOR) return ESCALA_DO_FATOR;
  return Math.round(valor);
}
function normalizar(valor, minimo, maximo, menorEhMelhor) {
  if (maximo === minimo) {
    return ESCALA_DO_FATOR / 2;
  }
  const posicao = (valor - minimo) * ESCALA_DO_FATOR / (maximo - minimo);
  return limitar(menorEhMelhor ? ESCALA_DO_FATOR - posicao : posicao);
}
var MILISSEGUNDOS_POR_DIA3 = 864e5;
function diasEntre(de, ate) {
  return Math.floor((ate.getTime() - de.getTime()) / MILISSEGUNDOS_POR_DIA3);
}
function faixaDe(valores) {
  if (valores.length === 0) {
    return { min: 0, max: 0 };
  }
  return { min: Math.min(...valores), max: Math.max(...valores) };
}
function folgaDeRevisaoEmKm(candidato) {
  return candidato.proximaRevisaoKm === null ? Number.MAX_SAFE_INTEGER : Math.max(0, candidato.proximaRevisaoKm - candidato.hodometroAtual);
}
function folgaDeBlindagemEmDias(candidato, pedido) {
  return candidato.blindagemValidaAte === null ? 0 : Math.max(0, diasEntre(pedido.devolucao, candidato.blindagemValidaAte));
}
function pontuarCandidatos(candidatos, pedido, pesos = PESOS_PADRAO) {
  const elegiveis = candidatos.filter(
    (candidato) => bloqueiosDoCandidato(candidato, pedido).length === 0
  );
  const base = elegiveis.length > 0 ? elegiveis : candidatos;
  const faixas = {
    hodometro: faixaDe(base.map((c) => c.hodometroAtual)),
    folgaDeRevisao: faixaDe(
      base.map(folgaDeRevisaoEmKm).filter((valor) => valor !== Number.MAX_SAFE_INTEGER)
    ),
    custoPorKm: faixaDe(base.map((c) => c.custoPorKmEmCentavos)),
    folgaDeBlindagem: faixaDe(base.map((c) => folgaDeBlindagemEmDias(c, pedido))),
    ociosidade: faixaDe(base.map((c) => c.ociosidadeEmHoras)),
    prioridade: faixaDe(base.map((c) => c.prioridadeDeAlocacao))
  };
  const pontuados = candidatos.map((candidato) => {
    const bloqueios = bloqueiosDoCandidato(candidato, pedido);
    const contribuicoes = contribuicoesDe(candidato, pedido, pesos, faixas);
    const somaDosPesos = contribuicoes.reduce((total, item) => total + item.peso, 0);
    const somaDosPontos = contribuicoes.reduce((total, item) => total + item.pontos, 0);
    return {
      vehicleId: candidato.vehicleId,
      placa: candidato.placa,
      // Divisão inteira com arredondamento: score fracionário empataria por um
      // bilionésimo e a ordem mudaria entre duas execuções iguais.
      score: somaDosPesos === 0 ? 0 : Math.round(somaDosPontos / somaDosPesos),
      contribuicoes,
      bloqueios,
      elegivel: bloqueios.length === 0
    };
  });
  return [...pontuados].sort((a, b) => {
    if (a.elegivel !== b.elegivel) return a.elegivel ? -1 : 1;
    if (a.score !== b.score) return b.score - a.score;
    return a.placa < b.placa ? -1 : 1;
  });
}
function contribuicoesDe(candidato, pedido, pesos, faixas) {
  const folgaRevisao = folgaDeRevisaoEmKm(candidato);
  const folgaBlindagem = folgaDeBlindagemEmDias(candidato, pedido);
  const notas = {
    PRIORIDADE_MANUAL: {
      nota: normalizar(
        candidato.prioridadeDeAlocacao,
        faixas.prioridade.min,
        faixas.prioridade.max,
        false
      ),
      valor: candidato.prioridadeDeAlocacao
    },
    // Menor hodômetro ganha: é o que equaliza a frota ao longo do tempo.
    RODIZIO_DE_USO: {
      nota: normalizar(candidato.hodometroAtual, faixas.hodometro.min, faixas.hodometro.max, true),
      valor: candidato.hodometroAtual
    },
    FOLGA_ATE_A_REVISAO: {
      nota: folgaRevisao === Number.MAX_SAFE_INTEGER ? (
        // Sem plano de manutenção não há folga a comparar. Meio da escala:
        // nem prêmio nem punição por um dado que não existe.
        ESCALA_DO_FATOR / 2
      ) : normalizar(folgaRevisao, faixas.folgaDeRevisao.min, faixas.folgaDeRevisao.max, false),
      valor: folgaRevisao === Number.MAX_SAFE_INTEGER ? -1 : folgaRevisao
    },
    PROXIMIDADE_DA_BASE: {
      nota: candidato.naBaseDaRetirada ? ESCALA_DO_FATOR : 0,
      valor: candidato.naBaseDaRetirada ? 1 : 0
    },
    CUSTO_POR_KM: {
      nota: normalizar(
        candidato.custoPorKmEmCentavos,
        faixas.custoPorKm.min,
        faixas.custoPorKm.max,
        true
      ),
      valor: candidato.custoPorKmEmCentavos
    },
    FOLGA_DA_BLINDAGEM: {
      nota: normalizar(
        folgaBlindagem,
        faixas.folgaDeBlindagem.min,
        faixas.folgaDeBlindagem.max,
        false
      ),
      valor: folgaBlindagem
    },
    // Menos buraco na agenda é melhor: é o fator que evita deixar um vão de
    // seis horas entre duas locações num carro que poderia emendar.
    ENCAIXE_NA_MALHA: {
      nota: normalizar(
        candidato.ociosidadeEmHoras,
        faixas.ociosidade.min,
        faixas.ociosidade.max,
        true
      ),
      valor: candidato.ociosidadeEmHoras
    }
  };
  return FATORES_DE_ALOCACAO.map((fator) => {
    const peso = Math.max(0, Math.trunc(pesos[fator]));
    const { nota, valor } = notas[fator];
    return { fator, peso, nota, pontos: nota * peso, valorObservado: valor };
  }).filter((contribuicao) => contribuicao.peso > 0);
}
function escolherVeiculo(pontuados) {
  return pontuados.find((candidato) => candidato.elegivel);
}

// src/risco.ts
var risco_exports = {};
__export(risco_exports, {
  ALERTAS_DE_RISCO: () => ALERTAS_DE_RISCO,
  ESCALA_DO_RISCO: () => ESCALA_DO_RISCO,
  FATORES_DE_RISCO: () => FATORES_DE_RISCO,
  IDADE_DE_CONTA_RECENTE_EM_DIAS: () => IDADE_DE_CONTA_RECENTE_EM_DIAS,
  LIMITE_DO_RISCO_BAIXO: () => LIMITE_DO_RISCO_BAIXO,
  LIMITE_DO_RISCO_MEDIO: () => LIMITE_DO_RISCO_MEDIO,
  NIVEIS_DE_RISCO: () => NIVEIS_DE_RISCO,
  RESULTADOS_DE_VERIFICACAO: () => RESULTADOS_DE_VERIFICACAO,
  ROTULO_DA_VERIFICACAO_EXTERNA: () => ROTULO_DA_VERIFICACAO_EXTERNA,
  ROTULO_DO_ALERTA_DE_RISCO: () => ROTULO_DO_ALERTA_DE_RISCO,
  ROTULO_DO_FATOR_DE_RISCO: () => ROTULO_DO_FATOR_DE_RISCO,
  ROTULO_DO_NIVEL_DE_RISCO: () => ROTULO_DO_NIVEL_DE_RISCO,
  VERIFICACOES_EXTERNAS: () => VERIFICACOES_EXTERNAS,
  calcularRisco: () => calcularRisco,
  nivelDoRisco: () => nivelDoRisco
});
var ESCALA_DO_RISCO = 1e3;
var NIVEIS_DE_RISCO = ["BAIXO", "MEDIO", "ALTO"];
var ROTULO_DO_NIVEL_DE_RISCO = {
  BAIXO: "Risco baixo",
  MEDIO: "Risco m\xE9dio",
  ALTO: "Risco alto"
};
var LIMITE_DO_RISCO_BAIXO = 350;
var LIMITE_DO_RISCO_MEDIO = 700;
function nivelDoRisco(score) {
  if (score < LIMITE_DO_RISCO_BAIXO) return "BAIXO";
  if (score < LIMITE_DO_RISCO_MEDIO) return "MEDIO";
  return "ALTO";
}
var FATORES_DE_RISCO = [
  "CONTA_RECENTE",
  "SEM_HISTORICO",
  "HISTORICO_NEGATIVO",
  "CADASTRO_INCOMPLETO",
  "CNH_VENCE_NA_LOCACAO",
  "PRIMEIRA_LOCACAO_DE_ALTO_VALOR"
];
var ROTULO_DO_FATOR_DE_RISCO = {
  CONTA_RECENTE: "Conta criada h\xE1 pouco tempo",
  SEM_HISTORICO: "Nenhuma loca\xE7\xE3o conclu\xEDda",
  HISTORICO_NEGATIVO: "No-show ou recusa anterior",
  CADASTRO_INCOMPLETO: "Cadastro sem verifica\xE7\xE3o completa",
  CNH_VENCE_NA_LOCACAO: "CNH vence durante a loca\xE7\xE3o",
  PRIMEIRA_LOCACAO_DE_ALTO_VALOR: "Primeira loca\xE7\xE3o j\xE1 em alto valor"
};
var ALERTAS_DE_RISCO = ["LISTA_RESTRITIVA", "CNH_VENCIDA", "CPF_NAO_VERIFICADO"];
var VERIFICACOES_EXTERNAS = [
  "LIVENESS_NA_SELFIE",
  "LISTA_RESTRITIVA",
  "CPF_EM_BUREAU",
  "CNH_NO_DETRAN"
];
var ROTULO_DA_VERIFICACAO_EXTERNA = {
  LIVENESS_NA_SELFIE: "Prova de vida na selfie",
  LISTA_RESTRITIVA: "Consulta a listas restritivas",
  CPF_EM_BUREAU: "CPF em bureau de cr\xE9dito",
  CNH_NO_DETRAN: "Situa\xE7\xE3o da CNH no Detran"
};
var RESULTADOS_DE_VERIFICACAO = ["NAO_CONSULTADO", "APROVADO", "REPROVADO"];
var ROTULO_DO_ALERTA_DE_RISCO = {
  LISTA_RESTRITIVA: "Presen\xE7a em lista restritiva",
  CNH_VENCIDA: "CNH do condutor principal vencida na retirada",
  CPF_NAO_VERIFICADO: "CPF n\xE3o passou na verifica\xE7\xE3o"
};
var PONTOS = {
  CONTA_RECENTE: 200,
  SEM_HISTORICO: 200,
  HISTORICO_NEGATIVO: 300,
  CADASTRO_INCOMPLETO: 200,
  CNH_VENCE_NA_LOCACAO: 150,
  PRIMEIRA_LOCACAO_DE_ALTO_VALOR: 150
};
var IDADE_DE_CONTA_RECENTE_EM_DIAS = 30;
function calcularRisco(sinais) {
  const alertas = [];
  if (sinais.presencaEmListaRestritiva) alertas.push("LISTA_RESTRITIVA");
  if (sinais.cnhVencidaNaRetirada) alertas.push("CNH_VENCIDA");
  if (!sinais.cpfVerificado) alertas.push("CPF_NAO_VERIFICADO");
  const contribuicoes = [];
  const somar2 = (fator, condicao) => {
    if (condicao) {
      contribuicoes.push({ fator, pontos: PONTOS[fator] });
    }
  };
  somar2("CONTA_RECENTE", sinais.idadeDaContaEmDias < IDADE_DE_CONTA_RECENTE_EM_DIAS);
  somar2("SEM_HISTORICO", sinais.reservasFinalizadas === 0);
  somar2("HISTORICO_NEGATIVO", sinais.noShows > 0 || sinais.recusasAnteriores > 0);
  somar2("CADASTRO_INCOMPLETO", !sinais.emailVerificado || !sinais.livenessAprovado);
  somar2("CNH_VENCE_NA_LOCACAO", sinais.cnhVenceDuranteALocacao);
  somar2(
    "PRIMEIRA_LOCACAO_DE_ALTO_VALOR",
    sinais.reservasFinalizadas === 0 && sinais.valorAcimaDoPercentil90
  );
  const soma = contribuicoes.reduce((total, contribuicao) => total + contribuicao.pontos, 0);
  const score = alertas.length > 0 ? ESCALA_DO_RISCO : Math.min(soma, ESCALA_DO_RISCO);
  return { score, nivel: nivelDoRisco(score), contribuicoes, alertas };
}
var VALIDADE_DA_ANALISE_EM_DIAS = 90;
var MILISSEGUNDOS_POR_DIA4 = 864e5;
function validadeDaAnalise(concluidaEm) {
  return new Date(concluidaEm.getTime() + VALIDADE_DA_ANALISE_EM_DIAS * MILISSEGUNDOS_POR_DIA4);
}
function precisaDeAnaliseAutomatica(sinais) {
  return sinais.reservasFinalizadas === 0 && !sinais.temAnalisePendenteOuValida;
}
var STATUS_DA_ANALISE_CADASTRAL = [
  /** Pedida aqui, ainda não aceita pelo provedor (fila com retentativa). */
  "SOLICITANDO",
  "AGUARDANDO_CONSENTIMENTO",
  "EM_ANALISE",
  "CONCLUIDA",
  "CONSENTIMENTO_RECUSADO",
  "CONSENTIMENTO_EXPIRADO",
  "CANCELADA",
  "FALHOU"
];
var statusDaAnaliseCadastralSchema = z.enum(STATUS_DA_ANALISE_CADASTRAL);
var STATUS_PENDENTES_DA_ANALISE = [
  "SOLICITANDO",
  "AGUARDANDO_CONSENTIMENTO",
  "EM_ANALISE"
];
function analiseEstaPendente(status) {
  return STATUS_PENDENTES_DA_ANALISE.includes(status);
}
var ROTULO_DO_STATUS_DA_ANALISE = {
  SOLICITANDO: "Pedindo a an\xE1lise",
  AGUARDANDO_CONSENTIMENTO: "Aguardando o cliente autorizar",
  EM_ANALISE: "Em an\xE1lise",
  CONCLUIDA: "Conclu\xEDda",
  CONSENTIMENTO_RECUSADO: "O cliente n\xE3o autorizou a an\xE1lise",
  CONSENTIMENTO_EXPIRADO: "A autoriza\xE7\xE3o do cliente expirou",
  CANCELADA: "Cancelada",
  FALHOU: "N\xE3o foi poss\xEDvel consultar"
};
var ORIGENS_DA_ANALISE = ["AUTOMATICA", "MANUAL"];
var DECISOES_DA_ANALISE = ["VERDE", "AMARELO", "VERMELHO"];
var ROTULO_DA_DECISAO = {
  VERDE: "Favor\xE1vel",
  AMARELO: "Aten\xE7\xE3o",
  VERMELHO: "Desfavor\xE1vel"
};
var GRAVIDADES_DO_MOTIVO = ["ALTA", "MEDIA", "BAIXA"];
var FAIXAS_DO_BUREAU = ["ALTA", "MEDIA", "BAIXA"];
var ROTULO_DA_FAIXA_DO_BUREAU = {
  ALTA: "alta (700 ou mais)",
  MEDIA: "m\xE9dia (500 a 699)",
  BAIXA: "baixa (abaixo de 500)"
};
var VERIFICACOES_DA_ANALISE = [
  "provaDeVida",
  "cpfReceita",
  "bureauDeCredito",
  "scr",
  "cnh",
  "processosJudiciais",
  "listasRestritivas"
];
var ROTULO_DA_VERIFICACAO_DA_ANALISE = {
  provaDeVida: "Prova de vida na selfie",
  cpfReceita: "CPF na Receita",
  bureauDeCredito: "Nota de cr\xE9dito (bir\xF4)",
  scr: "D\xEDvidas em banco (SCR)",
  cnh: "CNH no \xF3rg\xE3o oficial",
  processosJudiciais: "Processos judiciais",
  listasRestritivas: "Listas restritivas"
};
var RESULTADOS_DA_VERIFICACAO_DA_ANALISE = [
  "APROVADO",
  "ALERTA",
  "REPROVADO",
  "NAO_CONSULTADO",
  /** O provedor da fonte falhou — diferente de "ninguém pediu". */
  "INDISPONIVEL"
];
var ROTULO_DO_RESULTADO_DA_VERIFICACAO = {
  APROVADO: "Sem pend\xEAncia",
  ALERTA: "Aten\xE7\xE3o",
  REPROVADO: "Reprovado",
  NAO_CONSULTADO: "N\xE3o consultado",
  INDISPONIVEL: "Fonte indispon\xEDvel"
};
var SITUACOES_DA_CNH = [
  "REGULAR",
  "VENCIDA",
  "SUSPENSA",
  "CASSADA",
  /**
   * "Bloqueio a conferir": a base oficial acusou bloqueio sem dizer o tipo ou a
   * data de fim. É ATENÇÃO, nunca vermelho — suspensa e cassada só quando a
   * resposta disser (plano do Mira aprovado em 26/10/2026).
   */
  "BLOQUEADA",
  "NAO_ENCONTRADA"
];
var ROTULO_DA_SITUACAO_DA_CNH = {
  REGULAR: "regular",
  VENCIDA: "vencida",
  SUSPENSA: "suspensa",
  CASSADA: "cassada",
  BLOQUEADA: "com bloqueio a conferir",
  NAO_ENCONTRADA: "n\xE3o encontrada"
};
var CODIGOS_DE_FALHA_DA_ANALISE = [
  /** O rosto da selfie não confere com a base oficial. */
  "IDENTIDADE_NAO_CONFIRMADA",
  /** O cliente começou e não concluiu a selfie. */
  "PROVA_DE_VIDA_NAO_CONCLUIDA",
  /** Receita, birô ou CNH fora do ar depois das novas tentativas. */
  "FONTES_INDISPONIVEIS",
  /** O cadastro não tem o nome completo que a selfie exige. */
  "NOME_INCOMPLETO"
];
var ROTULO_DA_FALHA_DA_ANALISE = {
  IDENTIDADE_NAO_CONFIRMADA: "A selfie n\xE3o conferiu com a base oficial. Confira a identidade do cliente no balc\xE3o.",
  PROVA_DE_VIDA_NAO_CONCLUIDA: "O cliente n\xE3o concluiu a selfie. Pe\xE7a de novo se precisar.",
  FONTES_INDISPONIVEIS: "As fontes de consulta estavam fora do ar. Nada foi cobrado \u2014 pe\xE7a de novo mais tarde.",
  NOME_INCOMPLETO: "O cadastro do cliente n\xE3o tem o nome completo (nome e sobrenome), que a selfie exige."
};
function nomeCompletoParaAnalise(nome) {
  return (nome ?? "").trim().split(/\s+/).filter((parte) => parte.length > 1).length >= 2;
}
var verificacaoSchema = z.object({
  resultado: z.enum(RESULTADOS_DA_VERIFICACAO_DA_ANALISE),
  detalhe: z.string().max(200).nullable()
});
var NAO_CONSULTADO = { resultado: "NAO_CONSULTADO", detalhe: null };
var resultadoDaAnaliseSchema = z.object({
  decisao: z.enum(DECISOES_DA_ANALISE),
  /**
   * 0–1000, MAIOR = MELHOR (o risco interno é o contrário). Sempre `null` na
   * locação: o Mira nunca mostra a nota numérica do birô a terceiros — o que
   * vem é a `faixaDoBureau`. Fica no contrato para uma nota própria do Mira.
   */
  score: z.number().int().min(0).max(1e3).nullable(),
  /**
   * Alta, média ou baixa — `null` quando o birô não deu nota. A análise
   * gravada antes da emenda de 26/10/2026 não tem o campo.
   */
  faixaDoBureau: z.enum(FAIXAS_DO_BUREAU).nullable().default(null),
  motivos: z.array(
    z.object({
      codigo: z.string().max(60),
      descricao: z.string().max(300),
      gravidade: z.enum(GRAVIDADES_DO_MOTIVO)
    })
  ).max(50),
  verificacoes: z.object({
    provaDeVida: verificacaoSchema,
    cpfReceita: verificacaoSchema,
    bureauDeCredito: verificacaoSchema,
    /**
     * Atraso ou prejuízo em banco (SCR do Banco Central) — sem valor, credor
     * nem modalidade. FORA da análise econômica que o Marcos escolheu em
     * 26/10/2026: vem sempre NÃO CONSULTADO (como na análise gravada antes da
     * emenda). O campo fica no contrato para o dia em que entrar — e só com o
     * OK do jurídico (o SCR é para risco de crédito; locação não é empréstimo).
     */
    scr: verificacaoSchema.default(NAO_CONSULTADO),
    cnh: verificacaoSchema,
    processosJudiciais: verificacaoSchema,
    listasRestritivas: verificacaoSchema
  }),
  cnh: z.object({
    situacao: z.enum(SITUACOES_DA_CNH).nullable(),
    /** `AAAA-MM-DD`. */
    validade: z.string().nullable(),
    categoria: z.string().max(5).nullable()
  }),
  concluidaEm: z.string()
});
var analiseCadastralSchema = z.object({
  id: z.uuid(),
  reservaId: z.uuid().nullable(),
  status: statusDaAnaliseCadastralSchema,
  origem: z.enum(ORIGENS_DA_ANALISE),
  solicitadaEm: z.string(),
  atualizadaEm: z.string(),
  /** Até quando o cliente pode autorizar. Só enquanto aguarda o consentimento. */
  consentimentoExpiraEm: z.string().nullable(),
  /** Até quando o resultado é reaproveitado. Só na concluída. */
  validaAte: z.string().nullable(),
  resultado: resultadoDaAnaliseSchema.nullable(),
  /** Diagnóstico curto em pt-BR, quando `FALHOU`. Nunca payload do provedor. */
  erro: z.string().nullable(),
  /** O código da falha (`CODIGOS_DE_FALHA_DA_ANALISE`), quando o provedor disse. */
  codigoDoErro: z.string().nullable()
});
var autorizacaoDaAnaliseSchema = z.object({
  status: statusDaAnaliseCadastralSchema,
  /** O link da autorização com selfie. Só para o próprio titular, e só enquanto aguarda. */
  consentimentoUrl: z.url().nullable(),
  consentimentoExpiraEm: z.string().nullable()
});
var autorizacaoDaReservaSchema = z.object({
  analise: autorizacaoDaAnaliseSchema.nullable()
});
var MODOS_DO_MIRA_SCORE = ["DESLIGADO", "FAKE", "REAL"];
var configurarMiraScoreSchema = z.object({
  /** A chave de API que o Mira Score gerou para a conta da locadora. */
  chave: z.string().trim().min(8, "A chave parece curta demais \u2014 copie a chave inteira do Mira Score.").max(300, "A chave parece longa demais \u2014 confira se colou s\xF3 a chave.")
});
var integracaoMiraScoreSchema = z.object({
  /** O modo do AMBIENTE (env) — `DESLIGADO` esconde a integração inteira. */
  modo: z.enum(MODOS_DO_MIRA_SCORE),
  configurada: z.boolean(),
  /** Os 4 últimos caracteres, para a pessoa reconhecer QUAL chave está lá. */
  finalDaChave: z.string().nullable(),
  atualizadaEm: z.string().nullable(),
  ultimoTesteEm: z.string().nullable(),
  ultimoTesteOk: z.boolean().nullable()
});

// src/vistoria.ts
var vistoria_exports = {};
__export(vistoria_exports, {
  ANGULOS_DE_FOTO: () => ANGULOS_DE_FOTO,
  ANGULOS_OBRIGATORIOS: () => ANGULOS_OBRIGATORIOS,
  ESCALA_DA_POSICAO: () => ESCALA_DA_POSICAO,
  GRAVIDADES_DE_AVARIA: () => GRAVIDADES_DE_AVARIA,
  OITAVOS_DO_TANQUE: () => OITAVOS_DO_TANQUE,
  RAIO_DE_CORRESPONDENCIA_EM_MILESIMOS: () => RAIO_DE_CORRESPONDENCIA_EM_MILESIMOS,
  ROTULO_DA_GRAVIDADE: () => ROTULO_DA_GRAVIDADE,
  ROTULO_DO_ANGULO: () => ROTULO_DO_ANGULO,
  ROTULO_DO_TIPO_DE_AVARIA: () => ROTULO_DO_TIPO_DE_AVARIA,
  ROTULO_DO_TIPO_DE_VISTORIA: () => ROTULO_DO_TIPO_DE_VISTORIA,
  TIPOS_DE_AVARIA: () => TIPOS_DE_AVARIA,
  TIPOS_DE_VISTORIA: () => TIPOS_DE_VISTORIA,
  anguloDeFotoSchema: () => anguloDeFotoSchema,
  angulosFaltantes: () => angulosFaltantes,
  avariaSchema: () => avariaSchema,
  avariasNovas: () => avariasNovas,
  chaveDeArquivoSchema: () => chaveDeArquivoSchema,
  fotoDeVistoriaSchema: () => fotoDeVistoriaSchema,
  gravidadeDeAvariaSchema: () => gravidadeDeAvariaSchema,
  itemDeChecklistSchema: () => itemDeChecklistSchema,
  nivelDeCombustivelSchema: () => nivelDeCombustivelSchema,
  registrarVistoriaSchema: () => registrarVistoriaSchema,
  respostaDeChecklistSchema: () => respostaDeChecklistSchema,
  rotuloDoNivelDeCombustivel: () => rotuloDoNivelDeCombustivel,
  salvarChecklistSchema: () => salvarChecklistSchema,
  tipoDeAvariaSchema: () => tipoDeAvariaSchema,
  tipoDeVistoriaSchema: () => tipoDeVistoriaSchema,
  vistoriaAvulsaSchema: () => vistoriaAvulsaSchema
});
var TIPOS_DE_VISTORIA = ["SAIDA", "RETORNO"];
var tipoDeVistoriaSchema = z.enum(TIPOS_DE_VISTORIA, {
  error: "tipo de vistoria inv\xE1lido"
});
var ROTULO_DO_TIPO_DE_VISTORIA = {
  SAIDA: "Vistoria de sa\xEDda",
  RETORNO: "Vistoria de retorno"
};
var ANGULOS_DE_FOTO = [
  "FRENTE",
  "TRASEIRA",
  "LATERAL_ESQUERDA",
  "LATERAL_DIREITA",
  "TETO",
  "INTERIOR_DIANTEIRO",
  "INTERIOR_TRASEIRO",
  "PAINEL",
  "PORTA_MALAS",
  "DETALHE"
];
var anguloDeFotoSchema = z.enum(ANGULOS_DE_FOTO, { error: "\xE2ngulo de foto inv\xE1lido" });
var ROTULO_DO_ANGULO = {
  FRENTE: "Frente",
  TRASEIRA: "Traseira",
  LATERAL_ESQUERDA: "Lateral esquerda",
  LATERAL_DIREITA: "Lateral direita",
  TETO: "Teto",
  INTERIOR_DIANTEIRO: "Interior dianteiro",
  INTERIOR_TRASEIRO: "Interior traseiro",
  PAINEL: "Painel (hod\xF4metro e combust\xEDvel)",
  PORTA_MALAS: "Porta-malas",
  DETALHE: "Detalhe (avaria ou item espec\xEDfico)"
};
var ANGULOS_OBRIGATORIOS = [
  "FRENTE",
  "TRASEIRA",
  "LATERAL_ESQUERDA",
  "LATERAL_DIREITA",
  "INTERIOR_DIANTEIRO",
  "PAINEL",
  "PORTA_MALAS"
];
var OITAVOS_DO_TANQUE = 8;
var nivelDeCombustivelSchema = z.coerce.number({ error: "n\xEDvel de combust\xEDvel \xE9 obrigat\xF3rio" }).int({ error: "o n\xEDvel \xE9 medido em oitavos inteiros do tanque" }).min(0, { error: "o n\xEDvel m\xEDnimo \xE9 0 (tanque vazio)" }).max(OITAVOS_DO_TANQUE, { error: `o n\xEDvel m\xE1ximo \xE9 ${String(OITAVOS_DO_TANQUE)} (tanque cheio)` });
function rotuloDoNivelDeCombustivel(oitavos) {
  const ROTULOS = ["Vazio", "1/8", "1/4", "3/8", "1/2", "5/8", "3/4", "7/8", "Cheio"];
  return ROTULOS[oitavos] ?? String(oitavos);
}
var TIPOS_DE_AVARIA = [
  "RISCO",
  "AMASSADO",
  "TRINCA",
  "QUEBRA",
  "FALTANTE",
  "MANCHA",
  "DESGASTE",
  "OUTRO"
];
var tipoDeAvariaSchema = z.enum(TIPOS_DE_AVARIA, { error: "tipo de avaria inv\xE1lido" });
var ROTULO_DO_TIPO_DE_AVARIA = {
  RISCO: "Risco",
  AMASSADO: "Amassado",
  TRINCA: "Trinca",
  QUEBRA: "Quebra",
  FALTANTE: "Item faltante",
  MANCHA: "Mancha",
  DESGASTE: "Desgaste",
  OUTRO: "Outro"
};
var GRAVIDADES_DE_AVARIA = ["LEVE", "MODERADA", "GRAVE"];
var gravidadeDeAvariaSchema = z.enum(GRAVIDADES_DE_AVARIA, {
  error: "gravidade de avaria inv\xE1lida"
});
var ROTULO_DA_GRAVIDADE = {
  LEVE: "Leve",
  MODERADA: "Moderada",
  GRAVE: "Grave"
};
var ESCALA_DA_POSICAO = 1e3;
var chaveDeArquivoSchema = z.string({ error: "a chave do arquivo \xE9 obrigat\xF3ria" }).regex(/^tenants\/[0-9a-f-]{36}\/[a-z-]{1,40}\/[0-9a-f-]{36}\.[a-z0-9]{2,5}$/, {
  error: "chave de arquivo inv\xE1lida \u2014 use a chave devolvida pelo upload assinado"
});
var posicaoSchema = z.coerce.number({ error: "posi\xE7\xE3o da avaria \xE9 obrigat\xF3ria" }).int({ error: "a posi\xE7\xE3o \xE9 inteira, em mil\xE9simos do diagrama" }).min(0, { error: "a posi\xE7\xE3o m\xEDnima \xE9 0" }).max(ESCALA_DA_POSICAO, { error: `a posi\xE7\xE3o m\xE1xima \xE9 ${String(ESCALA_DA_POSICAO)}` });
var avariaSchema = z.object({
  tipo: tipoDeAvariaSchema,
  gravidade: gravidadeDeAvariaSchema,
  /** Posição sobre o diagrama SVG do carro, em milésimos (0..1000). */
  posicaoXEmMilesimos: posicaoSchema,
  posicaoYEmMilesimos: posicaoSchema,
  descricao: z.string({ error: "descri\xE7\xE3o da avaria \xE9 obrigat\xF3ria" }).trim().min(3, { error: 'descreva a avaria \u2014 "risco" sozinho n\xE3o se defende depois' }).max(500, { error: "descri\xE7\xE3o muito longa" }),
  /** Foto de detalhe da avaria. Opcional na marcação, cobrada pela operação. */
  fotoArquivoChave: chaveDeArquivoSchema.nullish()
});
var RAIO_DE_CORRESPONDENCIA_EM_MILESIMOS = 60;
function mesmaAvaria(a, b) {
  if (a.tipo !== b.tipo) {
    return false;
  }
  const dx = Math.abs(a.posicaoXEmMilesimos - b.posicaoXEmMilesimos);
  const dy = Math.abs(a.posicaoYEmMilesimos - b.posicaoYEmMilesimos);
  return Math.max(dx, dy) <= RAIO_DE_CORRESPONDENCIA_EM_MILESIMOS;
}
function avariasNovas(daSaida, doRetorno) {
  const absorvidas = /* @__PURE__ */ new Set();
  return doRetorno.filter((avaria) => {
    const indice = daSaida.findIndex(
      (antiga, i) => !absorvidas.has(i) && mesmaAvaria(antiga, avaria)
    );
    if (indice === -1) {
      return true;
    }
    absorvidas.add(indice);
    return false;
  });
}
var chaveDeChecklistSchema = z.string({ error: "chave do item \xE9 obrigat\xF3ria" }).regex(/^[a-z0-9-]{2,60}$/, {
  error: "a chave usa letras min\xFAsculas, n\xFAmeros e h\xEDfen (2 a 60 caracteres)"
});
var itemDeChecklistSchema = z.object({
  chave: chaveDeChecklistSchema,
  rotulo: z.string({ error: "r\xF3tulo do item \xE9 obrigat\xF3rio" }).trim().min(2, { error: "r\xF3tulo precisa de ao menos 2 caracteres" }).max(120, { error: "r\xF3tulo muito longo" }),
  /** Item obrigatório precisa de resposta em TODA vistoria; os demais podem ficar em branco. */
  obrigatorio: z.boolean().default(true),
  ordemDeExibicao: z.coerce.number().int().min(0).default(0)
});
var salvarChecklistSchema = z.object({
  itens: z.array(itemDeChecklistSchema).max(60, { error: "checklist com mais de 60 itens ningu\xE9m preenche no p\xE1tio" })
}).refine(
  (entrada) => new Set(entrada.itens.map((item) => item.chave)).size === entrada.itens.length,
  {
    error: "h\xE1 itens com a mesma chave",
    path: ["itens"]
  }
);
var respostaDeChecklistSchema = z.object({
  chave: chaveDeChecklistSchema,
  /** `true` = conferido e OK; `false` = problema (a observação explica). */
  ok: z.boolean(),
  observacao: z.string().trim().max(500, { error: "observa\xE7\xE3o muito longa" }).nullish()
});
var fotoDeVistoriaSchema = z.object({
  angulo: anguloDeFotoSchema,
  /** A chave do arquivo já enviado ao storage (módulo `arquivos`). */
  arquivoChave: chaveDeArquivoSchema
});
function angulosFaltantes(fotos) {
  const presentes = new Set(fotos.map((foto) => foto.angulo));
  return ANGULOS_OBRIGATORIOS.filter((angulo) => !presentes.has(angulo));
}
var camposDaVistoria = {
  hodometro: z.coerce.number({ error: "hod\xF4metro \xE9 obrigat\xF3rio" }).int({ error: "hod\xF4metro \xE9 inteiro, em km" }).min(0, { error: "hod\xF4metro n\xE3o pode ser negativo" }).max(2e6, { error: "hod\xF4metro implaus\xEDvel \u2014 confira o valor" }),
  combustivelEmOitavos: nivelDeCombustivelSchema,
  respostas: z.array(respostaDeChecklistSchema).max(60, { error: "h\xE1 mais respostas do que itens poss\xEDveis" }).default([]),
  fotos: z.array(fotoDeVistoriaSchema).min(1, { error: "a vistoria precisa de fotos" }).max(60, { error: "h\xE1 fotos demais \u2014 envie at\xE9 60" }),
  avarias: z.array(avariaSchema).max(100, { error: "h\xE1 avarias demais numa vistoria s\xF3" }).default([]),
  /**
   * Assinatura do cliente desenhada no canvas, já enviada ao storage como
   * imagem. Obrigatória: vistoria sem assinatura não prova concordância.
   */
  assinaturaArquivoChave: chaveDeArquivoSchema,
  observacoes: z.string().trim().max(2e3, { error: "observa\xE7\xF5es muito longas" }).nullish()
};
function fotosCompletas(entrada) {
  return angulosFaltantes(entrada.fotos).length === 0;
}
function respostasSemDuplicata(entrada) {
  return new Set(entrada.respostas.map((resposta) => resposta.chave)).size === entrada.respostas.length;
}
var ERRO_DE_FOTOS = { error: "faltam fotos de \xE2ngulos obrigat\xF3rios", path: ["fotos"] };
var ERRO_DE_RESPOSTAS = {
  error: "h\xE1 duas respostas para o mesmo item do checklist",
  path: ["respostas"]
};
var vistoriaAvulsaSchema = z.object(camposDaVistoria).refine(fotosCompletas, ERRO_DE_FOTOS).refine(respostasSemDuplicata, ERRO_DE_RESPOSTAS);
var registrarVistoriaSchema = z.object({ tipo: tipoDeVistoriaSchema, ...camposDaVistoria }).refine(fotosCompletas, ERRO_DE_FOTOS).refine(respostasSemDuplicata, ERRO_DE_RESPOSTAS);

// src/fechamento.ts
var fechamento_exports = {};
__export(fechamento_exports, {
  ROTULO_DA_LINHA_DE_FECHAMENTO: () => ROTULO_DA_LINHA_DE_FECHAMENTO,
  TIPOS_DE_LINHA_DE_FECHAMENTO: () => TIPOS_DE_LINHA_DE_FECHAMENTO,
  fecharLocacao: () => fecharLocacao
});
var TIPOS_DE_LINHA_DE_FECHAMENTO = [
  "ATRASO",
  "KM_EXCEDENTE",
  "COMBUSTIVEL",
  "AVARIA",
  "SINISTRO",
  "MULTA",
  /** A taxa de administração sobre a multa, quando a política da locadora cobra. */
  "TAXA_DE_MULTA",
  "CREDITO_DEVOLUCAO_ANTECIPADA"
];
var ROTULO_DA_LINHA_DE_FECHAMENTO = {
  ATRASO: "Di\xE1rias de atraso",
  KM_EXCEDENTE: "Quilometragem excedente",
  COMBUSTIVEL: "Reabastecimento",
  AVARIA: "Avarias",
  SINISTRO: "Sinistros (franquia)",
  MULTA: "Multas de tr\xE2nsito",
  TAXA_DE_MULTA: "Taxa de administra\xE7\xE3o de multa",
  CREDITO_DEVOLUCAO_ANTECIPADA: "Cr\xE9dito por devolu\xE7\xE3o antecipada"
};
function validarInteiroNaoNegativo(valor, rotulo) {
  if (!Number.isInteger(valor) || valor < 0) {
    throw new RangeError(`Fechamento inv\xE1lido: ${rotulo} precisa ser inteiro n\xE3o negativo.`);
  }
}
function validarTrecho(trecho, indice) {
  const rotulo = `trecho ${String(indice + 1)}`;
  validarInteiroNaoNegativo(trecho.hodometroSaida, `hod\xF4metro de sa\xEDda do ${rotulo}`);
  validarInteiroNaoNegativo(trecho.hodometroRetorno, `hod\xF4metro de retorno do ${rotulo}`);
  validarInteiroNaoNegativo(trecho.combustivelSaidaEmOitavos, `combust\xEDvel de sa\xEDda do ${rotulo}`);
  validarInteiroNaoNegativo(
    trecho.combustivelRetornoEmOitavos,
    `combust\xEDvel de retorno do ${rotulo}`
  );
  if (trecho.hodometroRetorno < trecho.hodometroSaida) {
    throw new RangeError(
      `Fechamento inv\xE1lido: o hod\xF4metro de retorno do ${rotulo} \xE9 menor que o de sa\xEDda. Corrija a vistoria antes de fechar.`
    );
  }
  if (trecho.combustivelSaidaEmOitavos > OITAVOS_DO_TANQUE || trecho.combustivelRetornoEmOitavos > OITAVOS_DO_TANQUE) {
    throw new RangeError(
      `Fechamento inv\xE1lido: n\xEDvel de combust\xEDvel acima do tanque cheio no ${rotulo}.`
    );
  }
}
function fecharLocacao(entrada) {
  validarInteiroNaoNegativo(entrada.diariasContratadas, "di\xE1rias contratadas");
  validarInteiroNaoNegativo(
    entrada.reembolsoDeDevolucaoAntecipadaPercentual,
    "reembolso de devolu\xE7\xE3o antecipada"
  );
  if (entrada.reembolsoDeDevolucaoAntecipadaPercentual > 100) {
    throw new RangeError("Fechamento inv\xE1lido: reembolso acima de 100%.");
  }
  if (entrada.diariasContratadas < 1) {
    throw new RangeError("Fechamento inv\xE1lido: n\xE3o existe loca\xE7\xE3o de zero di\xE1ria.");
  }
  if (entrada.trechos.length === 0) {
    throw new RangeError("Fechamento inv\xE1lido: a loca\xE7\xE3o precisa de ao menos um trecho vistoriado.");
  }
  entrada.trechos.forEach(validarTrecho);
  if (entrada.kmFranquiaTotal !== null) {
    validarInteiroNaoNegativo(entrada.kmFranquiaTotal, "franquia de km");
  }
  if (entrada.caucao < 0n) {
    throw new RangeError("Fechamento inv\xE1lido: cau\xE7\xE3o negativa.");
  }
  const linhas = [];
  const diariasReais = rentalDays(
    entrada.retirada,
    entrada.devolucaoReal,
    entrada.fusoDaRetirada,
    entrada.politica
  );
  const diariasDeAtraso = Math.max(0, diariasReais - entrada.diariasContratadas);
  if (diariasDeAtraso > 0) {
    linhas.push({
      tipo: "ATRASO",
      descricao: `${String(diariasDeAtraso)} di\xE1ria(s) de atraso na devolu\xE7\xE3o`,
      valor: mulQuantity(entrada.valorDaDiaria, diariasDeAtraso)
    });
  }
  const kmRodado = entrada.trechos.reduce(
    (total, trecho) => total + (trecho.hodometroRetorno - trecho.hodometroSaida),
    0
  );
  const kmExcedente = entrada.kmFranquiaTotal === null ? 0 : Math.max(0, kmRodado - entrada.kmFranquiaTotal);
  if (kmExcedente > 0) {
    linhas.push({
      tipo: "KM_EXCEDENTE",
      descricao: `${String(kmExcedente)} km al\xE9m da franquia`,
      valor: mulQuantity(entrada.valorDoKmExcedente, kmExcedente)
    });
  }
  const oitavosFaltantes = entrada.trechos.reduce(
    (total, trecho) => total + Math.max(0, trecho.combustivelSaidaEmOitavos - trecho.combustivelRetornoEmOitavos),
    0
  );
  if (oitavosFaltantes > 0) {
    linhas.push({
      tipo: "COMBUSTIVEL",
      descricao: `Reabastecimento de ${String(oitavosFaltantes)}/8 do tanque`,
      valor: mulRate(entrada.valorDoTanqueCheio, {
        numerator: BigInt(oitavosFaltantes),
        denominator: BigInt(OITAVOS_DO_TANQUE)
      })
    });
  }
  const totalDeAvarias = sumAll(entrada.avarias.map((avaria) => avaria.valor));
  const totalDeSinistros = sumAll(entrada.sinistros.map((sinistro) => sinistro.valor));
  const totalDeDanos = sumAll([totalDeAvarias, totalDeSinistros]);
  if (totalDeDanos > 0n) {
    const cobrado = entrada.protecao === null ? totalDeDanos : entrada.protecao.franquia === null ? zero : min(totalDeDanos, entrada.protecao.franquia);
    if (cobrado > 0n) {
      const limitado = cobrado < totalDeDanos;
      const [deAvarias, deSinistros] = totalDeAvarias > 0n && totalDeSinistros > 0n ? allocate(cobrado, [totalDeAvarias, totalDeSinistros]) : totalDeAvarias > 0n ? [cobrado, zero] : [zero, cobrado];
      if (deAvarias !== void 0 && deAvarias > 0n) {
        linhas.push({
          tipo: "AVARIA",
          descricao: limitado ? `${String(entrada.avarias.length)} avaria(s) \u2014 cobran\xE7a limitada \xE0 franquia da prote\xE7\xE3o` : `${String(entrada.avarias.length)} avaria(s) or\xE7ada(s)`,
          valor: deAvarias
        });
      }
      if (deSinistros !== void 0 && deSinistros > 0n) {
        linhas.push({
          tipo: "SINISTRO",
          descricao: limitado ? `${String(entrada.sinistros.length)} sinistro(s) \u2014 cobran\xE7a limitada \xE0 franquia da prote\xE7\xE3o` : `${String(entrada.sinistros.length)} sinistro(s) com responsabilidade do cliente`,
          valor: deSinistros
        });
      }
    }
  }
  for (const multa of entrada.multas) {
    if (multa.valor > 0n) {
      linhas.push({ tipo: "MULTA", descricao: multa.descricao, valor: multa.valor });
    }
  }
  for (const taxa of entrada.taxasDeMulta ?? []) {
    if (taxa.valor > 0n) {
      linhas.push({ tipo: "TAXA_DE_MULTA", descricao: taxa.descricao, valor: taxa.valor });
    }
  }
  const diariasNaoUsadas = Math.max(0, entrada.diariasContratadas - diariasReais);
  const creditoBruto = diariasNaoUsadas > 0 && entrada.reembolsoDeDevolucaoAntecipadaPercentual > 0 ? mulRate(mulQuantity(entrada.valorDaDiaria, diariasNaoUsadas), {
    numerator: BigInt(entrada.reembolsoDeDevolucaoAntecipadaPercentual),
    denominator: 100n
  }) : zero;
  const somaDasCobrancas = sumAll(linhas.map((linha) => linha.valor));
  const creditoAplicado = min(creditoBruto, somaDasCobrancas);
  if (creditoAplicado > 0n) {
    linhas.push({
      tipo: "CREDITO_DEVOLUCAO_ANTECIPADA",
      descricao: `${String(diariasNaoUsadas)} di\xE1ria(s) n\xE3o usada(s) \u2014 ${String(
        entrada.reembolsoDeDevolucaoAntecipadaPercentual
      )}% de reembolso abatido das cobran\xE7as`,
      valor: sub(zero, creditoAplicado)
    });
  }
  const totalDeCobrancas = sub(somaDasCobrancas, creditoAplicado);
  const caucaoRetida = min(entrada.caucao, totalDeCobrancas);
  const caucaoALiberar = sub(entrada.caucao, caucaoRetida);
  const saldoACobrar = sub(totalDeCobrancas, caucaoRetida);
  return {
    linhas,
    totalDeCobrancas,
    caucao: entrada.caucao,
    caucaoRetida,
    caucaoALiberar,
    saldoACobrar,
    diariasReais,
    diariasDeAtraso,
    kmRodado,
    kmExcedente,
    oitavosFaltantes,
    creditoDeDevolucaoAntecipada: creditoBruto
  };
}

// src/locacao.ts
var locacao_exports = {};
__export(locacao_exports, {
  ACOES_DE_TRANSICAO: () => ACOES_DE_TRANSICAO,
  ALFABETO_DO_CODIGO_DE_RETIRADA: () => ALFABETO_DO_CODIGO_DE_RETIRADA,
  CATEGORIAS_DE_CNH: () => CATEGORIAS_DE_CNH,
  CODIGO_DE_RETIRADA_NAO_E_O_NUMERO: () => CODIGO_DE_RETIRADA_NAO_E_O_NUMERO,
  JANELA_MAXIMA_DO_GANTT_EM_DIAS: () => JANELA_MAXIMA_DO_GANTT_EM_DIAS,
  MAXIMO_DE_CONDUTORES: () => MAXIMO_DE_CONDUTORES,
  MOTIVOS_DE_CANCELAMENTO_PADRAO: () => MOTIVOS_DE_CANCELAMENTO_PADRAO,
  ORIGENS_DE_CANCELAMENTO: () => ORIGENS_DE_CANCELAMENTO,
  PESO_MAXIMO_DE_FATOR: () => PESO_MAXIMO_DE_FATOR,
  PROVEDORES_DE_ASSINATURA: () => PROVEDORES_DE_ASSINATURA,
  PROVEDOR_DE_ASSINATURA_PADRAO: () => PROVEDOR_DE_ASSINATURA_PADRAO,
  ROTULO_DA_ORIGEM_DE_CANCELAMENTO: () => ROTULO_DA_ORIGEM_DE_CANCELAMENTO,
  ROTULO_DO_DOCUMENTO_SOLICITAVEL: () => ROTULO_DO_DOCUMENTO_SOLICITAVEL,
  ROTULO_DO_PROVEDOR_DE_ASSINATURA: () => ROTULO_DO_PROVEDOR_DE_ASSINATURA,
  ROTULO_DO_STATUS_DE_CONTRATO: () => ROTULO_DO_STATUS_DE_CONTRATO,
  ROTULO_DO_STATUS_DE_DOCUMENTO: () => ROTULO_DO_STATUS_DE_DOCUMENTO,
  ROTULO_DO_STATUS_DE_MULTA: () => ROTULO_DO_STATUS_DE_MULTA,
  ROTULO_DO_STATUS_DE_SINISTRO: () => ROTULO_DO_STATUS_DE_SINISTRO,
  ROTULO_DO_TIPO_DE_SINISTRO: () => ROTULO_DO_TIPO_DE_SINISTRO,
  STATUS_DE_CONTRATO: () => STATUS_DE_CONTRATO,
  STATUS_DE_DOCUMENTO: () => STATUS_DE_DOCUMENTO,
  STATUS_DE_MULTA: () => STATUS_DE_MULTA,
  STATUS_DE_SINISTRO: () => STATUS_DE_SINISTRO,
  TAMANHO_DO_CODIGO_DE_RETIRADA: () => TAMANHO_DO_CODIGO_DE_RETIRADA,
  TIPOS_DE_DOCUMENTO_SOLICITAVEL: () => TIPOS_DE_DOCUMENTO_SOLICITAVEL,
  TIPOS_DE_SINISTRO: () => TIPOS_DE_SINISTRO,
  alocarVeiculoSchema: () => alocarVeiculoSchema,
  anexarDocumentoSchema: () => anexarDocumentoSchema,
  atualizarSinistroSchema: () => atualizarSinistroSchema,
  avaliarDocumentoSchema: () => avaliarDocumentoSchema,
  categoriaDeCnhSchema: () => categoriaDeCnhSchema,
  codigoDeRetiradaSchema: () => codigoDeRetiradaSchema,
  condutorSchema: () => condutorSchema,
  criarReservaSchema: () => criarReservaSchema,
  enviarParaAssinaturaSchema: () => enviarParaAssinaturaSchema,
  fecharReservaSchema: () => fecharReservaSchema,
  filtroDeMultaSchema: () => filtroDeMultaSchema,
  filtroDeReservaSchema: () => filtroDeReservaSchema,
  filtroDeSinistroSchema: () => filtroDeSinistroSchema,
  filtroDoGanttSchema: () => filtroDoGanttSchema,
  indicarCondutorSchema: () => indicarCondutorSchema,
  motivoDeCancelamentoSchema: () => motivoDeCancelamentoSchema,
  motivoDeTransicaoSchema: () => motivoDeTransicaoSchema,
  motivosDaOrigem: () => motivosDaOrigem,
  normalizarCnh: () => normalizarCnh,
  normalizarCodigoDeRetirada: () => normalizarCodigoDeRetirada,
  numeroDeCnhSchema: () => numeroDeCnhSchema,
  origemDeCancelamentoSchema: () => origemDeCancelamentoSchema,
  prorrogarReservaSchema: () => prorrogarReservaSchema,
  provedorDeAssinaturaSchema: () => provedorDeAssinaturaSchema,
  registrarMultaSchema: () => registrarMultaSchema,
  registrarSinistroSchema: () => registrarSinistroSchema,
  salvarPesosDeAlocacaoSchema: () => salvarPesosDeAlocacaoSchema,
  solicitarDocumentoSchema: () => solicitarDocumentoSchema,
  statusDaReservaSchema: () => statusDaReservaSchema,
  statusDeContratoSchema: () => statusDeContratoSchema,
  statusDeDocumentoSchema: () => statusDeDocumentoSchema,
  statusDeMultaSchema: () => statusDeMultaSchema,
  statusDeSinistroSchema: () => statusDeSinistroSchema,
  tipoDeDocumentoSolicitavelSchema: () => tipoDeDocumentoSolicitavelSchema,
  tipoDeSinistroSchema: () => tipoDeSinistroSchema,
  transicionarReservaSchema: () => transicionarReservaSchema,
  trocarVeiculoSchema: () => trocarVeiculoSchema
});
var statusDaReservaSchema = z.enum(STATUS_DE_RESERVA, {
  error: "status de reserva inv\xE1lido"
});
var ACOES_DE_TRANSICAO = TRANSICOES.map((transicao) => transicao.acao);
var CATEGORIAS_DE_CNH = ["B", "AB", "C", "AC", "D", "AD", "E", "AE"];
var categoriaDeCnhSchema = z.enum(CATEGORIAS_DE_CNH, {
  error: "categoria de CNH inv\xE1lida \u2014 o m\xEDnimo para conduzir \xE9 a categoria B"
});
var CNH_PATTERN = /^\d{11}$/;
var NAO_DIGITO2 = /\D/g;
function normalizarCnh(valor) {
  return valor.replace(NAO_DIGITO2, "");
}
var numeroDeCnhSchema = z.string({ error: "n\xFAmero da CNH \xE9 obrigat\xF3rio" }).transform(normalizarCnh).refine((valor) => CNH_PATTERN.test(valor), {
  error: "n\xFAmero de CNH inv\xE1lido \u2014 s\xE3o 11 d\xEDgitos"
});
var condutorSchema = z.object({
  nome: nomeSchema,
  cpf: cpfSchema,
  dataDeNascimento: dataDeNascimentoSchema,
  telefone: telefoneSchema.nullish(),
  cnhNumero: numeroDeCnhSchema,
  cnhCategoria: categoriaDeCnhSchema,
  /** Validade da CNH. Vencida na retirada é sinal DURO no score de risco. */
  cnhValidade: dataSimplesSchema2,
  /** Ano da primeira habilitação — é dele que sai o tempo de CNH exigido pela categoria. */
  cnhPrimeiraHabilitacao: dataSimplesSchema2,
  /** Exatamente UM condutor principal por reserva — o refine do conjunto garante. */
  principal: z.boolean().default(false)
});
var MAXIMO_DE_CONDUTORES = 5;
var condutoresSchema = z.array(condutorSchema).min(1, { error: "a reserva precisa de ao menos um condutor" }).max(MAXIMO_DE_CONDUTORES, {
  error: `a reserva aceita at\xE9 ${String(MAXIMO_DE_CONDUTORES)} condutores`
}).refine((condutores) => condutores.filter((condutor) => condutor.principal).length === 1, {
  error: "marque exatamente um condutor como principal"
}).refine(
  (condutores) => new Set(condutores.map((condutor) => condutor.cpf)).size === condutores.length,
  { error: "h\xE1 condutores com o mesmo CPF" }
);
var criarReservaSchema = z.object({
  /** A cotação congelada de onde a reserva nasce. Ver o cabeçalho. */
  quoteId: uuidSchema,
  /**
   * Conta do cliente — OPCIONAL, e só o painel a usa de verdade.
   *
   * No app ela sempre foi redundante (a conta vem da sessão), e a redundância
   * custava caro: enquanto o campo era obrigatório, a API precisava aceitá-lo
   * de todo mundo, e um cliente sem conta vinculada reservava em qualquer conta
   * cujo uuid conhecesse. A API agora IGNORA este campo para quem não é da
   * locadora (403 se ele divergir do claim `acc`), e o mantém só para o
   * atendente que reserva em nome do cliente no balcão.
   */
  accountId: uuidSchema.optional(),
  condutores: condutoresSchema,
  observacoes: z.string().trim().max(2e3, { error: "observa\xE7\xF5es muito longas" }).nullish()
});
var motivoDeTransicaoSchema = z.string({ error: "motivo \xE9 obrigat\xF3rio nesta transi\xE7\xE3o" }).trim().min(5, { error: "o motivo precisa dizer alguma coisa \u2014 m\xEDnimo de 5 caracteres" }).max(1e3, { error: "motivo muito longo" });
var transicionarReservaSchema = z.object({
  para: statusDaReservaSchema,
  motivo: motivoDeTransicaoSchema.nullish(),
  /**
   * O motivo CATALOGADO, quando existe um que sirva.
   *
   * Ver `motivoDeCancelamentoSchema`: texto livre não agrega, e a pergunta que
   * o gestor faz — "por que as pessoas cancelam?" — não se responde com uma
   * coluna de frases. O catálogo dá a resposta; o texto livre continua ao lado,
   * porque nenhuma lista cobre tudo.
   */
  motivoId: uuidSchema.nullish()
});
var ALFABETO_DO_CODIGO_DE_RETIRADA = "23456789BCDFGHJKMNPQRSTVWXYZ";
var TAMANHO_DO_CODIGO_DE_RETIRADA = 6;
function ehCodigoDeRetirada(valor) {
  if (valor.length !== TAMANHO_DO_CODIGO_DE_RETIRADA) {
    return false;
  }
  for (const caractere of valor) {
    if (!ALFABETO_DO_CODIGO_DE_RETIRADA.includes(caractere)) {
      return false;
    }
  }
  return true;
}
function normalizarCodigoDeRetirada(valor) {
  return valor.replace(/\s+/g, "").toUpperCase();
}
var codigoDeRetiradaSchema = z.string({ error: "c\xF3digo de retirada \xE9 obrigat\xF3rio" }).transform(normalizarCodigoDeRetirada).refine(ehCodigoDeRetirada, {
  error: `o c\xF3digo tem ${String(TAMANHO_DO_CODIGO_DE_RETIRADA)} caracteres, sem as letras e os n\xFAmeros que se confundem ao ler`
});
var CODIGO_DE_RETIRADA_NAO_E_O_NUMERO = true;
var ORIGENS_DE_CANCELAMENTO = ["CLIENTE", "EMPRESA", "AMBOS"];
var origemDeCancelamentoSchema = z.enum(ORIGENS_DE_CANCELAMENTO, {
  error: "origem de cancelamento inv\xE1lida"
});
var ROTULO_DA_ORIGEM_DE_CANCELAMENTO = {
  CLIENTE: "O cliente escolhe",
  EMPRESA: "A equipe escolhe",
  AMBOS: "Os dois podem escolher"
};
var motivoDeCancelamentoSchema = z.object({
  rotulo: z.string({ error: "o motivo precisa de um r\xF3tulo" }).trim().min(3, { error: "r\xF3tulo muito curto" }).max(80, { error: "r\xF3tulo muito longo \u2014 ele aparece numa lista de escolha" }),
  origem: origemDeCancelamentoSchema,
  ordem: z.number().int().min(0).max(100).default(0),
  ativo: z.boolean().default(true)
});
var MOTIVOS_DE_CANCELAMENTO_PADRAO = [
  { rotulo: "Mudan\xE7a de planos", origem: "CLIENTE", ordem: 0 },
  { rotulo: "Encontrou pre\xE7o melhor", origem: "CLIENTE", ordem: 1 },
  { rotulo: "Viagem cancelada ou adiada", origem: "CLIENTE", ordem: 2 },
  { rotulo: "N\xE3o conseguiu enviar os documentos", origem: "CLIENTE", ordem: 3 },
  { rotulo: "Problema no pagamento", origem: "AMBOS", ordem: 4 },
  { rotulo: "Sem ve\xEDculo dispon\xEDvel para a data", origem: "EMPRESA", ordem: 5 },
  { rotulo: "An\xE1lise de risco n\xE3o aprovada", origem: "EMPRESA", ordem: 6 },
  { rotulo: "Documenta\xE7\xE3o irregular", origem: "EMPRESA", ordem: 7 },
  { rotulo: "Outro", origem: "AMBOS", ordem: 99 }
];
function motivosDaOrigem(motivos, origem) {
  return motivos.filter((motivo) => motivo.origem === origem || motivo.origem === "AMBOS");
}
var prorrogarReservaSchema = z.object({
  novaDevolucao: instanteSchema,
  motivo: motivoDeTransicaoSchema
});
var TIPOS_DE_DOCUMENTO_SOLICITAVEL = [
  "CNH",
  "COMPROVANTE_DE_ENDERECO",
  "COMPROVANTE_DE_RENDA",
  "SELFIE",
  "CONTRATO_SOCIAL",
  "OUTRO"
];
var tipoDeDocumentoSolicitavelSchema = z.enum(TIPOS_DE_DOCUMENTO_SOLICITAVEL, {
  error: "tipo de documento inv\xE1lido"
});
var ROTULO_DO_DOCUMENTO_SOLICITAVEL = {
  CNH: "CNH",
  COMPROVANTE_DE_ENDERECO: "Comprovante de endere\xE7o",
  COMPROVANTE_DE_RENDA: "Comprovante de renda",
  SELFIE: "Selfie de verifica\xE7\xE3o",
  CONTRATO_SOCIAL: "Contrato social",
  OUTRO: "Outro documento"
};
var solicitarDocumentoSchema = z.object({
  tipo: tipoDeDocumentoSolicitavelSchema,
  instrucoes: z.string({ error: "diga ao cliente o que enviar" }).trim().min(5, { error: "as instru\xE7\xF5es precisam dizer o que enviar" }).max(1e3, { error: "instru\xE7\xF5es muito longas" })
});
var STATUS_DE_DOCUMENTO = ["SOLICITADO", "ENVIADO", "APROVADO", "REJEITADO"];
var statusDeDocumentoSchema = z.enum(STATUS_DE_DOCUMENTO, {
  error: "status de documento inv\xE1lido"
});
var ROTULO_DO_STATUS_DE_DOCUMENTO = {
  SOLICITADO: "Solicitado",
  ENVIADO: "Enviado",
  APROVADO: "Aprovado",
  REJEITADO: "Rejeitado"
};
var anexarDocumentoSchema = z.object({
  arquivoChave: chaveDeArquivoSchema
});
var avaliarDocumentoSchema = z.object({
  resultado: z.enum(["APROVADO", "REJEITADO"], { error: "avalie como aprovado ou rejeitado" }),
  observacao: z.string().trim().max(1e3, { error: "observa\xE7\xE3o muito longa" }).nullish()
}).refine(
  (avaliacao) => avaliacao.resultado === "APROVADO" || avaliacao.observacao !== null && avaliacao.observacao !== void 0 && avaliacao.observacao.trim().length >= 5,
  {
    error: "rejeitar exige dizer o que est\xE1 errado \u2014 \xE9 o que o cliente vai ler",
    path: ["observacao"]
  }
);
var alocarVeiculoSchema = z.object({
  vehicleId: uuidSchema.nullish(),
  justificativa: z.string().trim().min(5, { error: "a justificativa precisa dizer alguma coisa" }).max(1e3, { error: "justificativa muito longa" }).nullish()
}).refine(
  (entrada) => entrada.vehicleId === null || entrada.vehicleId === void 0 || entrada.justificativa !== null && entrada.justificativa !== void 0 && entrada.justificativa.length > 0,
  {
    error: "escolher o ve\xEDculo \xE0 m\xE3o exige justificativa \u2014 o algoritmo tinha outro candidato",
    path: ["justificativa"]
  }
);
var trocarVeiculoSchema = z.object({
  motivo: motivoDeTransicaoSchema,
  vehicleId: uuidSchema.nullish(),
  justificativa: z.string().trim().min(5, { error: "a justificativa precisa dizer alguma coisa" }).max(1e3, { error: "justificativa muito longa" }).nullish(),
  /** A devolução do carro que SAI da reserva. */
  devolucaoDoTrecho: vistoriaAvulsaSchema,
  /** A saída do carro que ENTRA. */
  saidaDoNovoTrecho: vistoriaAvulsaSchema
}).refine(
  (entrada) => entrada.vehicleId === null || entrada.vehicleId === void 0 || entrada.justificativa !== null && entrada.justificativa !== void 0 && entrada.justificativa.length > 0,
  {
    error: "escolher o ve\xEDculo \xE0 m\xE3o exige justificativa \u2014 o algoritmo tinha outro candidato",
    path: ["justificativa"]
  }
);
var PESO_MAXIMO_DE_FATOR = 100;
var pesoSchema = z.coerce.number({ error: "peso \xE9 obrigat\xF3rio" }).int({ error: "peso \xE9 inteiro" }).min(0, { error: "peso n\xE3o pode ser negativo" }).max(PESO_MAXIMO_DE_FATOR, { error: `o peso m\xE1ximo \xE9 ${String(PESO_MAXIMO_DE_FATOR)}` });
var salvarPesosDeAlocacaoSchema = z.object(
  Object.fromEntries(FATORES_DE_ALOCACAO.map((fator) => [fator, pesoSchema]))
).refine((pesos) => Object.values(pesos).some((peso) => peso > 0), {
  error: "com todos os pesos zerados o algoritmo n\xE3o tem crit\xE9rio nenhum"
});
var STATUS_DE_CONTRATO = [
  "GERADO",
  "ENVIADO_PARA_ASSINATURA",
  "ASSINADO",
  "CANCELADO"
];
var statusDeContratoSchema = z.enum(STATUS_DE_CONTRATO, {
  error: "status de contrato inv\xE1lido"
});
var ROTULO_DO_STATUS_DE_CONTRATO = {
  GERADO: "Gerado",
  ENVIADO_PARA_ASSINATURA: "Aguardando assinatura",
  ASSINADO: "Assinado",
  CANCELADO: "Cancelado"
};
var PROVEDORES_DE_ASSINATURA = ["CLICKSIGN", "ZAPSIGN", "DOCUSIGN", "MANUAL"];
var provedorDeAssinaturaSchema = z.enum(PROVEDORES_DE_ASSINATURA, {
  error: "provedor de assinatura inv\xE1lido"
});
var PROVEDOR_DE_ASSINATURA_PADRAO = "CLICKSIGN";
var ROTULO_DO_PROVEDOR_DE_ASSINATURA = {
  CLICKSIGN: "Clicksign",
  ZAPSIGN: "ZapSign",
  DOCUSIGN: "DocuSign",
  MANUAL: "Assinatura manual (balc\xE3o)"
};
var enviarParaAssinaturaSchema = z.object({
  provedor: provedorDeAssinaturaSchema.default(PROVEDOR_DE_ASSINATURA_PADRAO)
});
var TIPOS_DE_SINISTRO = [
  "COLISAO",
  "ROUBO",
  "FURTO",
  "TENTATIVA_DE_ROUBO",
  "VANDALISMO",
  "ALAGAMENTO",
  "OUTRO"
];
var tipoDeSinistroSchema = z.enum(TIPOS_DE_SINISTRO, {
  error: "tipo de sinistro inv\xE1lido"
});
var ROTULO_DO_TIPO_DE_SINISTRO = {
  COLISAO: "Colis\xE3o",
  ROUBO: "Roubo",
  FURTO: "Furto",
  TENTATIVA_DE_ROUBO: "Tentativa de roubo",
  VANDALISMO: "Vandalismo",
  ALAGAMENTO: "Alagamento",
  OUTRO: "Outro"
};
var STATUS_DE_SINISTRO = [
  "ABERTO",
  "SEGURADORA_ACIONADA",
  "EM_REPARO",
  "ENCERRADO"
];
var statusDeSinistroSchema = z.enum(STATUS_DE_SINISTRO, {
  error: "status de sinistro inv\xE1lido"
});
var ROTULO_DO_STATUS_DE_SINISTRO = {
  ABERTO: "Aberto",
  SEGURADORA_ACIONADA: "Seguradora acionada",
  EM_REPARO: "Em reparo",
  ENCERRADO: "Encerrado"
};
var registrarSinistroSchema = z.object({
  vehicleId: uuidSchema,
  /** Nulo quando o sinistro aconteceu fora de locação (pátio, translado). */
  reservationId: uuidSchema.nullish(),
  tipo: tipoDeSinistroSchema,
  dataDoSinistro: instanteSchema,
  descricao: z.string({ error: "descreva o que aconteceu" }).trim().min(10, { error: "a descri\xE7\xE3o precisa contar o que aconteceu \u2014 m\xEDnimo de 10 caracteres" }).max(5e3, { error: "descri\xE7\xE3o muito longa" }),
  boletimDeOcorrencia: z.string().trim().max(60, { error: "n\xFAmero de BO muito longo" }).nullish(),
  fotos: z.array(chaveDeArquivoSchema).max(30, { error: "envie at\xE9 30 fotos" }).default([]),
  seguradoraAcionada: z.boolean().default(false),
  /** Franquia da apólice a pagar, quando a seguradora for acionada. */
  franquiaDaApolice: moneySchemaNonNegative.nullish(),
  /** `true` quando a apuração atribuiu a responsabilidade ao cliente. */
  responsabilidadeDoCliente: z.boolean().default(false)
});
var atualizarSinistroSchema = z.object({
  status: statusDeSinistroSchema.optional(),
  boletimDeOcorrencia: z.string().trim().max(60).nullish(),
  seguradoraAcionada: z.boolean().optional(),
  franquiaDaApolice: moneySchemaNonNegative.nullish(),
  responsabilidadeDoCliente: z.boolean().optional(),
  observacoes: z.string().trim().max(5e3, { error: "observa\xE7\xF5es muito longas" }).nullish()
});
var STATUS_DE_MULTA = [
  "RECEBIDA",
  "VINCULADA",
  "CONDUTOR_INDICADO",
  /**
   * Multa que chegou depois do fechamento, a cobrar no cartão guardado no
   * provedor: o cliente já foi avisado e a cobrança tem data
   * (`multa-pos-devolucao.ts`). Contestar ou cancelar antes dela desmarca.
   */
  "COBRANCA_AGENDADA",
  "COBRADA",
  "CONTESTADA",
  "CANCELADA"
];
var statusDeMultaSchema = z.enum(STATUS_DE_MULTA, { error: "status de multa inv\xE1lido" });
var ROTULO_DO_STATUS_DE_MULTA = {
  RECEBIDA: "Recebida",
  VINCULADA: "Vinculada \xE0 reserva",
  CONDUTOR_INDICADO: "Condutor indicado",
  COBRANCA_AGENDADA: "Cobran\xE7a agendada (cliente avisado)",
  COBRADA: "Cobrada do cliente",
  CONTESTADA: "Contestada",
  CANCELADA: "Cancelada"
};
var registrarMultaSchema = z.object({
  vehicleId: uuidSchema,
  dataDaInfracao: instanteSchema,
  autoDeInfracao: z.string({ error: "n\xFAmero do auto de infra\xE7\xE3o \xE9 obrigat\xF3rio" }).trim().min(3, { error: "n\xFAmero do auto de infra\xE7\xE3o muito curto" }).max(40, { error: "n\xFAmero do auto de infra\xE7\xE3o muito longo" }),
  orgaoAutuador: z.string({ error: "\xF3rg\xE3o autuador \xE9 obrigat\xF3rio" }).trim().min(2, { error: "informe o \xF3rg\xE3o autuador" }).max(80, { error: "\xF3rg\xE3o autuador muito longo" }),
  descricao: z.string({ error: "descri\xE7\xE3o da infra\xE7\xE3o \xE9 obrigat\xF3ria" }).trim().min(5, { error: "descreva a infra\xE7\xE3o" }).max(1e3, { error: "descri\xE7\xE3o muito longa" }),
  valor: moneySchemaPositive,
  pontos: z.coerce.number({ error: "pontos s\xE3o obrigat\xF3rios" }).int({ error: "pontos s\xE3o inteiros" }).min(0, { error: "pontos n\xE3o podem ser negativos" }).max(7, { error: "a infra\xE7\xE3o mais grave vale 7 pontos" }),
  /** Prazo LEGAL para indicar o condutor. Perdê-lo dobra a multa para a locadora. */
  prazoParaIndicacao: dataSimplesSchema2
});
var indicarCondutorSchema = z.object({
  condutorId: uuidSchema
});
var fecharReservaSchema = z.object({
  avarias: z.array(
    z.object({
      /** Referência à avaria NOVA apontada pelo comparativo. */
      descricao: z.string({ error: "descreva a avaria or\xE7ada" }).trim().min(3, { error: "descreva a avaria or\xE7ada" }).max(500, { error: "descri\xE7\xE3o muito longa" }),
      valor: moneySchemaPositive
    })
  ).max(100, { error: "h\xE1 avarias demais num fechamento s\xF3" }).default([]),
  /** Ids das multas vinculadas que entram nesta cobrança. */
  multaIds: z.array(uuidSchema).max(50, { error: "h\xE1 multas demais num fechamento s\xF3" }).default([]),
  /**
   * Ids dos SINISTROS da reserva com responsabilidade atribuída ao cliente —
   * a franquia da apólice de cada um entra na conta, sob o mesmo teto de
   * proteção das avarias (Adendo D: "cobrança gerada").
   */
  sinistroIds: z.array(uuidSchema).max(20, { error: "h\xE1 sinistros demais num fechamento s\xF3" }).default([]),
  observacoes: z.string().trim().max(2e3, { error: "observa\xE7\xF5es muito longas" }).nullish()
});
var filtroDeReservaSchema = paginacaoSchema.extend({
  status: statusDaReservaSchema.optional(),
  /** Só as vivas (não terminais) — o filtro padrão da lista operacional. */
  apenasVivas: z.stringbool().optional(),
  categoryId: uuidSchema.optional(),
  accountId: uuidSchema.optional(),
  vehicleId: uuidSchema.optional(),
  /** Reservas cujo período TOCA a janela [de, ate]. */
  retiradaDe: instanteSchema.optional(),
  retiradaAte: instanteSchema.optional()
});
var filtroDeMultaSchema = paginacaoSchema.extend({
  status: statusDeMultaSchema.optional(),
  vehicleId: uuidSchema.optional(),
  reservationId: uuidSchema.optional(),
  /** Multas cujo prazo de indicação vence até esta data — a lista de urgência. */
  prazoAte: dataSimplesSchema2.optional()
});
var filtroDeSinistroSchema = paginacaoSchema.extend({
  status: statusDeSinistroSchema.optional(),
  tipo: tipoDeSinistroSchema.optional(),
  vehicleId: uuidSchema.optional(),
  /** Os sinistros de UMA reserva — o fechamento escolhe entre eles. */
  reservationId: uuidSchema.optional()
});
var JANELA_MAXIMA_DO_GANTT_EM_DIAS = 92;
var filtroDoGanttSchema = z.object({
  de: instanteSchema,
  ate: instanteSchema,
  categoryId: uuidSchema.optional(),
  locationId: uuidSchema.optional()
}).refine(
  (filtro) => !(filtro.de instanceof Date) || !(filtro.ate instanceof Date) || filtro.ate.getTime() > filtro.de.getTime() && filtro.ate.getTime() - filtro.de.getTime() <= JANELA_MAXIMA_DO_GANTT_EM_DIAS * 864e5,
  {
    error: `a janela do calend\xE1rio vai de 1 dia a ${String(JANELA_MAXIMA_DO_GANTT_EM_DIAS)} dias`,
    path: ["ate"]
  }
);

// src/http.ts
var TENANT_HEADER = "x-tenant-slug";
var IDEMPOTENCY_HEADER = "Idempotency-Key";
var TRACE_HEADER = "x-request-id";
function buildUrl(baseUrl, path) {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const base = baseUrl.replace(/\/+$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return `${base}${suffix}`;
}
function buildHeaders(options) {
  const headers = { accept: "application/json" };
  if (options.hasBody) {
    headers["content-type"] = "application/json";
  }
  headers[TENANT_HEADER] = options.tenantSlug;
  if (options.accessToken !== null && options.accessToken !== void 0) {
    headers.authorization = `Bearer ${options.accessToken}`;
  }
  if (options.idempotencyKey !== void 0) {
    headers[IDEMPOTENCY_HEADER.toLowerCase()] = options.idempotencyKey;
  }
  for (const [name, value] of Object.entries(options.headers ?? {})) {
    headers[name.toLowerCase()] = value;
  }
  return headers;
}

// src/datas.ts
var SEM_VALOR = "\u2014";
var DIA_SIMPLES = /^(\d{4})-(\d{2})-(\d{2})$/;
function interpretar(valor) {
  if (valor === null || valor === void 0 || valor.length === 0) {
    return null;
  }
  const partes = DIA_SIMPLES.exec(valor);
  if (partes !== null) {
    const [, ano, mes, dia] = partes;
    if (ano === void 0 || mes === void 0 || dia === void 0) {
      return null;
    }
    const data = new Date(Date.UTC(Number(ano), Number(mes) - 1, Number(dia)));
    const existeNoCalendario = data.getUTCFullYear() === Number(ano) && data.getUTCMonth() === Number(mes) - 1 && data.getUTCDate() === Number(dia);
    return existeNoCalendario ? { tipo: "dia", ano, mes, dia } : null;
  }
  if (!ISO_INSTANT_PATTERN.test(valor)) {
    return null;
  }
  const instante = new Date(valor);
  return Number.isNaN(instante.getTime()) ? null : { tipo: "instante", instante };
}
function formatarDataHora(valor) {
  const data = interpretar(valor);
  if (data === null) {
    return SEM_VALOR;
  }
  return data.tipo === "dia" ? `${data.dia}/${data.mes}/${data.ano} 00:00` : formatDateTimeBR(data.instante);
}
function formatarData(valor) {
  const data = interpretar(valor);
  if (data === null) {
    return SEM_VALOR;
  }
  return data.tipo === "dia" ? `${data.dia}/${data.mes}/${data.ano}` : formatDateBR(data.instante);
}

// src/dinheiro.ts
function formatarMoedaDoWire(valor) {
  if (valor === null || valor === void 0 || valor.length === 0) {
    return SEM_VALOR;
  }
  try {
    return formatBRL(fromWire(valor));
  } catch {
    return SEM_VALOR;
  }
}
function paraCampoDeReais(valor) {
  if (valor === null || valor === void 0 || valor.length === 0) {
    return "";
  }
  try {
    return formatBRL(fromWire(valor)).replace("R$ ", "").trim();
  } catch {
    return "";
  }
}
function deCampoDeReaisParaWire(texto) {
  const limpo = texto.trim();
  if (limpo.length === 0) {
    return null;
  }
  try {
    return toWire(fromReais(limpo));
  } catch {
    return null;
  }
}

// src/mascaras.ts
var NAO_DIGITO3 = /\D/g;
var NAO_ALFANUMERICO2 = /[^0-9A-Za-z]/g;
var CEP_TAMANHO = 8;
function mascararCpfProgressivo(valor) {
  const digitos = normalizarCpf(valor).replace(NAO_DIGITO3, "").slice(0, CPF_TAMANHO);
  const partes = [];
  partes.push(digitos.slice(0, 3));
  if (digitos.length > 3) partes.push(`.${digitos.slice(3, 6)}`);
  if (digitos.length > 6) partes.push(`.${digitos.slice(6, 9)}`);
  if (digitos.length > 9) partes.push(`-${digitos.slice(9, 11)}`);
  return partes.join("");
}
function mascararCnpjProgressivo(valor) {
  const bruto = normalizarCnpj(valor).replace(NAO_ALFANUMERICO2, "").toUpperCase();
  const base = bruto.slice(0, 12);
  const verificadores = bruto.slice(12, CNPJ_TAMANHO).replace(NAO_DIGITO3, "");
  const caracteres = `${base}${verificadores}`;
  const partes = [];
  partes.push(caracteres.slice(0, 2));
  if (caracteres.length > 2) partes.push(`.${caracteres.slice(2, 5)}`);
  if (caracteres.length > 5) partes.push(`.${caracteres.slice(5, 8)}`);
  if (caracteres.length > 8) partes.push(`/${caracteres.slice(8, 12)}`);
  if (caracteres.length > 12) partes.push(`-${caracteres.slice(12, 14)}`);
  return partes.join("");
}
function mascararTelefoneProgressivo(valor) {
  const digitos = valor.replace(NAO_DIGITO3, "").slice(0, 11);
  if (digitos.length === 0) {
    return "";
  }
  if (digitos.length <= 2) {
    return `(${digitos}`;
  }
  const ddd = digitos.slice(0, 2);
  const assinante = digitos.slice(2);
  const corte = assinante.startsWith("9") ? 5 : 4;
  if (assinante.length <= corte) {
    return `(${ddd}) ${assinante}`;
  }
  return `(${ddd}) ${assinante.slice(0, corte)}-${assinante.slice(corte)}`;
}
function mascararCepProgressivo(valor) {
  const digitos = normalizarCep(valor).slice(0, CEP_TAMANHO);
  if (digitos.length <= 5) {
    return digitos;
  }
  return `${digitos.slice(0, 5)}-${digitos.slice(5)}`;
}
function apenasDigitos(valor) {
  return valor.replace(NAO_DIGITO3, "");
}
function apenasAlfanumericos(valor) {
  return valor.replace(NAO_ALFANUMERICO2, "").toUpperCase();
}
function formatarTelefoneDeExibicao(valor) {
  if (valor === null || valor === void 0 || valor.length === 0) {
    return SEM_VALOR;
  }
  return formatarTelefone(valor);
}
function formatarCepDeExibicao(valor) {
  if (valor === null || valor === void 0 || valor.length === 0) {
    return SEM_VALOR;
  }
  return formatarCep(valor);
}
function formatarDocumentoDeExibicao(tipo, valor) {
  if (valor === null || valor === void 0 || valor.length === 0) {
    return SEM_VALOR;
  }
  return tipo === "PF" ? formatarCpf(valor) : formatarCnpj(valor);
}
var ESCALA_MINIMA_POSSIVEL = 0;
var ESCALA_MAXIMA_POSSIVEL = 10;
var ESCALA_PADRAO = { minima: 1, maxima: 5 };
var notaSchema = z.coerce.number({ error: "a nota \xE9 obrigat\xF3ria" }).int({ error: "a nota \xE9 um n\xFAmero inteiro" }).min(ESCALA_MINIMA_POSSIVEL).max(ESCALA_MAXIMA_POSSIVEL);
var FAIXAS_DE_NPS = ["PROMOTOR", "PASSIVO", "DETRATOR"];
var ROTULO_DA_FAIXA_DE_NPS = {
  PROMOTOR: "Promotor",
  PASSIVO: "Passivo",
  DETRATOR: "Detrator"
};
function limiaresPadrao(minima, maxima) {
  const pontos = maxima - minima + 1;
  if (pontos <= 6) {
    return { minimaDePromotor: maxima, minimaDePassivo: Math.max(minima, maxima - 1) };
  }
  return {
    minimaDePromotor: maxima - 1,
    minimaDePassivo: Math.max(minima, maxima - 3)
  };
}
function faixaDaNota(nota, limiares) {
  if (nota >= limiares.minimaDePromotor) {
    return "PROMOTOR";
  }
  if (nota >= limiares.minimaDePassivo) {
    return "PASSIVO";
  }
  return "DETRATOR";
}
function calcularNps(notas, limiares) {
  if (notas.length === 0) {
    return null;
  }
  let promotores = 0;
  let detratores = 0;
  for (const nota of notas) {
    const faixa = faixaDaNota(nota, limiares);
    if (faixa === "PROMOTOR") {
      promotores += 1;
    } else if (faixa === "DETRATOR") {
      detratores += 1;
    }
  }
  return Math.round((promotores - detratores) / notas.length * 100);
}
var ZONAS_DE_NPS = ["CRITICA", "MELHORIA", "QUALIDADE", "EXCELENCIA"];
var ROTULO_DA_ZONA_DE_NPS = {
  CRITICA: "Zona cr\xEDtica",
  MELHORIA: "Zona de melhoria",
  QUALIDADE: "Zona de qualidade",
  EXCELENCIA: "Zona de excel\xEAncia"
};
function zonaDoNps(nps) {
  if (nps >= 75) {
    return "EXCELENCIA";
  }
  if (nps >= 50) {
    return "QUALIDADE";
  }
  if (nps >= 0) {
    return "MELHORIA";
  }
  return "CRITICA";
}
var TAXA_DE_RESPOSTA_MINIMA_CONFIAVEL = 20;
function taxaDeResposta(respondidas, enviadas) {
  if (enviadas <= 0) {
    return null;
  }
  return Math.round(respondidas / enviadas * 100);
}
function npsConfiavel(taxa) {
  return taxa !== null && taxa >= TAXA_DE_RESPOSTA_MINIMA_CONFIAVEL;
}
var rotuloDeCriterioSchema = z.string({ error: "o crit\xE9rio precisa de um r\xF3tulo" }).trim().min(2, { error: "r\xF3tulo muito curto" }).max(60, { error: "r\xF3tulo muito longo \u2014 ele aparece numa coluna de tabela" });
var criterioDaPesquisaSchema = z.object({
  /** Estável entre edições: é por ele que a série histórica agrupa. */
  id: uuidSchema,
  rotulo: rotuloDeCriterioSchema,
  ordem: z.number().int().min(0).max(50),
  ativo: z.boolean().default(true)
});
var MAXIMO_DE_CRITERIOS = 10;
var camposDaConfiguracaoDaPesquisa = {
  ativa: z.boolean().default(false),
  criterios: z.array(criterioDaPesquisaSchema).min(1, { error: "a pesquisa precisa de ao menos um crit\xE9rio" }).max(MAXIMO_DE_CRITERIOS, {
    error: `a pesquisa aceita at\xE9 ${String(MAXIMO_DE_CRITERIOS)} crit\xE9rios \u2014 acima disso a taxa de resposta despenca`
  }).refine((lista) => new Set(lista.map((c) => c.id)).size === lista.length, {
    error: "h\xE1 crit\xE9rios com o mesmo id"
  }),
  escalaMinima: notaSchema.default(ESCALA_PADRAO.minima),
  escalaMaxima: notaSchema.default(ESCALA_PADRAO.maxima),
  /** O texto embaixo da ponta baixa da escala. */
  rotuloDaNotaMinima: z.string().trim().max(120).default("Que pena! Lamentamos a experi\xEAncia."),
  rotuloDaNotaMaxima: z.string().trim().max(120).default("Que \xF3timo! Volte sempre."),
  minimaDePromotor: notaSchema.optional(),
  minimaDePassivo: notaSchema.optional(),
  /**
   * A nota a partir da qual a pessoa é levada à review pública.
   *
   * É a alavanca comercial inteira, e é SEPARADA do limiar de promotor de
   * propósito: dá para querer mandar o passivo também (mais volume, nota
   * média menor), ou só o promotor (menos volume, nota melhor). Amarrá-la ao
   * NPS tiraria essa escolha do gestor.
   */
  notaMinimaParaReview: notaSchema.optional(),
  /** O link do Google Meu Negócio. Vazio = ninguém é redirecionado. */
  urlDeReview: z.url({ error: "a URL de review precisa ser um endere\xE7o v\xE1lido" }).nullish(),
  mensagemDeRedirecionamento: z.string().trim().max(300).default("Obrigado! Que tal contar isso para mais gente?"),
  /** Para onde vai o alerta de nota baixa. Vazio = só fica na fila do painel. */
  emailParaFeedbackNegativo: z.string().trim().max(320).nullish()
};
function aplicarRegrasDaConfiguracao(schema) {
  return schema.refine((config) => config.escalaMaxima > config.escalaMinima, {
    error: "a nota m\xE1xima precisa ser maior que a m\xEDnima",
    path: ["escalaMaxima"]
  }).refine(
    (config) => config.minimaDePromotor === void 0 || config.minimaDePromotor >= config.escalaMinima && config.minimaDePromotor <= config.escalaMaxima,
    { error: "o limiar de promotor est\xE1 fora da escala", path: ["minimaDePromotor"] }
  ).refine(
    (config) => config.minimaDePassivo === void 0 || config.minimaDePromotor === void 0 || config.minimaDePassivo <= config.minimaDePromotor,
    {
      error: "o limiar de passivo n\xE3o pode ser maior que o de promotor",
      path: ["minimaDePassivo"]
    }
  ).refine((config) => config.urlDeReview == null || config.notaMinimaParaReview !== void 0, {
    // Uma URL sem limiar redirecionaria TODO MUNDO, inclusive quem deu a
    // nota mínima — o oposto exato do que o desvio existe para fazer.
    error: "informe a nota m\xEDnima para redirecionar \xE0 review",
    path: ["notaMinimaParaReview"]
  }).refine(
    (config) => config.notaMinimaParaReview === void 0 || config.notaMinimaParaReview >= config.escalaMinima && config.notaMinimaParaReview <= config.escalaMaxima,
    {
      // ⚠️ FORA DA ESCALA, O DESVIO MORRE EM SILÊNCIO.
      //
      // `notaSchema` limita ao intervalo ABSOLUTO (0 a 10), não à escala do
      // tenant. Um limiar 8 numa pesquisa de 1 a 5 é aceito, salvo e nunca
      // alcançado: `mereceReview` exige que TODAS as notas cheguem lá, e a
      // maior nota possível é 5. A tela mostra a configuração como válida, o
      // gestor espera avaliações públicas, e nenhuma sai — sem erro nenhum
      // em lugar nenhum.
      //
      // `minimaDePromotor` já tinha esta conferência desde o começo; o limiar
      // comercial não tinha, e é o que dá dinheiro.
      error: "a nota m\xEDnima para review est\xE1 fora da escala",
      path: ["notaMinimaParaReview"]
    }
  );
}
var configuracaoDaPesquisaSchema = aplicarRegrasDaConfiguracao(
  z.object(camposDaConfiguracaoDaPesquisa)
);
function limiaresDaConfiguracao(config) {
  const padrao = limiaresPadrao(config.escalaMinima, config.escalaMaxima);
  return {
    minimaDePromotor: config.minimaDePromotor ?? padrao.minimaDePromotor,
    minimaDePassivo: config.minimaDePassivo ?? padrao.minimaDePassivo
  };
}
function mereceReview(notas, config) {
  if (config.urlDeReview == null || config.notaMinimaParaReview === void 0) {
    return false;
  }
  if (notas.length === 0) {
    return false;
  }
  return notas.every((nota) => nota >= (config.notaMinimaParaReview ?? Number.POSITIVE_INFINITY));
}
var notaDeCriterioSchema = z.object({
  criterioId: uuidSchema,
  nota: notaSchema
});
var responderPesquisaSchema = z.object({
  notas: z.array(notaDeCriterioSchema).min(1, { error: "responda ao menos um crit\xE9rio" }).max(MAXIMO_DE_CRITERIOS).refine((lista) => new Set(lista.map((n) => n.criterioId)).size === lista.length, {
    error: "o mesmo crit\xE9rio foi respondido duas vezes"
  }),
  /** O que a pessoa escreveu. É onde mora o motivo de verdade da nota baixa. */
  comentario: z.string().trim().max(2e3).nullish()
});
var pesquisaParaResponderSchema = z.object({
  criterios: z.array(z.object({ id: uuidSchema, rotulo: z.string() })),
  escalaMinima: z.number().int(),
  escalaMaxima: z.number().int(),
  rotuloDaNotaMinima: z.string(),
  rotuloDaNotaMaxima: z.string(),
  /** `true` quando esta pesquisa já foi respondida — a tela agradece e para. */
  respondida: z.boolean()
});
var resultadoDaRespostaSchema = z.object({
  /** Preenchido só quando a nota mereceu — e só se houver URL configurada. */
  urlDeReview: z.url().nullable(),
  mensagem: z.string()
});
var agregadoDoCriterioSchema = z.object({
  criterioId: uuidSchema,
  /** O rótulo CONGELADO na resposta — ver o cabeçalho. */
  rotulo: z.string(),
  respostas: z.number().int().nonnegative(),
  promotores: z.number().int().nonnegative(),
  passivos: z.number().int().nonnegative(),
  detratores: z.number().int().nonnegative(),
  /** `null` sem resposta nenhuma. Zero é NPS legítimo; ausência não é. */
  nps: z.number().int().min(-100).max(100).nullable(),
  /** Média simples, para quem prefere ler nota a ler NPS. */
  media: z.number().nullable()
});
var relatorioDaPesquisaSchema = z.object({
  /** Quantas locações fecharam na janela — o denominador da taxa. */
  reservasElegiveis: z.number().int().nonnegative(),
  pesquisasEnviadas: z.number().int().nonnegative(),
  pesquisasRespondidas: z.number().int().nonnegative(),
  /** `null` sem envio. */
  taxaDeResposta: z.number().int().min(0).max(100).nullable(),
  /** ⚠️ `false` manda a tela exibir o agregado COM ressalva. */
  confiavel: z.boolean(),
  npsGeral: z.number().int().min(-100).max(100).nullable(),
  porCriterio: z.array(agregadoDoCriterioSchema),
  /** O mesmo recorte, por ponto de retirada. É o que vira ordem de serviço. */
  porLocal: z.array(
    z.object({
      localId: uuidSchema.nullable(),
      local: nomeSchema.nullable(),
      respostas: z.number().int().nonnegative(),
      nps: z.number().int().min(-100).max(100).nullable()
    })
  )
});
function agregarCriterio(criterioId, rotulo, notas, limiares) {
  let promotores = 0;
  let passivos = 0;
  let detratores = 0;
  let soma = 0;
  for (const nota of notas) {
    soma += nota;
    const faixa = faixaDaNota(nota, limiares);
    if (faixa === "PROMOTOR") {
      promotores += 1;
    } else if (faixa === "PASSIVO") {
      passivos += 1;
    } else {
      detratores += 1;
    }
  }
  return {
    criterioId,
    rotulo,
    respostas: notas.length,
    promotores,
    passivos,
    detratores,
    nps: calcularNps(notas, limiares),
    // Uma casa decimal: a média é para comparar critérios entre si, e duas
    // casas sugeririam uma precisão que a amostra não tem.
    media: notas.length === 0 ? null : Math.round(soma / notas.length * 10) / 10
  };
}
var CANAIS_DE_NOTIFICACAO = ["PUSH", "EMAIL", "WHATSAPP"];
var canalDeNotificacaoSchema = z.enum(CANAIS_DE_NOTIFICACAO, {
  error: "canal de notifica\xE7\xE3o inv\xE1lido"
});
var ROTULO_DO_CANAL = {
  PUSH: "Notifica\xE7\xE3o no app",
  EMAIL: "E-mail",
  WHATSAPP: "WhatsApp"
};
var AVISO_DO_STATUS = {
  AGUARDANDO_PAGAMENTO: {
    titulo: "Falta o pagamento",
    corpo: "Sua reserva n\xBA {{numero}} est\xE1 reservada at\xE9 o pagamento ser confirmado.",
    canais: ["PUSH", "EMAIL", "WHATSAPP"],
    exigeAcao: true
  },
  AGUARDANDO_APROVACAO: {
    titulo: "Recebemos sua reserva",
    corpo: "A reserva n\xBA {{numero}} est\xE1 em an\xE1lise. Avisamos assim que tivermos a resposta.",
    canais: ["PUSH", "EMAIL"],
    exigeAcao: false
  },
  APROVADA: {
    titulo: "Reserva aprovada",
    corpo: "A reserva n\xBA {{numero}} foi aprovada.",
    canais: ["PUSH", "EMAIL", "WHATSAPP"],
    exigeAcao: false
  },
  VEICULO_ALOCADO: {
    // Sem push: o cliente aluga CATEGORIA, e qual placa saiu é decisão da
    // operação. Vibrar o telefone para anunciar um dado que ele nem escolheu é
    // ruído — e o dado em si não pode ir no texto (ver o cabeçalho).
    titulo: "Tudo pronto para a retirada",
    corpo: "Est\xE1 tudo separado para a sua reserva n\xBA {{numero}}.",
    canais: ["EMAIL"],
    exigeAcao: false
  },
  RETIRADA_PENDENTE: {
    titulo: "Sua retirada \xE9 hoje",
    corpo: "Abra o app para ver o c\xF3digo de retirada da reserva n\xBA {{numero}}.",
    canais: ["PUSH", "EMAIL", "WHATSAPP"],
    exigeAcao: true
  },
  EM_ANDAMENTO: {
    titulo: "Boa viagem",
    corpo: "A loca\xE7\xE3o da reserva n\xBA {{numero}} come\xE7ou.",
    canais: ["PUSH", "EMAIL"],
    exigeAcao: false
  },
  DEVOLUCAO_PENDENTE: {
    titulo: "Devolu\xE7\xE3o prevista para hoje",
    corpo: "A reserva n\xBA {{numero}} tem devolu\xE7\xE3o prevista. Precisa de mais tempo? Fale com a gente.",
    canais: ["PUSH", "EMAIL", "WHATSAPP"],
    exigeAcao: true
  },
  FINALIZADA: {
    titulo: "Loca\xE7\xE3o encerrada",
    corpo: "O fechamento da reserva n\xBA {{numero}} est\xE1 dispon\xEDvel no app.",
    canais: ["PUSH", "EMAIL"],
    exigeAcao: false
  },
  RECUSADA: {
    titulo: "N\xE3o foi poss\xEDvel seguir",
    corpo: "A reserva n\xBA {{numero}} n\xE3o p\xF4de ser aprovada. Enviamos os detalhes por e-mail.",
    canais: ["PUSH", "EMAIL"],
    exigeAcao: false
  },
  CANCELADA_CLIENTE: {
    titulo: "Reserva cancelada",
    corpo: "A reserva n\xBA {{numero}} foi cancelada. O comprovante est\xE1 no e-mail.",
    canais: ["PUSH", "EMAIL"],
    exigeAcao: false
  },
  CANCELADA_EMPRESA: {
    titulo: "Precisamos cancelar sua reserva",
    corpo: "A reserva n\xBA {{numero}} foi cancelada por n\xF3s. Explicamos tudo no e-mail.",
    canais: ["PUSH", "EMAIL", "WHATSAPP"],
    exigeAcao: false
  },
  NO_SHOW: {
    titulo: "Reserva encerrada por n\xE3o comparecimento",
    corpo: "A reserva n\xBA {{numero}} foi encerrada. Os detalhes est\xE3o no e-mail.",
    canais: ["EMAIL"],
    exigeAcao: false
  },
  EXPIRADA: {
    titulo: "Sua reserva expirou",
    corpo: "A reserva n\xBA {{numero}} expirou antes da confirma\xE7\xE3o. D\xE1 para refazer no app.",
    canais: ["PUSH", "EMAIL"],
    exigeAcao: false
  }
};
var STATUS_QUE_NOTIFICAM = STATUS_DE_RESERVA.filter(
  (status) => AVISO_DO_STATUS[status] !== void 0
);
var MARCADOR_DE_NUMERO = "{{numero}}";
function textoDoAviso(corpo, numeroDaReserva) {
  return corpo.split(MARCADOR_DE_NUMERO).join(String(numeroDaReserva));
}
var ESQUEMA_DO_APP = "locaveri";
function linkDaReservaNoApp(reservaId) {
  return `${ESQUEMA_DO_APP}:///reservas/${encodeURIComponent(reservaId)}`;
}
var AVISOS_AVULSOS = {
  PESQUISA: {
    titulo: "Como foi sua loca\xE7\xE3o?",
    corpo: `Sua loca\xE7\xE3o n\xBA ${MARCADOR_DE_NUMERO} foi encerrada. Leva menos de um minuto para contar como foi.`,
    canais: ["EMAIL", "WHATSAPP"],
    exigeAcao: false
  },
  /**
   * O pedido de autorização da análise cadastral (Mira Score).
   *
   * COM push, ao contrário da pesquisa: aqui a reserva está PARADA esperando a
   * pessoa — sem a autorização, o gestor decide sem a análise. O texto não diz
   * "crédito" de propósito: na tela bloqueada, "análise de crédito" conta a quem
   * estiver ao lado mais do que a pessoa escolheu contar.
   *
   * A unicidade (ver `NotificacoesService.avisarAvulso`) vem da análise: o aviso
   * sai uma vez, na passagem de SOLICITANDO para AGUARDANDO_CONSENTIMENTO.
   */
  ANALISE_CADASTRAL: {
    titulo: "Autorize a an\xE1lise do seu cadastro",
    corpo: `Para seguir com a reserva n\xBA ${MARCADOR_DE_NUMERO}, a locadora pede uma an\xE1lise r\xE1pida do seu cadastro. Abra o app para autorizar.`,
    canais: ["PUSH", "EMAIL"],
    exigeAcao: true
  },
  /**
   * O AVISO ANTES da cobrança de multa no cartão guardado no provedor
   * (`multa-pos-devolucao.ts`) — a cláusula do contrato promete que o cliente
   * é avisado, e a cobrança só acontece dias depois deste aviso. Sem valor e
   * sem o auto no texto (regra da tela bloqueada): os detalhes estão no app e no
   * e-mail da locadora.
   */
  MULTA_A_COBRAR: {
    titulo: "Cobran\xE7a de multa de tr\xE2nsito",
    corpo: `Uma multa de tr\xE2nsito da reserva n\xBA ${MARCADOR_DE_NUMERO} ser\xE1 cobrada no cart\xE3o usado na reserva. Em caso de d\xFAvida, fale com a locadora.`,
    canais: ["PUSH", "EMAIL"],
    exigeAcao: false
  }
};
var AVISO_DA_PESQUISA = AVISOS_AVULSOS.PESQUISA;
var TODOS_OS_AVISOS = {
  ...AVISO_DO_STATUS,
  ...AVISOS_AVULSOS
};
var CAMINHO_DA_PESQUISA = "/pesquisa";
function linkDaPesquisa(baseDoSite, token) {
  const base = baseDoSite.endsWith("/") ? baseDoSite.slice(0, -1) : baseDoSite;
  return `${base}${CAMINHO_DA_PESQUISA}/${encodeURIComponent(token)}`;
}
var PLATAFORMAS_DE_PUSH = ["IOS", "ANDROID"];
var TOKEN_DE_PUSH_PATTERN = /^Expo(nent)?PushToken\[[A-Za-z0-9_-]{1,200}\]$/;
var tokenDePushSchema = z.string({ error: "token de push \xE9 obrigat\xF3rio" }).trim().max(256, { error: "token de push implaus\xEDvel" }).refine((valor) => TOKEN_DE_PUSH_PATTERN.test(valor), {
  error: "token de push fora do formato do Expo"
});
var registrarAparelhoSchema = z.object({
  token: tokenDePushSchema,
  plataforma: z.enum(PLATAFORMAS_DE_PUSH, { error: "plataforma inv\xE1lida" }),
  instalacaoId: z.string({ error: "identificador da instala\xE7\xE3o \xE9 obrigat\xF3rio" }).trim().min(8, { error: "identificador da instala\xE7\xE3o muito curto" }).max(128, { error: "identificador da instala\xE7\xE3o muito longo" }),
  /** Para o corpo do aviso sair no idioma do aparelho quando houver tradução. */
  idioma: z.string().trim().max(16).optional()
});
var esquecerAparelhoSchema = z.object({
  instalacaoId: registrarAparelhoSchema.shape.instalacaoId
});
var preferenciaDeNotificacaoSchema = z.object({
  canaisDesligados: z.array(canalDeNotificacaoSchema).max(CANAIS_DE_NOTIFICACAO.length).refine((lista) => new Set(lista).size === lista.length, { error: "canal repetido" }).default([])
});
function canaisEfetivos(aviso, preferencia) {
  const desligados = new Set(preferencia.canaisDesligados);
  return aviso.canais.filter((canal) => !desligados.has(canal));
}
var PARAMETROS_DA_PONTE = {
  /** Slug da categoria, nunca uuid — é o mesmo identificador que a vitrine publica. */
  categoria: "categoria",
  /** `AAAA-MM-DD` no fuso do ponto de retirada. */
  retirada: "retirada",
  devolucao: "devolucao",
  /** `HH:MM` — hora de parede da retirada. */
  horaDeRetirada: "hr",
  horaDeDevolucao: "hd",
  retiradaLocalId: "retiradaLocal",
  devolucaoLocalId: "devolucaoLocal",
  /** REPETIDO, um por opcional: `extra=<uuid>:<quantidade>`. Ver `lerExtras`. */
  extra: "extra",
  cupom: "cupom"
};
var CAMINHO_DA_PONTE = "/reservar/datas";
var PRE_PREENCHIMENTO_VAZIO = {
  categoriaSlug: null,
  retirada: null,
  devolucao: null,
  horaDeRetirada: null,
  horaDeDevolucao: null,
  retiradaLocalId: null,
  devolucaoLocalId: null,
  extras: [],
  cupom: null
};
function temPrePreenchimento(dados) {
  return dados.categoriaSlug !== null || dados.retirada !== null || dados.devolucao !== null || dados.retiradaLocalId !== null || dados.devolucaoLocalId !== null || dados.extras.length > 0 || dados.cupom !== null;
}
var ALFABETO_DO_SLUG = /^[a-z0-9-]+$/;
function ehSlug(valor) {
  return ALFABETO_DO_SLUG.test(valor) && !valor.startsWith("-") && !valor.endsWith("-") && !valor.includes("--");
}
var slugSchema = z.string().min(1).max(120).refine(ehSlug);
var HORA_PATTERN2 = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
var horaSchema2 = z.string().regex(HORA_PATTERN2);
var uuidDaPonteSchema = z.uuid();
var QUANTIDADE_MAXIMA_DE_EXTRA = 99;
var SO_DIGITOS = /^\d{1,2}$/;
function partirExtra(bruto) {
  const pedacos = bruto.split(":");
  const [id, quantidadeBruta] = pedacos;
  if (id === void 0 || pedacos.length > 2) {
    return null;
  }
  if (quantidadeBruta === void 0) {
    return { id, quantidade: 1 };
  }
  if (!SO_DIGITOS.test(quantidadeBruta)) {
    return null;
  }
  return { id, quantidade: Number.parseInt(quantidadeBruta, 10) };
}
var MAXIMO_DE_EXTRAS_NA_PONTE = 20;
function temGetAll(consulta) {
  return typeof consulta.getAll === "function";
}
function valoresDe(consulta, nome) {
  if (temGetAll(consulta)) {
    return consulta.getAll(nome);
  }
  const bruto = consulta[nome];
  if (bruto === void 0) {
    return [];
  }
  return typeof bruto === "string" ? [bruto] : bruto;
}
function primeiro(consulta, nome) {
  const encontrado = valoresDe(consulta, nome)[0];
  if (encontrado === void 0) {
    return null;
  }
  const limpo = encontrado.trim();
  return limpo === "" ? null : limpo;
}
function validado(valor, schema) {
  if (valor === null) {
    return null;
  }
  const resultado = schema.safeParse(valor);
  return resultado.success ? resultado.data : null;
}
function lerExtras(consulta) {
  const brutos = valoresDe(consulta, PARAMETROS_DA_PONTE.extra).slice(0, MAXIMO_DE_EXTRAS_NA_PONTE);
  const vistos = /* @__PURE__ */ new Set();
  const extras = [];
  for (const bruto of brutos) {
    const partido = partirExtra(bruto.trim());
    if (partido === null || !uuidDaPonteSchema.safeParse(partido.id).success) {
      continue;
    }
    const identificador = partido.id.toLowerCase();
    if (vistos.has(identificador)) {
      continue;
    }
    if (partido.quantidade < 1 || partido.quantidade > QUANTIDADE_MAXIMA_DE_EXTRA) {
      continue;
    }
    vistos.add(identificador);
    extras.push({ extraId: identificador, quantidade: partido.quantidade });
  }
  return extras;
}
function lerPrePreenchimento(consulta) {
  const retirada = validado(primeiro(consulta, PARAMETROS_DA_PONTE.retirada), dataSimplesSchema2);
  const devolucao = validado(primeiro(consulta, PARAMETROS_DA_PONTE.devolucao), dataSimplesSchema2);
  const periodoValido = retirada !== null && devolucao !== null && retirada <= devolucao;
  return {
    categoriaSlug: validado(primeiro(consulta, PARAMETROS_DA_PONTE.categoria), slugSchema),
    retirada: periodoValido ? retirada : null,
    devolucao: periodoValido ? devolucao : null,
    horaDeRetirada: validado(primeiro(consulta, PARAMETROS_DA_PONTE.horaDeRetirada), horaSchema2),
    horaDeDevolucao: validado(primeiro(consulta, PARAMETROS_DA_PONTE.horaDeDevolucao), horaSchema2),
    retiradaLocalId: validado(
      primeiro(consulta, PARAMETROS_DA_PONTE.retiradaLocalId),
      uuidDaPonteSchema
    ),
    devolucaoLocalId: validado(
      primeiro(consulta, PARAMETROS_DA_PONTE.devolucaoLocalId),
      uuidDaPonteSchema
    ),
    extras: lerExtras(consulta),
    cupom: validado(primeiro(consulta, PARAMETROS_DA_PONTE.cupom), codigoDeCupomSchema)
  };
}
function montarConsultaDaPonte(pedido) {
  const partes = [];
  function acrescentar(nome, valor) {
    if (valor !== null && valor !== void 0 && valor !== "") {
      partes.push(`${encodeURIComponent(nome)}=${encodeURIComponent(valor)}`);
    }
  }
  acrescentar(PARAMETROS_DA_PONTE.categoria, pedido.categoriaSlug);
  acrescentar(PARAMETROS_DA_PONTE.retirada, pedido.retirada);
  acrescentar(PARAMETROS_DA_PONTE.devolucao, pedido.devolucao);
  acrescentar(PARAMETROS_DA_PONTE.horaDeRetirada, pedido.horaDeRetirada);
  acrescentar(PARAMETROS_DA_PONTE.horaDeDevolucao, pedido.horaDeDevolucao);
  acrescentar(PARAMETROS_DA_PONTE.retiradaLocalId, pedido.retiradaLocalId);
  acrescentar(PARAMETROS_DA_PONTE.devolucaoLocalId, pedido.devolucaoLocalId);
  for (const extra of (pedido.extras ?? []).slice(0, MAXIMO_DE_EXTRAS_NA_PONTE)) {
    acrescentar(
      PARAMETROS_DA_PONTE.extra,
      extra.quantidade === 1 ? extra.extraId : `${extra.extraId}:${String(extra.quantidade)}`
    );
  }
  acrescentar(PARAMETROS_DA_PONTE.cupom, pedido.cupom);
  return partes.join("&");
}
function montarLinkDeReserva(base, caminho, pedido) {
  const consulta = montarConsultaDaPonte(pedido);
  const semBarraFinal = base.endsWith("://") ? base : base.replace(/\/+$/, "");
  const comBarraInicial = caminho.startsWith("/") ? caminho : `/${caminho}`;
  const url = `${semBarraFinal}${comBarraInicial}`;
  return consulta === "" ? url : `${url}?${consulta}`;
}
var DDI_DO_BRASIL = "55";
function linkDeWhatsApp(telefone, mensagem) {
  if (!validarTelefone(telefone)) {
    return null;
  }
  const numero = `${DDI_DO_BRASIL}${normalizarTelefone(telefone)}`;
  return `https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`;
}
function mensagemDaPonte(dados) {
  const saudacao = dados.nomeDoCliente === null || dados.nomeDoCliente === void 0 || dados.nomeDoCliente === "" ? "Ol\xE1!" : `Ol\xE1, ${dados.nomeDoCliente.split(" ")[0] ?? dados.nomeDoCliente}!`;
  const linhas = [`${saudacao} Separei a sua reserva:`, "", `\u2022 ${dados.categoria}`];
  if (dados.periodo !== null && dados.periodo !== void 0 && dados.periodo !== "") {
    linhas.push(`\u2022 ${dados.periodo}`);
  }
  if (dados.total !== null && dados.total !== void 0 && dados.total !== "") {
    linhas.push(`\u2022 ${dados.total}`);
  }
  linhas.push("", "\xC9 s\xF3 abrir o link e confirmar:", dados.link);
  return linhas.join("\n");
}

// src/vitrine.ts
var vitrine_exports = {};
__export(vitrine_exports, {
  DIAS_MAXIMOS_DA_REFERENCIA: () => DIAS_MAXIMOS_DA_REFERENCIA,
  DIAS_MINIMOS_DA_REFERENCIA: () => DIAS_MINIMOS_DA_REFERENCIA,
  DIAS_PADRAO_DA_REFERENCIA: () => DIAS_PADRAO_DA_REFERENCIA,
  FINALIDADE_DA_WAITLIST: () => FINALIDADE_DA_WAITLIST,
  INDICE_MAXIMO_DA_FOTO: () => INDICE_MAXIMO_DA_FOTO,
  JANELA_MAXIMA_DO_CALENDARIO_PUBLICO: () => JANELA_MAXIMA_DO_CALENDARIO_PUBLICO,
  aceitesDoCadastroSchema: () => aceitesDoCadastroSchema,
  adotarCotacoesDeVisitanteSchema: () => adotarCotacoesDeVisitanteSchema,
  adotarSessaoDeVisitanteSchema: () => adotarSessaoDeVisitanteSchema,
  baseDoPrecoPublicoSchema: () => baseDoPrecoPublicoSchema,
  calendarioPublicoQuerySchema: () => calendarioPublicoQuerySchema,
  calendarioPublicoSchema: () => calendarioPublicoSchema,
  catalogoPublicoQuerySchema: () => catalogoPublicoQuerySchema,
  catalogoPublicoSchema: () => catalogoPublicoSchema,
  categoriaPublicaDetalheSchema: () => categoriaPublicaDetalheSchema,
  categoriaPublicaSchema: () => categoriaPublicaSchema,
  criarItemDeWaitlistDeVisitanteSchema: () => criarItemDeWaitlistDeVisitanteSchema,
  diaPublicoSchema: () => diaPublicoSchema,
  extraPublicoSchema: () => extraPublicoSchema,
  extrasPublicosSchema: () => extrasPublicosSchema,
  indiceDeFotoSchema: () => indiceDeFotoSchema,
  janelaDaReferenciaSchema: () => janelaDaReferenciaSchema,
  locaisPublicosQuerySchema: () => locaisPublicosQuerySchema,
  locaisPublicosSchema: () => locaisPublicosSchema,
  localPublicoSchema: () => localPublicoSchema,
  pedirCotacaoDeVisitanteSchema: () => pedirCotacaoDeVisitanteSchema,
  pedirSessaoDeVisitanteSchema: () => pedirSessaoDeVisitanteSchema,
  precoDeReferenciaDeCategoriaSchema: () => precoDeReferenciaDeCategoriaSchema,
  precoDeReferenciaQuerySchema: () => precoDeReferenciaQuerySchema,
  precosDeReferenciaSchema: () => precosDeReferenciaSchema,
  quantidadeDeMalas: () => quantidadeDeMalas,
  resultadoDaAdocaoSchema: () => resultadoDaAdocaoSchema,
  sessaoDeVisitanteSchema: () => sessaoDeVisitanteSchema,
  slugPublicoSchema: () => slugPublicoSchema
});
var slugPublicoSchema = z.string().trim().toLowerCase().min(1).max(140).regex(/^[a-z0-9-]+$/, { error: "slug aceita apenas letras min\xFAsculas, n\xFAmeros e h\xEDfen" });
var INDICE_MAXIMO_DA_FOTO = 19;
var indiceDeFotoSchema = z.coerce.number({ error: "o \xEDndice da foto \xE9 obrigat\xF3rio" }).int({ error: "o \xEDndice da foto \xE9 um n\xFAmero inteiro" }).min(0, { error: "o \xEDndice da foto come\xE7a em 0" }).max(INDICE_MAXIMO_DA_FOTO, {
  error: `a galeria vai at\xE9 a foto ${String(INDICE_MAXIMO_DA_FOTO)}`
});
var JANELA_MAXIMA_DO_CALENDARIO_PUBLICO = 62;
var DIAS_MINIMOS_DA_REFERENCIA = 1;
var DIAS_MAXIMOS_DA_REFERENCIA = 30;
var DIAS_PADRAO_DA_REFERENCIA = 3;
var catalogoPublicoQuerySchema = z.object({ locationId: uuidSchema.optional() }).strict();
var precoDeReferenciaQuerySchema = z.object({
  categoryId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  /**
   * `coerce` porque query string é texto. O teto de 30 diárias não é
   * arbitrário: acima disso a faixa de duração já é a mais barata da tabela e
   * o número deixa de ser "a partir de" para virar simulação — que é o que a
   * cotação faz, com data de verdade.
   */
  dias: z.coerce.number().int().min(DIAS_MINIMOS_DA_REFERENCIA).max(DIAS_MAXIMOS_DA_REFERENCIA).default(DIAS_PADRAO_DA_REFERENCIA)
}).strict();
var calendarioPublicoQuerySchema = z.object({
  categoryId: uuidSchema,
  locationId: uuidSchema.optional(),
  de: dataSimplesSchema2,
  ate: dataSimplesSchema2
}).strict().refine((janela) => janela.ate >= janela.de, {
  error: "a data final precisa ser igual ou posterior \xE0 inicial",
  path: ["ate"]
}).refine((janela) => diasEntre2(janela.de, janela.ate) <= JANELA_MAXIMA_DO_CALENDARIO_PUBLICO, {
  // Dois meses, e não os seis do calendário autenticado: cada dia da janela
  // custa uma contagem de frota, e numa rota anônima a janela é o multiplicador
  // que um raspador escolhe. Dois meses cobrem a tela do site com folga.
  error: `o calend\xE1rio p\xFAblico mostra at\xE9 ${String(JANELA_MAXIMA_DO_CALENDARIO_PUBLICO)} dias por vez`,
  path: ["ate"]
});
var locaisPublicosQuerySchema = z.object({ tipo: z.enum(TIPOS_DE_LOCAL).optional() }).strict();
var categoriaPublicaSchema = z.object({
  id: uuidSchema,
  /** O identificador da URL pública. É por ele que a página do site ranqueia. */
  slug: z.string(),
  nome: z.string(),
  descricao: z.string().nullable(),
  nivelDeBlindagem: z.enum(NIVEIS_DE_BLINDAGEM),
  lugares: z.number().int(),
  portas: z.number().int(),
  malas: z.number().int(),
  cambio: z.enum(TIPOS_DE_CAMBIO),
  /** "Compass, Renegade ou similar" — promessa de CLASSE, nunca uma lista. */
  modelosRepresentativos: z.string(),
  /**
   * Fotos da CATEGORIA — nunca do veículo específico, nunca com placa visível.
   *
   * São URLs PRONTAS para um `src`, e não as chaves gravadas. A galeria guarda a
   * chave do storage (`tenants/<uuid>/categorias/<uuid>.jpg`), e o bucket é
   * privado — na mesma árvore moram `documentos` (CNH), `vistorias` e
   * `sinistros`, então abrir um prefixo público erraria por um caractere e
   * publicaria documento de cliente. O que sai aqui é o endereço da rota de
   * foto, que confere as duas cercas e responde `302` para uma URL assinada de
   * curta duração.
   *
   * "Pronta para um `src`" é uma promessa VERIFICADA, e ela custa o tenant no
   * caminho (`/api/public/t/<tenant>/photos/<categoria>/<i>`): um `<img>` não
   * manda `x-tenant-slug`, e o host da API nunca resolve tenant — ele está na
   * denylist que `TravaDeHostsReservados` confere no boot. Sem o segmento, toda
   * URL daqui era 400 `TENANT_REQUIRED` para o único consumidor que existe. O
   * teste que sustenta a promessa SEGUE a URL emitida, sem header nenhum;
   * comparar strings não prova que uma foto abre.
   *
   * URL crua gravada na release anterior (P8) atravessa como está: ela já é um
   * endereço absoluto e não tem chave para assinar. É o que permite trocar o
   * `<Textarea>` de URLs pelo uploader sem quebrar o catálogo que existe hoje.
   */
  fotos: z.array(z.url()),
  idadeMinimaDoCondutor: z.number().int(),
  tempoMinimoDeCnhEmAnos: z.number().int(),
  caucaoBase: dinheiroNoFioSchema,
  kmDiarioIncluso: z.number().int(),
  valorDoKmExcedente: dinheiroNoFioSchema,
  ordemDeExibicao: z.number().int(),
  /**
   * A POLÍTICA DE PARCELAMENTO da categoria — e por que ela é pública.
   *
   * O teto real de parcelas não é o `MAXIMO_DE_PARCELAS` global (que é teto de
   * sanidade do produto): é `planoDeParcelas(total, política)` sobre estes dois
   * campos, e é ele que `PagamentosService` aplica na cobrança. Sem eles na
   * vitrine, a tela do cliente só sabia o teto global — oferecia "3x" a uma
   * categoria configurada com `maximoDeParcelas: 1` e o cartão era recusado
   * DEPOIS de tokenizado, com a mensagem de "pagamento não aprovado" a explicar
   * uma pastilha que o próprio app tinha desenhado.
   *
   * Eles são termo COMERCIAL e não dado de operação: "em até 6x" é o que uma
   * locadora anuncia na vitrine. Nada aqui revela frota, custo ou tabela.
   */
  maximoDeParcelas: z.number().int().positive(),
  valorMinimoDaParcela: dinheiroNoFioSchema,
  /**
   * A diária "a partir de", da TABELA PADRÃO, para a janela canônica.
   *
   * `null` quando a categoria não tem tarifa na tabela padrão — e `null` é a
   * resposta honesta: a tela mostra "consulte", não um preço inventado. Nunca
   * vem de tabela negociada; ver `CotacoesService.precoDeReferencia`.
   */
  precoAPartirDe: dinheiroNoFioSchema.nullable()
});
function quantidadeDeMalas(malas) {
  return `${String(malas)} ${malas === 1 ? "mala" : "malas"}`;
}
var extraPublicoSchema = z.object({
  id: uuidSchema,
  nome: z.string(),
  descricao: z.string().nullable(),
  tipo: tipoDeExtraSchema,
  /** Como o valor é multiplicado: por diária, por unidade ou por reserva. */
  cobranca: cobrancaDeExtraSchema,
  valorUnitario: dinheiroNoFioSchema,
  /** Só em `PROTECAO` — é ela que distingue as três proteções entre si. */
  franquia: dinheiroNoFioSchema.nullable(),
  quantidadeMaxima: z.number().int().nullable(),
  kmPorUnidade: z.number().int().nullable(),
  ordemDeExibicao: z.number().int()
});
var localPublicoSchema = z.object({
  id: uuidSchema,
  nome: z.string(),
  tipo: z.enum(TIPOS_DE_LOCAL),
  bairro: z.string(),
  cidade: z.string(),
  uf: z.string(),
  /** Só dígitos, como está gravado. A máscara é de quem exibe. */
  cep: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  /** Os sete dias, sempre presentes. Dia com lista vazia é FECHADO. */
  horarios: horariosSchema
});
var baseDoPrecoPublicoSchema = z.literal("TABELA_PADRAO");
var janelaDaReferenciaSchema = z.object({
  de: z.iso.datetime(),
  ate: z.iso.datetime()
});
var precoDeReferenciaDeCategoriaSchema = z.object({
  categoryId: uuidSchema,
  /** `null` = sem tarifa na tabela padrão para a faixa desta duração. */
  aPartirDe: dinheiroNoFioSchema.nullable(),
  baseadoEm: baseDoPrecoPublicoSchema
});
var precosDeReferenciaSchema = z.object({
  moeda: z.literal("BRL"),
  avaliadoEm: z.iso.datetime(),
  janela: janelaDaReferenciaSchema,
  dias: z.number().int(),
  itens: z.array(precoDeReferenciaDeCategoriaSchema)
});
var catalogoPublicoSchema = z.object({
  tenant: z.object({ nome: z.string() }),
  categorias: z.array(categoriaPublicaSchema),
  extras: z.array(extraPublicoSchema),
  locais: z.array(localPublicoSchema),
  /** Quando este corpo foi montado — o que a página imprime como "preços de". */
  geradoEm: z.iso.datetime(),
  /** A janela e a duração que o `precoAPartirDe` de cada categoria respondeu. */
  referenciaDePreco: z.object({
    dias: z.number().int(),
    janela: janelaDaReferenciaSchema,
    baseadoEm: baseDoPrecoPublicoSchema
  })
});
var categoriaPublicaDetalheSchema = z.object({
  categoria: categoriaPublicaSchema,
  /** Os extras contratáveis, para a página dizer o que dá para somar. */
  extras: z.array(extraPublicoSchema),
  geradoEm: z.iso.datetime(),
  referenciaDePreco: z.object({
    dias: z.number().int(),
    janela: janelaDaReferenciaSchema,
    baseadoEm: baseDoPrecoPublicoSchema
  })
});
var diaPublicoSchema = z.object({
  data: dataSimplesSchema2,
  disponivel: z.boolean()
});
var calendarioPublicoSchema = z.object({
  categoryId: uuidSchema,
  de: dataSimplesSchema2,
  ate: dataSimplesSchema2,
  dias: z.array(diaPublicoSchema)
});
var locaisPublicosSchema = z.object({
  itens: z.array(localPublicoSchema),
  geradoEm: z.iso.datetime()
});
var extrasPublicosSchema = z.object({
  itens: z.array(extraPublicoSchema),
  geradoEm: z.iso.datetime()
});
var MAXIMO_DE_EXTRAS = 20;
var ERRO_DE_ORDEM_DO_PERIODO2 = {
  error: "a devolu\xE7\xE3o precisa ser depois da retirada",
  path: ["devolucao"]
};
var ERRO_DE_TETO_DO_PERIODO2 = {
  error: `a loca\xE7\xE3o vai at\xE9 ${String(DURACAO_MAXIMA_EM_DIAS)} dias \u2014 confira o ano da devolu\xE7\xE3o`,
  path: ["devolucao"]
};
var ERRO_DE_EXTRA_REPETIDO = {
  error: "o mesmo extra aparece duas vezes \u2014 some a quantidade em vez de repetir",
  path: ["extras"]
};
var pedirSessaoDeVisitanteSchema = z.object({
  origemUtm: z.string().trim().max(120).optional(),
  desafio: z.string().trim().max(4096).optional()
}).strict();
var sessaoDeVisitanteSchema = z.object({
  token: z.string(),
  expiraEm: z.iso.datetime(),
  expiraEmSegundos: z.number().int().positive(),
  escopo: z.array(z.string())
});
var pedirCotacaoDeVisitanteSchema = z.object({
  /** OBRIGATÓRIO — ver "categoryId obrigatório" no cabeçalho da seção. */
  categoryId: uuidSchema,
  retirada: instanteSchema,
  devolucao: instanteSchema,
  retiradaLocalId: uuidSchema.nullish(),
  devolucaoLocalId: uuidSchema.nullish(),
  extras: z.array(extraEscolhidoSchema).max(MAXIMO_DE_EXTRAS, { error: "h\xE1 extras demais nesta cota\xE7\xE3o" }).default([]),
  cupom: codigoDeCupomSchema.nullish()
}).strict().refine(devolucaoDepoisDaRetirada, ERRO_DE_ORDEM_DO_PERIODO2).refine(periodoDentroDoTeto, ERRO_DE_TETO_DO_PERIODO2).refine(
  (pedido) => new Set(pedido.extras.map((extra) => extra.extraId)).size === pedido.extras.length,
  ERRO_DE_EXTRA_REPETIDO
);
var criarItemDeWaitlistDeVisitanteSchema = z.object({
  categoryId: uuidSchema,
  retirada: instanteSchema,
  devolucao: instanteSchema,
  nome: z.string().trim().min(2).max(120).optional(),
  email: emailSchema.optional(),
  telefone: telefoneSchema.optional(),
  observacoes: z.string().trim().max(1e3).optional(),
  /**
   * O aceite explícito. Ver o cabeçalho: literal, nunca booleano.
   */
  consentimentoLgpd: z.literal(true, {
    error: "\xE9 preciso concordar com o uso dos seus dados para entrarmos em contato"
  })
}).strict().refine(devolucaoDepoisDaRetirada, ERRO_DE_ORDEM_DO_PERIODO2).refine(periodoDentroDoTeto, ERRO_DE_TETO_DO_PERIODO2).refine(
  // O gêmeo do CHECK `waitlist_contato_check`. Sem `accountId` nesta variante
  // (visitante não tem conta), o contato tem de vir de e-mail ou telefone —
  // um registro sem canal nenhum é uma linha que ninguém consegue atender.
  (item) => item.email !== void 0 || item.telefone !== void 0,
  {
    error: "informe ao menos um contato: e-mail ou telefone",
    path: ["email"]
  }
);
var FINALIDADE_DA_WAITLIST = "contato sobre disponibilidade de ve\xEDculo no per\xEDodo informado";
var aceitesDoCadastroSchema = z.object({
  termos: z.literal(true, { error: "\xE9 preciso aceitar os termos de uso" }),
  privacidade: z.literal(true, { error: "\xE9 preciso aceitar a pol\xEDtica de privacidade" }),
  /** Identificador do texto aceito (ex.: `2026-09-01`). */
  versao: z.string().trim().min(1).max(40)
}).strict();
var adotarSessaoDeVisitanteSchema = z.object({
  usuario: z.object({
    nome: nomeSchema,
    email: emailSchema,
    senha: senhaSchema,
    confirmacaoDeSenha: z.string(),
    telefone: telefoneSchema.optional()
  }).strict().refine((bloco) => bloco.senha === bloco.confirmacaoDeSenha, {
    error: MENSAGEM_CONFIRMACAO_DE_SENHA,
    path: ["confirmacaoDeSenha"]
  }),
  conta: z.object({
    cpf: cpfSchema,
    dataDeNascimento: dataDeNascimentoSchema
  }).strict(),
  endereco: criarEnderecoDaContaSchema,
  aceites: aceitesDoCadastroSchema,
  /** O campo do Turnstile. Ver `pedirSessaoDeVisitanteSchema`. */
  desafio: z.string().trim().max(4096).optional()
}).strict();
var adotarCotacoesDeVisitanteSchema = z.object({
  /** O token de visitante INTEIRO, não o `vst`. Ver o cabeçalho da seção. */
  visitanteToken: z.string().trim().min(1, { error: "informe o token da sess\xE3o de navega\xE7\xE3o" })
}).strict();
var resultadoDaAdocaoSchema = z.object({
  cotacoesAdotadas: z.number().int().nonnegative()
});
function diasEntre2(de, ate) {
  const inicio = Date.parse(`${de}T00:00:00Z`);
  const fim = Date.parse(`${ate}T00:00:00Z`);
  return Math.round((fim - inicio) / 864e5) + 1;
}

// src/tokens.ts
var surfaces = {
  /** Fundo raiz, splash e hero. */
  base: "#000000",
  /** Cards e sidebar. */
  surface: "#0B0B0C",
  /** Modais, dropdowns e hover de linha. */
  elevated: "#141416",
  border: "#26262A",
  borderStrong: "#3A3A40"
};
var text = {
  primary: "#FFFFFF",
  secondary: "#A1A1AA",
  /**
   * 4,63:1 na superfície mais clara do tema escuro — o AA de texto de corpo.
   *
   * Era `#6B6B73` (3,48:1 sobre `elevated`) até 05/10/2026. O painel usa este
   * token em `text-xs` em ~180 lugares, e nesse tamanho o WCAG pede 4,5:1: o
   * token mais usado da tela auxiliar era o único que não alcançava. Segue
   * visivelmente abaixo do `secondary` (ΔE 11 em OKLab), que é o seu trabalho.
   */
  muted: "#7F7F87"
};
var brand2 = {
  /** Hover e estados claros. */
  400: "#F8E148",
  /** COR PRIMÁRIA — CTA, destaque, série principal de gráfico, item ativo. */
  500: "#EBCD09",
  /** Pressed. */
  600: "#CDB308",
  /** Texto, link, borda e detalhe sobre fundo CLARO. */
  700: "#837205",
  /** Fundo sutil de destaque no tema escuro. */
  tint: "#1F1B05"
};
var semantic = {
  success: "#22C55E",
  /** Âmbar — visualmente distinto do amarelo da marca, sempre com ícone. */
  warning: "#F59E0B",
  danger: "#EF4444",
  info: "#38BDF8"
};
var semanticLight = {
  success: "#15803D",
  warning: "#B45309",
  danger: "#B91C1C",
  info: "#0369A1"
};
var chartNeutrals = ["#E4E4E7", "#B4B4BC", "#8A8A93", "#6B6B73"];
var lightSurfaces = {
  base: "#FAFAFA",
  surface: "#FFFFFF",
  elevated: "#FFFFFF",
  muted: "#F4F4F5",
  border: "#E4E4E7",
  borderStrong: "#D4D4D8"
};
var lightText = {
  primary: "#0B0B0C",
  secondary: "#52525B",
  /** 5,10:1 sobre o cinza `muted` de fundo de seção, onde `#71717A` dava 4,40:1. */
  muted: "#67676F"
};
var chartNeutralsLight = ["#3F3F46", "#5F5F68", "#7A7A82", "#949499"];
var radius = {
  /** O raio da marca. */
  base: "10px",
  sm: "6px",
  lg: "14px",
  full: "9999px"
};
var typography = {
  fontFamily: "'Poppins', 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  fontFamilyMono: "ui-monospace, SFMono-Regular, 'JetBrains Mono', Menlo, monospace",
  /** Peso mínimo de número em destaque (KPI, total). */
  numericWeight: 600,
  /** `tabular-nums` é obrigatório em qualquer coluna monetária. */
  numericVariant: "tabular-nums"
};
var shadows = {
  none: "none",
  subtle: "0 1px 2px 0 rgb(0 0 0 / 0.40)",
  elevated: "0 8px 24px -8px rgb(0 0 0 / 0.60)"
};
var tokens = {
  surfaces,
  text,
  lightSurfaces,
  lightText,
  brand: brand2,
  semantic,
  semanticLight,
  chartNeutrals,
  chartNeutralsLight,
  radius,
  typography,
  shadows
};

// src/pagamento.ts
var pagamento_exports = {};
__export(pagamento_exports, {
  ATORES_DE_TRANSACAO_DE_PAGAMENTO: () => ATORES_DE_TRANSACAO_DE_PAGAMENTO,
  JANELA_DE_RENOVACAO_EM_DIAS: () => JANELA_DE_RENOVACAO_EM_DIAS,
  MAXIMO_DE_PARCELAS: () => MAXIMO_DE_PARCELAS,
  METODOS_DE_PAGAMENTO: () => METODOS_DE_PAGAMENTO,
  PROVEDORES_DE_PAGAMENTO: () => PROVEDORES_DE_PAGAMENTO,
  ROTULO_DO_METODO_DE_PAGAMENTO: () => ROTULO_DO_METODO_DE_PAGAMENTO,
  ROTULO_DO_STATUS_DE_FATURA: () => ROTULO_DO_STATUS_DE_FATURA,
  ROTULO_DO_STATUS_DE_TRANSACAO: () => ROTULO_DO_STATUS_DE_TRANSACAO,
  ROTULO_DO_TIPO_DE_TRANSACAO: () => ROTULO_DO_TIPO_DE_TRANSACAO,
  STATUS_DE_FATURA: () => STATUS_DE_FATURA,
  STATUS_DE_TRANSACAO: () => STATUS_DE_TRANSACAO,
  STATUS_TERMINAIS_DE_TRANSACAO: () => STATUS_TERMINAIS_DE_TRANSACAO,
  TAMANHO_DA_CHAVE_DE_ACESSO: () => TAMANHO_DA_CHAVE_DE_ACESSO,
  TIPOS_DE_TRANSACAO: () => TIPOS_DE_TRANSACAO,
  TRANSICOES_DE_PAGAMENTO: () => TRANSICOES_DE_PAGAMENTO,
  VALIDADE_DA_PRE_AUTORIZACAO_EM_DIAS: () => VALIDADE_DA_PRE_AUTORIZACAO_EM_DIAS,
  anexarNotaFiscalSchema: () => anexarNotaFiscalSchema,
  chaveDeAcessoSchema: () => chaveDeAcessoSchema,
  encontrarTransicaoDePagamento: () => encontrarTransicaoDePagamento,
  estornarSchema: () => estornarSchema,
  planoDeParcelas: () => planoDeParcelas,
  precisaRenovarPreAutorizacao: () => precisaRenovarPreAutorizacao,
  statusDeFaturaSchema: () => statusDeFaturaSchema,
  statusDeTransacaoSchema: () => statusDeTransacaoSchema,
  transicoesDePagamentoPossiveis: () => transicoesDePagamentoPossiveis,
  validarChaveDeAcesso: () => validarChaveDeAcesso
});
var TIPOS_DE_TRANSACAO = ["VENDA", "PRE_AUTORIZACAO"];
var ROTULO_DO_TIPO_DE_TRANSACAO = {
  VENDA: "Venda",
  PRE_AUTORIZACAO: "Pr\xE9-autoriza\xE7\xE3o (cau\xE7\xE3o)"
};
var METODOS_DE_PAGAMENTO = ["CARTAO_CREDITO", "PIX"];
var ROTULO_DO_METODO_DE_PAGAMENTO = {
  CARTAO_CREDITO: "Cart\xE3o de cr\xE9dito",
  PIX: "PIX"
};
var PROVEDORES_DE_PAGAMENTO = ["CIELO", "FAKE"];
var STATUS_DE_TRANSACAO = [
  /** Intenção registrada; nada foi ao provedor ainda. */
  "CRIADA",
  /** Enviada ao provedor; PIX aguardando pagamento, cartão em processamento. */
  "AGUARDANDO_CONFIRMACAO",
  /** Pré-autorização aprovada — o limite está bloqueado, nada foi cobrado. */
  "AUTORIZADA",
  /** Venda aprovada ou PIX confirmado — o dinheiro é da locadora. */
  "PAGA",
  /** Pré-autorização capturada (total ou parcial) — virou cobrança. */
  "CAPTURADA",
  /** O emissor recusou. Terminal: nova tentativa é NOVA transação. */
  "NEGADA",
  /** Pré-autorização ou QR de PIX venceu sem conclusão. Terminal. */
  "EXPIRADA",
  /** Pré-autorização baixada sem captura — o limite foi devolvido. Terminal. */
  "BAIXADA",
  /** Estorno parcial: parte do valor voltou; o resto continua capturado. */
  "PARCIALMENTE_ESTORNADA",
  /** Estorno total. Terminal. */
  "ESTORNADA",
  /** Erro técnico terminal (timeout sem confirmação, resposta inválida). */
  "FALHOU"
];
var statusDeTransacaoSchema = z.enum(STATUS_DE_TRANSACAO, {
  error: "status de transa\xE7\xE3o inv\xE1lido"
});
var ROTULO_DO_STATUS_DE_TRANSACAO = {
  CRIADA: { rotulo: "Criada", intencao: "neutra" },
  AGUARDANDO_CONFIRMACAO: { rotulo: "Aguardando confirma\xE7\xE3o", intencao: "atencao" },
  AUTORIZADA: { rotulo: "Autorizada", intencao: "sucesso" },
  PAGA: { rotulo: "Paga", intencao: "sucesso" },
  CAPTURADA: { rotulo: "Capturada", intencao: "sucesso" },
  NEGADA: { rotulo: "Negada", intencao: "erro" },
  EXPIRADA: { rotulo: "Expirada", intencao: "erro" },
  BAIXADA: { rotulo: "Baixada (limite devolvido)", intencao: "neutra" },
  PARCIALMENTE_ESTORNADA: { rotulo: "Parcialmente estornada", intencao: "atencao" },
  ESTORNADA: { rotulo: "Estornada", intencao: "neutra" },
  FALHOU: { rotulo: "Falhou", intencao: "erro" }
};
var ATORES_DE_TRANSACAO_DE_PAGAMENTO = ["SISTEMA", "EMPRESA", "PROVEDOR"];
var TRANSICOES_DE_PAGAMENTO = [
  {
    de: "CRIADA",
    para: "AGUARDANDO_CONFIRMACAO",
    atores: ["SISTEMA"],
    acao: "ENVIAR_AO_PROVEDOR",
    descricao: "A transa\xE7\xE3o foi enviada ao provedor (QR gerado, cart\xE3o em processamento).",
    exigeMotivo: false
  },
  {
    de: "CRIADA",
    para: "FALHOU",
    atores: ["SISTEMA"],
    acao: "FALHAR_NO_ENVIO",
    descricao: "O provedor n\xE3o aceitou a cria\xE7\xE3o (erro t\xE9cnico antes de existir l\xE1).",
    exigeMotivo: true
  },
  {
    de: "AGUARDANDO_CONFIRMACAO",
    para: "AUTORIZADA",
    atores: ["PROVEDOR", "SISTEMA"],
    acao: "AUTORIZAR",
    descricao: "A pr\xE9-autoriza\xE7\xE3o foi aprovada \u2014 o limite do cliente est\xE1 bloqueado.",
    exigeMotivo: false
  },
  {
    de: "AGUARDANDO_CONFIRMACAO",
    para: "PAGA",
    atores: ["PROVEDOR", "SISTEMA"],
    acao: "CONFIRMAR_PAGAMENTO",
    descricao: "A venda foi aprovada ou o PIX foi pago.",
    exigeMotivo: false
  },
  {
    de: "AGUARDANDO_CONFIRMACAO",
    para: "NEGADA",
    atores: ["PROVEDOR", "SISTEMA"],
    acao: "NEGAR",
    descricao: "O emissor recusou. Nova tentativa \xE9 uma transa\xE7\xE3o nova.",
    exigeMotivo: false
  },
  {
    de: "AGUARDANDO_CONFIRMACAO",
    para: "EXPIRADA",
    atores: ["PROVEDOR", "SISTEMA"],
    acao: "EXPIRAR_CONFIRMACAO",
    descricao: "O QR do PIX venceu sem pagamento, ou o processamento estourou o prazo.",
    exigeMotivo: false
  },
  {
    de: "AGUARDANDO_CONFIRMACAO",
    para: "FALHOU",
    atores: ["SISTEMA"],
    acao: "FALHAR",
    descricao: "Erro t\xE9cnico terminal \u2014 a reconcilia\xE7\xE3o decide o destino real.",
    exigeMotivo: true
  },
  {
    de: "AUTORIZADA",
    para: "CAPTURADA",
    atores: ["EMPRESA", "SISTEMA"],
    acao: "CAPTURAR",
    descricao: "A cau\xE7\xE3o (toda ou parte) virou cobran\xE7a \u2014 o fechamento mandou.",
    exigeMotivo: false
  },
  {
    de: "AUTORIZADA",
    para: "BAIXADA",
    atores: ["EMPRESA", "SISTEMA"],
    acao: "BAIXAR",
    descricao: "Devolu\xE7\xE3o em ordem: o bloqueio foi liberado sem cobrar nada.",
    exigeMotivo: false
  },
  {
    de: "AUTORIZADA",
    para: "EXPIRADA",
    atores: ["SISTEMA", "PROVEDOR"],
    acao: "EXPIRAR_AUTORIZACAO",
    descricao: "A pr\xE9-autoriza\xE7\xE3o venceu sem captura nem baixa. Na loca\xE7\xE3o mensal, a renova\xE7\xE3o programada existe para isto nunca acontecer.",
    exigeMotivo: false
  },
  {
    de: "PAGA",
    para: "PARCIALMENTE_ESTORNADA",
    atores: ["EMPRESA"],
    acao: "ESTORNAR_PARTE",
    descricao: "Parte do valor voltou ao cliente.",
    exigeMotivo: true
  },
  {
    de: "PAGA",
    para: "ESTORNADA",
    atores: ["EMPRESA"],
    acao: "ESTORNAR",
    descricao: "O valor inteiro voltou ao cliente.",
    exigeMotivo: true
  },
  {
    de: "CAPTURADA",
    para: "PARCIALMENTE_ESTORNADA",
    atores: ["EMPRESA"],
    acao: "ESTORNAR_PARTE",
    descricao: "Parte da captura voltou ao cliente.",
    exigeMotivo: true
  },
  {
    de: "CAPTURADA",
    para: "ESTORNADA",
    atores: ["EMPRESA"],
    acao: "ESTORNAR",
    descricao: "A captura inteira voltou ao cliente.",
    exigeMotivo: true
  },
  {
    de: "PARCIALMENTE_ESTORNADA",
    para: "PARCIALMENTE_ESTORNADA",
    atores: ["EMPRESA"],
    acao: "ESTORNAR_PARTE",
    descricao: "Mais uma parte voltou \u2014 o acumulado ainda n\xE3o cobre o total.",
    exigeMotivo: true
  },
  {
    de: "PARCIALMENTE_ESTORNADA",
    para: "ESTORNADA",
    atores: ["EMPRESA"],
    acao: "ESTORNAR",
    descricao: "O restante voltou \u2014 o estorno acumulado alcan\xE7ou o total.",
    exigeMotivo: true
  }
];
function encontrarTransicaoDePagamento(de, para, ator) {
  return TRANSICOES_DE_PAGAMENTO.find(
    (transicao) => transicao.de === de && transicao.para === para && transicao.atores.includes(ator)
  );
}
function transicoesDePagamentoPossiveis(de, ator) {
  return TRANSICOES_DE_PAGAMENTO.filter(
    (transicao) => transicao.de === de && transicao.atores.includes(ator)
  );
}
var STATUS_TERMINAIS_DE_TRANSACAO = STATUS_DE_TRANSACAO.filter(
  (status) => TRANSICOES_DE_PAGAMENTO.every((transicao) => transicao.de !== status)
);
var VALIDADE_DA_PRE_AUTORIZACAO_EM_DIAS = 28;
var JANELA_DE_RENOVACAO_EM_DIAS = 5;
function precisaRenovarPreAutorizacao(expiraEm, agora) {
  const janelaEmMs = JANELA_DE_RENOVACAO_EM_DIAS * 864e5;
  return expiraEm.getTime() - agora.getTime() <= janelaEmMs;
}
var MAXIMO_DE_PARCELAS = 12;
function planoDeParcelas(total, politica) {
  if (total <= 0n) {
    throw new RangeError("Parcelamento inv\xE1lido: o total precisa ser positivo.");
  }
  const teto = Math.min(Math.max(1, politica.maximoDeParcelas), MAXIMO_DE_PARCELAS);
  let parcelas = 1;
  for (let tentativa = teto; tentativa >= 1; tentativa -= 1) {
    const piso = total / BigInt(tentativa);
    if (piso >= politica.valorMinimoDaParcela) {
      parcelas = tentativa;
      break;
    }
  }
  const pesos = Array.from({ length: parcelas }, () => 1n);
  const valores = parcelas === 1 ? [total] : allocate(total, pesos);
  return { parcelas, valores };
}
var TAMANHO_DA_CHAVE_DE_ACESSO = 44;
function validarChaveDeAcesso(chave) {
  if (!/^\d{44}$/.test(chave)) {
    return false;
  }
  const corpo = chave.slice(0, 43);
  const dvInformado = Number(chave[43]);
  let peso = 2;
  let soma = 0;
  for (let indice = corpo.length - 1; indice >= 0; indice -= 1) {
    soma += Number(corpo[indice]) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  const resto = soma % 11;
  const dv = resto === 0 || resto === 1 ? 0 : 11 - resto;
  return dv === dvInformado;
}
var chaveDeAcessoSchema = z.string({ error: "a chave de acesso \xE9 obrigat\xF3ria" }).trim().transform((valor) => valor.replace(/\s/g, "")).refine((valor) => validarChaveDeAcesso(valor), {
  error: "chave de acesso inv\xE1lida \u2014 confira os 44 d\xEDgitos"
});
var STATUS_DE_FATURA = [
  /** Competência corrente, acumulando reservas fechadas. */
  "ABERTA",
  /** Competência encerrada no dia de fechamento da conta — valor travado. */
  "FECHADA",
  "PAGA",
  "VENCIDA",
  "CANCELADA"
];
var statusDeFaturaSchema = z.enum(STATUS_DE_FATURA, { error: "status de fatura inv\xE1lido" });
var ROTULO_DO_STATUS_DE_FATURA = {
  ABERTA: "Aberta",
  FECHADA: "Fechada \u2014 aguardando pagamento",
  PAGA: "Paga",
  VENCIDA: "Vencida",
  CANCELADA: "Cancelada"
};
var estornarSchema = z.object({
  /** Sem valor = estorno total. */
  valor: z.string().regex(/^\d+$/, { error: "valor em centavos, como string" }).nullish(),
  motivo: z.string({ error: "o motivo do estorno \xE9 obrigat\xF3rio" }).trim().min(5, { error: "o motivo precisa dizer alguma coisa" }).max(500, { error: "motivo muito longo" })
}).strict();
var anexarNotaFiscalSchema = z.object({
  chaveDeAcesso: chaveDeAcessoSchema,
  /** O XML no storage (a MESMA forma de chave de todo anexo do repo). */
  arquivoChave: chaveDeArquivoSchema,
  /** Centavos como string — conferido contra o total da fatura/reserva. */
  valor: z.string().regex(/^\d+$/, { error: "valor em centavos, como string" }),
  reservationId: uuidSchema.nullish(),
  invoiceId: uuidSchema.nullish()
});

// src/rateio.ts
var LINHAS_DE_RECEITA_DE_LOCACAO = [
  "ATRASO",
  "KM_EXCEDENTE",
  "COMBUSTIVEL",
  "CREDITO_DEVOLUCAO_ANTECIPADA"
];
function ehReceitaDeLocacao(tipo) {
  return LINHAS_DE_RECEITA_DE_LOCACAO.includes(tipo);
}
function ratearReceita(receitaTotal, trechos) {
  if (trechos.length === 0) {
    return [];
  }
  const pesos = trechos.map((trecho) => Math.max(1, trecho.fim.getTime() - trecho.inicio.getTime()));
  const partes = allocate(receitaTotal, pesos);
  return trechos.map((trecho, indice) => ({
    ...trecho,
    duracaoEmMs: pesos[indice] ?? 1,
    receita: partes[indice] ?? zero
  }));
}
var DIARIAS_DE_CONTRATO_LONGO = 30;
function ehContratoLongo(diarias) {
  return diarias >= DIARIAS_DE_CONTRATO_LONGO;
}

// src/metricas.ts
function percentual(parte, total) {
  if (total <= 0) {
    return null;
  }
  return Math.round(parte / total * 100);
}
function taxaDeOcupacao(dias) {
  return percentual(dias.diasAlugados, dias.diasDisponiveis);
}
function taxaDeUtilizacao(dias) {
  return percentual(dias.diasAlugados, dias.diasDePosse);
}
function taxaDeOcupacaoDeDiaria(dias) {
  return percentual(
    dias.diasAlugados - dias.diasEmContratoLongo,
    dias.diasDisponiveis - dias.diasEmContratoLongo
  );
}
function diasDeOciosidade(dias) {
  return Math.max(0, dias.diasDePosse - dias.diasAlugados);
}
function diasParadoDisponivel(dias) {
  return Math.max(0, dias.diasDisponiveis - dias.diasAlugados);
}
function revPac(receita, dias) {
  if (dias.diasDisponiveis <= 0) {
    return null;
  }
  return receita / BigInt(dias.diasDisponiveis);
}
function diariaMediaRealizada(receita, dias) {
  if (dias.diasAlugados <= 0) {
    return null;
  }
  return receita / BigInt(dias.diasAlugados);
}
function margemDeContribuicao(receita, custoDireto) {
  return receita - custoDireto;
}
function margemPercentual(receita, custoDireto) {
  if (receita <= 0n) {
    return null;
  }
  return Math.round(Number(receita - custoDireto) / Number(receita) * 100);
}
function custoPorKm(custo, kmRodado) {
  if (kmRodado <= 0) {
    return null;
  }
  return custo / BigInt(kmRodado);
}
function custoPorDiaDePosse(custo, dias) {
  if (dias.diasDePosse <= 0) {
    return null;
  }
  return custo / BigInt(dias.diasDePosse);
}
function custoDaOciosidade(custo, dias) {
  const porDia = custoPorDiaDePosse(custo, dias);
  if (porDia === null) {
    return null;
  }
  return porDia * BigInt(diasDeOciosidade(dias));
}
function tco(valorDeAquisicao, custoAcumulado) {
  return valorDeAquisicao + custoAcumulado;
}
function calcularPayback(valorDeAquisicao, margemAcumulada, mesesDecorridos) {
  if (valorDeAquisicao <= 0n) {
    return null;
  }
  const percentualCoberto = Math.round(Number(margemAcumulada) / Number(valorDeAquisicao) * 100);
  if (margemAcumulada >= valorDeAquisicao) {
    return { percentualCoberto, mesesRestantes: 0, pago: true };
  }
  if (mesesDecorridos <= 0 || margemAcumulada <= 0n) {
    return { percentualCoberto, mesesRestantes: null, pago: false };
  }
  const margemMensal = Number(margemAcumulada) / mesesDecorridos;
  const falta = Number(valorDeAquisicao - margemAcumulada);
  return {
    percentualCoberto,
    mesesRestantes: Math.ceil(falta / margemMensal),
    pago: false
  };
}
function taxaDeConversao(reservas, cotacoes) {
  return percentual(reservas, cotacoes);
}
function ticketMedio(receita, reservas) {
  if (reservas <= 0) {
    return null;
  }
  return receita / BigInt(reservas);
}
function leadTimeMedio(diasPorReserva) {
  if (diasPorReserva.length === 0) {
    return null;
  }
  const soma = diasPorReserva.reduce((total, dias) => total + dias, 0);
  return Math.round(soma / diasPorReserva.length);
}
function somar(valores) {
  let total = zero;
  for (const valor of valores) {
    total = total + valor;
  }
  return total;
}
function diasCobertos(janela, intervalos, timeZone = APP_TIMEZONE) {
  const dias = /* @__PURE__ */ new Set();
  for (const intervalo of intervalos) {
    const inicio = intervalo.inicio > janela.inicio ? intervalo.inicio : janela.inicio;
    const fim = intervalo.fim < janela.fim ? intervalo.fim : janela.fim;
    if (inicio >= fim) {
      continue;
    }
    for (let cursor = inicio.getTime(); cursor < fim.getTime(); cursor += 24 * 60 * 60 * 1e3) {
      dias.add(localDayKey(new Date(cursor), timeZone));
    }
    const ultimo = new Date(fim.getTime() - 1);
    if (ultimo >= inicio) {
      dias.add(localDayKey(ultimo, timeZone));
    }
  }
  return dias.size;
}

// src/anomalias.ts
var FATOR_MAD = 1.4826;
var MINIMO_DE_PONTOS = 5;
var LIMIAR_PADRAO = 3;
function mediana(valores) {
  if (valores.length === 0) return null;
  const ordenado = [...valores].sort((a, b) => a - b);
  const meio = Math.floor(ordenado.length / 2);
  if (ordenado.length % 2 === 1) {
    return ordenado[meio] ?? null;
  }
  const esquerda = ordenado[meio - 1];
  const direita = ordenado[meio];
  if (esquerda === void 0 || direita === void 0) return null;
  return (esquerda + direita) / 2;
}
function dispersaoRobusta(valores) {
  const centro = mediana(valores);
  if (centro === null) return null;
  const desvios = valores.map((valor) => Math.abs(valor - centro));
  const mad = mediana(desvios);
  return mad === null ? null : mad * FATOR_MAD;
}
function detectarAnomalia(historico, valor, limiar = LIMIAR_PADRAO) {
  if (historico.length < MINIMO_DE_PONTOS) return null;
  const centro = mediana(historico);
  const dispersao = dispersaoRobusta(historico);
  if (centro === null || dispersao === null || dispersao <= 0) return null;
  const escore = Math.abs(valor - centro) / dispersao;
  if (escore < limiar) return null;
  return {
    valor,
    mediana: centro,
    dispersao,
    escore: Math.round(escore * 100) / 100,
    direcao: valor > centro ? "ACIMA" : "ABAIXO"
  };
}

// src/sazonalidade.ts
function rotuloDaSemana(chave) {
  return `${String(chave.ano)}-W${String(chave.semana).padStart(2, "0")}`;
}
function semanaIso(instante, timeZone = APP_TIMEZONE) {
  const chaveDoDia = localDayKey(instante, timeZone);
  const dia = /* @__PURE__ */ new Date(`${chaveDoDia}T12:00:00.000Z`);
  const diaDaSemana = dia.getUTCDay() === 0 ? 7 : dia.getUTCDay();
  const quinta = new Date(dia);
  quinta.setUTCDate(dia.getUTCDate() + (4 - diaDaSemana));
  const ano = quinta.getUTCFullYear();
  const quintaEmDias = Date.UTC(quinta.getUTCFullYear(), quinta.getUTCMonth(), quinta.getUTCDate());
  const diasDesdeJaneiro = (quintaEmDias - Date.UTC(ano, 0, 1)) / 864e5;
  return { ano, semana: Math.floor(diasDesdeJaneiro / 7) + 1 };
}
function inicioDaSemana(instante, timeZone = APP_TIMEZONE) {
  const chaveDoDia = localDayKey(instante, timeZone);
  const dia = /* @__PURE__ */ new Date(`${chaveDoDia}T12:00:00.000Z`);
  const diaDaSemana = dia.getUTCDay() === 0 ? 7 : dia.getUTCDay();
  const segunda = new Date(dia);
  segunda.setUTCDate(dia.getUTCDate() - (diaDaSemana - 1));
  return startOfLocalDay(segunda, timeZone);
}
function curvaPorSemana(locacoes, timeZone = APP_TIMEZONE) {
  const acumulado = /* @__PURE__ */ new Map();
  for (const locacao of locacoes) {
    const diasPorSemana = distribuirDias(locacao, timeZone);
    if (diasPorSemana.length === 0) continue;
    const pesos = diasPorSemana.map((entrada) => entrada.dias);
    const partes = allocate(locacao.receita, pesos);
    diasPorSemana.forEach((entrada, i) => {
      const rotulo = rotuloDaSemana(entrada.chave);
      const atual = acumulado.get(rotulo) ?? { chave: entrada.chave, diarias: 0, receita: zero };
      acumulado.set(rotulo, {
        chave: entrada.chave,
        diarias: atual.diarias + entrada.dias,
        receita: atual.receita + (partes[i] ?? zero)
      });
    });
  }
  return [...acumulado.entries()].map(([rotulo, valor]) => ({
    chave: valor.chave,
    rotulo,
    diarias: valor.diarias,
    receita: valor.receita
  })).sort((a, b) => a.rotulo.localeCompare(b.rotulo));
}
function distribuirDias(locacao, timeZone) {
  if (locacao.fim <= locacao.inicio) return [];
  const porSemana = /* @__PURE__ */ new Map();
  const vistos = /* @__PURE__ */ new Set();
  for (let instante = locacao.inicio.getTime(); instante < locacao.fim.getTime(); instante += 864e5) {
    marcar(new Date(instante));
  }
  marcar(new Date(locacao.fim.getTime() - 1));
  function marcar(momento) {
    const chaveDoDia = localDayKey(momento, timeZone);
    if (vistos.has(chaveDoDia)) return;
    vistos.add(chaveDoDia);
    const chave = semanaIso(momento, timeZone);
    const rotulo = rotuloDaSemana(chave);
    const atual = porSemana.get(rotulo);
    porSemana.set(rotulo, { chave, dias: (atual?.dias ?? 0) + 1 });
  }
  return [...porSemana.values()];
}

export { ACOES, ACOES_DE_TRANSICAO, ALERTAS_DE_RISCO, ALFABETO_DO_CODIGO_DE_RETIRADA, ANGULOS_DE_FOTO, ANGULOS_OBRIGATORIOS, ANTECEDENCIA_MAXIMA_EM_DIAS, APP_TIMEZONE, ATORES_DE_TRANSACAO_DE_PAGAMENTO, ATORES_DE_TRANSICAO, AVISOS_AVULSOS, AVISO_DA_PESQUISA, AVISO_DO_STATUS, BUFFER_MAXIMO_EM_HORAS, CAMINHO_DA_PESQUISA, CAMINHO_DA_PONTE, CANAIS_DE_NOTIFICACAO, CATEGORIAS_DE_CNH, CHAVES_DE_FAIXA, CHAVES_DE_PERMISSAO, CNPJ_TAMANHO, COBRANCAS_DE_EXTRA, CODIGOS_DE_FALHA_DA_ANALISE, CODIGOS_DE_PAPEL, CODIGO_DE_CUPOM_TAMANHO_MAXIMO, CODIGO_DE_CUPOM_TAMANHO_MINIMO, CONFIRMACAO_DE_EXCLUSAO, CPF_TAMANHO, DATE_FORMAT_BR, DATE_KEY_FORMAT, DATE_TIME_FORMAT_BR, DECISOES_DA_ANALISE, DEFAULT_RENTAL_DAY_POLICY, DESCRICAO_DO_IMPEDIMENTO, DESCRICAO_DO_ITEM_APAGADO, DESCRICAO_DO_ITEM_RETIDO, DESTINOS_DA_COBRANCA_DE_MULTA, DIARIAS_DE_CONTRATO_LONGO, DIAS_DA_SEMANA, DIAS_DA_SEMANA_VALIDOS, DIAS_MAXIMOS_DA_REFERENCIA, DIAS_MAXIMOS_DE_AVISO, DIAS_MAXIMOS_DE_RETENCAO_DA_CAUCAO, DIAS_MINIMOS_DA_REFERENCIA, DIAS_MINIMOS_DE_AVISO, DIAS_PADRAO_DA_REFERENCIA, DIAS_PARA_ALERTA_CRITICO, DIAS_PARA_ALERTA_DE_ATENCAO, DIA_MAXIMO_DE_FECHAMENTO, DURACAO_MAXIMA_DE_BLOQUEIO_EM_DIAS, DURACAO_MAXIMA_EM_DIAS, EFEITOS_DE_REGRA, EMAIL_TAMANHO_MAXIMO, ESCALA_DA_POSICAO, ESCALA_DO_FATOR, ESCALA_DO_RISCO, ESCALA_MAXIMA_POSSIVEL, ESCALA_MINIMA_POSSIVEL, ESCALA_PADRAO, ESCOPO_DE_VISITANTE, ESQUEMA_DO_APP, EXPLICACAO_DO_BLOQUEIO, FAIXAS_DE_DURACAO, FAIXAS_DE_NPS, FAIXAS_DO_BUREAU, FATORES_DE_ALOCACAO, FATORES_DE_RISCO, FINALIDADE_DA_WAITLIST, GRAVIDADES_DE_ALERTA, GRAVIDADES_DE_AVARIA, GRAVIDADES_DO_MOTIVO, IDADE_DE_CONTA_RECENTE_EM_DIAS, IDADE_MAXIMA_TITULAR, IDADE_MINIMA_TITULAR, IDEMPOTENCY_HEADER, IMPEDIMENTOS_DE_EXCLUSAO, INDICE_MAXIMO_DA_FOTO, ITENS_APAGADOS_NA_EXCLUSAO, ITENS_RETIDOS_NA_EXCLUSAO, JANELA_DE_RENOVACAO_EM_DIAS, JANELA_MAXIMA_DO_CALENDARIO_EM_DIAS, JANELA_MAXIMA_DO_CALENDARIO_PUBLICO, JANELA_MAXIMA_DO_GANTT_EM_DIAS, LIMIAR_PADRAO, LIMITE_DO_RISCO_BAIXO, LIMITE_DO_RISCO_MEDIO, LINHAS_DE_RECEITA_DE_LOCACAO, MARCADOR_DE_NUMERO, MARGEM_ANTES_DO_VENCIMENTO_EM_DIAS, MAXIMO_DE_CONDUTORES, MAXIMO_DE_CRITERIOS, MAXIMO_DE_EXTRAS_NA_PONTE, MAXIMO_DE_PARCELAS, MENSAGEM_CONFIRMACAO_DE_SENHA, METODOS_DE_PAGAMENTO, MINIMO_DE_PONTOS, MODOS_DO_MIRA_SCORE, MOTIVOS_CONTROLADOS_PELA_RESERVA, MOTIVOS_DE_BLOQUEIO, MOTIVOS_DE_BLOQUEIO_DE_ALOCACAO, MOTIVOS_DE_CANCELAMENTO_PADRAO, MOTIVOS_DE_REVOGACAO, MOTIVOS_OPERACIONAIS, MULTIPLICADOR_MAXIMO_EM_PONTOS_BASE, NIVEIS_DE_BLINDAGEM, NIVEIS_DE_RISCO, OITAVOS_DO_TANQUE, ORIGENS_DA_ANALISE, ORIGENS_DE_AJUSTE, ORIGENS_DE_CANCELAMENTO, ORIGENS_DE_CUSTO, OVERBOOKING_MAXIMO_EM_PONTOS_BASE, PAPEIS, PAPEIS_ATRIBUIVEIS, PAPEIS_INTERNOS, PAPEIS_QUE_EXIGEM_2FA, PARAMETROS_ARGON2ID, PARAMETROS_DA_PONTE, PERMISSOES, PERMISSOES_POR_PAPEL, PESOS_PADRAO, PESO_MAXIMO_DE_FATOR, PLATAFORMAS_DE_PUSH, POLITICA_DE_LOCKOUT, POLITICA_DE_MULTA_PADRAO, PONTOS_BASE_NEUTROS, PRAZO_DE_RETENCAO_FISCAL_EM_ANOS, PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS, PRE_PREENCHIMENTO_VAZIO, PRIORIDADE_MAXIMA, PROVEDORES_DE_ASSINATURA, PROVEDORES_DE_PAGAMENTO, PROVEDORES_OAUTH, PROVEDOR_DE_ASSINATURA_PADRAO, QUANTIDADE_DE_CODIGOS_DE_RECUPERACAO, RAIO_DE_CORRESPONDENCIA_EM_MILESIMOS, RECURSOS, REGIMES_DE_FATURAMENTO, RESULTADOS_DA_VERIFICACAO_DA_ANALISE, RESULTADOS_DE_VERIFICACAO, ROTULO_DA_COBRANCA, ROTULO_DA_DECISAO, ROTULO_DA_FAIXA, ROTULO_DA_FAIXA_DE_NPS, ROTULO_DA_FAIXA_DO_BUREAU, ROTULO_DA_FALHA_DA_ANALISE, ROTULO_DA_GRAVIDADE, ROTULO_DA_LINHA_DE_FECHAMENTO, ROTULO_DA_ORIGEM_DE_CANCELAMENTO, ROTULO_DA_ORIGEM_DO_AJUSTE, ROTULO_DA_SITUACAO_DA_CNH, ROTULO_DA_VERIFICACAO_DA_ANALISE, ROTULO_DA_VERIFICACAO_EXTERNA, ROTULO_DA_ZONA_DE_NPS, ROTULO_DO_ALERTA, ROTULO_DO_ALERTA_DE_RISCO, ROTULO_DO_ANGULO, ROTULO_DO_CAMBIO, ROTULO_DO_CANAL, ROTULO_DO_DESTINO_DA_COBRANCA, ROTULO_DO_DIA, ROTULO_DO_DOCUMENTO_SOLICITAVEL, ROTULO_DO_EFEITO, ROTULO_DO_FATOR, ROTULO_DO_FATOR_DE_RISCO, ROTULO_DO_METODO_DE_PAGAMENTO, ROTULO_DO_MOTIVO_DE_BLOQUEIO, ROTULO_DO_NIVEL_DE_BLINDAGEM, ROTULO_DO_NIVEL_DE_RISCO, ROTULO_DO_PROVEDOR_DE_ASSINATURA, ROTULO_DO_RESULTADO_DA_VERIFICACAO, ROTULO_DO_STATUS_DA_ANALISE, ROTULO_DO_STATUS_DE_CONTRATO, ROTULO_DO_STATUS_DE_COTACAO, ROTULO_DO_STATUS_DE_DOCUMENTO, ROTULO_DO_STATUS_DE_FATURA, ROTULO_DO_STATUS_DE_MULTA, ROTULO_DO_STATUS_DE_PRESTADOR, ROTULO_DO_STATUS_DE_RESERVA, ROTULO_DO_STATUS_DE_SINISTRO, ROTULO_DO_STATUS_DE_TRANSACAO, ROTULO_DO_STATUS_DE_VEICULO, ROTULO_DO_STATUS_DE_WAITLIST, ROTULO_DO_TIPO_DE_AVARIA, ROTULO_DO_TIPO_DE_CUSTO, ROTULO_DO_TIPO_DE_DESCONTO, ROTULO_DO_TIPO_DE_EXTRA, ROTULO_DO_TIPO_DE_LINHA, ROTULO_DO_TIPO_DE_LOCAL, ROTULO_DO_TIPO_DE_PRESTADOR, ROTULO_DO_TIPO_DE_SINISTRO, ROTULO_DO_TIPO_DE_TAXA_DE_MULTA, ROTULO_DO_TIPO_DE_TRANSACAO, ROTULO_DO_TIPO_DE_VISTORIA, SEM_VALOR, SENHA_TAMANHO_MAXIMO, SENHA_TAMANHO_MINIMO, SITUACOES_DA_CNH, STATUS_CONTROLADOS_PELA_RESERVA, STATUS_DA_ANALISE_CADASTRAL, STATUS_DE_CONTA, STATUS_DE_CONTRATO, STATUS_DE_COTACAO, STATUS_DE_DOCUMENTO, STATUS_DE_FATURA, STATUS_DE_MULTA, STATUS_DE_PRESTADOR, STATUS_DE_RESERVA, STATUS_DE_SINISTRO, STATUS_DE_TRANSACAO, STATUS_DE_USUARIO, STATUS_DE_VEICULO, STATUS_DE_WAITLIST, STATUS_OPERACIONAIS, STATUS_PENDENTES_DA_ANALISE, STATUS_QUE_NOTIFICAM, STATUS_QUE_OCUPAM_FROTA, STATUS_TERMINAIS, STATUS_TERMINAIS_DE_TRANSACAO, TAMANHO_DA_CHAVE_DE_ACESSO, TAMANHO_DO_CODIGO_DE_RECUPERACAO, TAMANHO_DO_CODIGO_DE_RETIRADA, TAMANHO_MAXIMO_DE_PAGINA, TAXA_DE_RESPOSTA_MINIMA_CONFIAVEL, TAXA_PERCENTUAL_MAXIMA, TENANT_HEADER, TIPOS_DE_ALERTA, TIPOS_DE_AVARIA, TIPOS_DE_CAMBIO, TIPOS_DE_CONTA, TIPOS_DE_CUSTO_DE_VEICULO, TIPOS_DE_CUSTO_LANCAVEIS, TIPOS_DE_DESCONTO, TIPOS_DE_DOCUMENTO_SOLICITAVEL, TIPOS_DE_EXTRA, TIPOS_DE_EXTRA_COM_PRESTADOR, TIPOS_DE_LINHA, TIPOS_DE_LINHA_DE_FECHAMENTO, TIPOS_DE_LOCAL, TIPOS_DE_PRESTADOR, TIPOS_DE_SINISTRO, TIPOS_DE_TAXA_DE_MULTA, TIPOS_DE_TRANSACAO, TIPOS_DE_VISTORIA, TIPOS_RATEAVEIS, TIPOS_SEM_CONTRAPARTIDA_EM_CAIXA, TIPO_DE_EXTRA_COM_FRANQUIA, TIPO_DE_EXTRA_COM_KM, TODOS_OS_AVISOS, TOLERANCIA_MAXIMA_EM_MINUTOS, TOTP_ALGORITMO, TOTP_DIGITOS, TOTP_JANELAS_TOLERADAS, TOTP_PERIODO_SEGUNDOS, TRACE_HEADER, TRANSICOES, TRANSICOES_DE_PAGAMENTO, TTL_DESAFIO_2FA_MINUTOS, TTL_RESET_DE_SENHA_MINUTOS, TTL_VERIFICACAO_DE_EMAIL_MINUTOS, UNIDADES_FEDERATIVAS, VALIDADE_DA_ANALISE_EM_DIAS, VALIDADE_DA_PRE_AUTORIZACAO_EM_DIAS, VALIDADE_MAXIMA_DA_COTACAO_EM_MINUTOS, VERIFICACOES_DA_ANALISE, VERIFICACOES_EXTERNAS, ZONAS_DE_NPS, aceitesDoCadastroSchema, adotarCotacoesDeVisitanteSchema, adotarSessaoDeVisitanteSchema, agregadoDoCriterioSchema, agregarCriterio, ajusteDoDiaLidoSchema, allocate, alocacao_exports as alocacao, alocarVeiculoSchema, alterarSenhaSchema, analiseCadastralSchema, analiseEstaPendente, anexarDocumentoSchema, anexarNotaFiscalSchema, anguloDeFotoSchema, angulosFaltantes, antecedenciaAplicavel, antecedenciaEmDias, apagaDadosPessoais, apenasAlfanumericos, apenasDigitos, aplicarRegrasDaConfiguracao, atribuirPapeisSchema, atualizarBloqueioDeVeiculoSchema, atualizarCategoriaSchema, atualizarContaPessoaFisicaSchema, atualizarContaPessoaJuridicaSchema, atualizarCupomSchema, atualizarCustoSchema, atualizarEnderecoDaContaSchema, atualizarExtraSchema, atualizarItemDeWaitlistSchema, atualizarLocalSchema, atualizarPrestadorSchema, atualizarRegraDeAntecedenciaSchema, atualizarRegraDeDiaDaSemanaSchema, atualizarRegraDeOcupacaoSchema, atualizarSazonalidadeSchema, atualizarSinistroSchema, atualizarTabelaDePrecoSchema, atualizarUsuarioInternoSchema, atualizarVeiculoSchema, autorizacaoDaAnaliseSchema, autorizacaoDaReservaSchema, avaliarDocumentoSchema, avariaSchema, avariasNovas, baseDoPrecoPublicoSchema, bloqueiosDoCandidato, brand2 as brand, breakdownFecha, buildHeaders, buildUrl, calcularNps, calcularPayback, calcularRisco, calcularTaxaDeMulta, calendarDaysSpanned, calendarioDeDisponibilidadeSchema, calendarioPublicoQuerySchema, calendarioPublicoSchema, callbackOAuthSchema, camposDaConfiguracaoDaPesquisa, camposDaPoliticaDeMulta, canaisEfetivos, canalDeNotificacaoSchema, catalogoPublicoQuerySchema, catalogoPublicoSchema, categoriaDeCnhSchema, categoriaPublicaDetalheSchema, categoriaPublicaSchema, cepSchema, chartNeutrals, chartNeutralsLight, chaveDaFaixaSchema, chaveDeAcessoSchema, chaveDeArquivoSchema, chaveDeFotoDeCategoriaSchema, cnpjEhAlfanumerico, cnpjSchema, cobrancaDeExtraSchema, cobrancaNoCartaoEm, codigoDeCupomSchema, codigoDePapelSchema, codigoDeRecuperacaoSchema, codigoDeRetiradaSchema, codigoTotpSchema, condutorSchema, configuracaoDaPesquisaSchema, configurarMiraScoreSchema, confirmarResetDeSenhaSchema, confirmarTotpSchema, confirmarVerificacaoDeEmailSchema, consultarDisponibilidadeSchema, convidarCondutorSchema, cotacaoEmitidaSchema, cotacaoLidaSchema, cotar, cpfSchema, criarBloqueioDePrestadorSchema, criarBloqueioDeVeiculoSchema, criarCategoriaSchema, criarContaPessoaFisicaSchema, criarContaPessoaJuridicaSchema, criarContaSchema, criarCupomSchema, criarEnderecoDaContaSchema, criarExtraSchema, criarItemDeWaitlistDeVisitanteSchema, criarItemDeWaitlistSchema, criarLocalSchema, criarPrestadorSchema, criarRegraDeAntecedenciaSchema, criarRegraDeDiaDaSemanaSchema, criarRegraDeOcupacaoSchema, criarReservaSchema, criarSazonalidadeSchema, criarTabelaDePrecoSchema, criarUsuarioInternoSchema, criarVeiculoSchema, criterioDaPesquisaSchema, cronogramaDeDepreciacao, curvaPorSemana, custoDaOciosidade, custoDeCapitalDoMes, custoIndiretoDoMes, custoPorDiaDePosse, custoPorKm, custos_exports as custos, dataDeNascimentoSchema, dataSimplesSchema2 as dataSimplesSchema, datetime_exports as datetime, dayOfWeekFromKey, deCampoDeReaisParaWire, depreciacaoDoMes, desafio2faSchema, desafioDeSetupDe2faSchema, desafioPendenteSchema, desativarTotpSchema, descontoDoCupom, descontoDoCupomCoerente, destinoDaCobrancaDeMulta, desvincularOAuthSchema, detectarAnomalia, devolucaoDepoisDaRetirada, diaDoCalendarioSchema, diaPublicoSchema, diariaDoDiaLidaSchema, diariaMediaRealizada, diasCobertos, diasDeOciosidade, diasParadoDisponivel, dinheiroNoFioSchema, dispersaoRobusta, disponibilidade_exports as disponibilidade, disponibilidadeDaCategoriaSchema, duracaoEmDias, efeitoDeRegraSchema, efeitoDeSazonalidadeCoerente, ehContratoLongo, ehPapelAtribuivel, ehPermissao, ehReceitaDeLocacao, ehTerminal, emailSchema, encontrarTransicao, encontrarTransicaoDePagamento, endOfLocalDay, enderecoDaContaSchema, enviarParaAssinaturaSchema, escolherVeiculo, esquecerAparelhoSchema, estadosInalcancaveis, estadosSemSaida, estornarSchema, excluirContaSchema, exige2fa, exigeMotivo, extraEscolhidoSchema, extraPublicoSchema, extrasPublicosSchema, faixaDaDuracao, faixaDaNota, faixasFaltantes, fechamento_exports as fechamento, fecharLocacao, fecharReservaSchema, filtroDeAlertaSchema, filtroDeBloqueioSchema, filtroDeCategoriaSchema, filtroDeCotacaoSchema, filtroDeCupomSchema, filtroDeCustoSchema, filtroDeExtraSchema, filtroDeLocalSchema, filtroDeMultaSchema, filtroDeOverbookingSchema, filtroDePrestadorSchema, filtroDeRegraDePrecoSchema, filtroDeReservaSchema, filtroDeSazonalidadeSchema, filtroDeSinistroSchema, filtroDeTabelaDePrecoSchema, filtroDeTarifaSchema, filtroDeVeiculoSchema, filtroDeWaitlistSchema, filtroDoGanttSchema, fimDepoisDoInicio, formatBRL, formatDateBR, formatDateTimeBR, formatInTimeZone, formatarCep, formatarCepDeExibicao, formatarCnpj, formatarCpf, formatarData, formatarDataHora, formatarDocumentoDeExibicao, formatarMoedaDoWire, formatarPlaca, formatarTelefone, formatarTelefoneDeExibicao, fotoDaGaleriaSchema, fotoDeVistoriaSchema, franquiaCoerente, fromCents, fromReais, fromWire, frota_exports as frota, gerarSlug, gravidadeDeAvariaSchema, gravidadeDoVencimento, hashDasRegrasSchema, horariosSchema, idadeEmAnos, indicarCondutorSchema, indiceDeFotoSchema, indiceDoMesDeOperacao, inicioDaSemana, inscricaoEstadualSchema, instanteSchema, integracaoMiraScoreSchema, isMoney, isSameLocalDay, itemDeChecklistSchema, janelaDaReferenciaSchema, kmPorUnidadeCoerente, lancarCustoSchema, leadTimeMedio, lerPrePreenchimento, lightSurfaces, lightText, limiaresDaConfiguracao, limiaresPadrao, linhaDaCotacaoLidaSchema, linkDaPesquisa, linkDaReservaNoApp, linkDeWhatsApp, listarContasQuerySchema, listarUsuariosQuerySchema, locacao_exports as locacao, locaisPublicosQuerySchema, locaisPublicosSchema, localDayKey, localPublicoSchema, loginSchema, logoutSchema, margemDeContribuicao, margemPercentual, mascararCepProgressivo, mascararCnpjProgressivo, mascararCpf, mascararCpfProgressivo, mascararTelefoneProgressivo, mediana, mensagemDaPonte, mereceReview, money_exports as money, moneySchema, moneySchemaNonNegative, moneySchemaPositive, montarConsultaDaPonte, montarLinkDeReserva, motivoDeBloqueioSchema, motivoDeCancelamentoSchema, motivoDeRevogacaoSchema, motivoDeTransicaoSchema, motivosDaOrigem, mudarStatusDoVeiculoSchema, mulQuantity, mulRate, multiplicadorEmPontosBaseSchema, nivelDeCombustivelSchema, nivelDoRisco, nomeCompletoParaAnalise, nomeSchema, normalizar, normalizarCep, normalizarChassi, normalizarCnh, normalizarCnpj, normalizarCodigoDeCupom, normalizarCodigoDeRetirada, normalizarCpf, normalizarPlaca, normalizarRenavam, normalizarTelefone, notaDeCriterioSchema, nowInTimeZone, npsConfiavel, numeroDeCnhSchema, ocupaFrota, ocupacaoAplicavel, origemDeCancelamentoSchema, origemDoAjusteSchema, pagamento_exports as pagamento, paginacaoSchema, paraCampoDeReais, parseUtcIso, partesDaPermissao, payloadDoAccessTokenSchema, payloadDoTokenDeVisitanteSchema, pedirCotacaoDeVisitanteSchema, pedirCotacaoSchema, pedirSessaoDeVisitanteSchema, percentOf, percentual, periodoDentroDoTeto, periodoValidoSchema, permissaoSchema, permissoesDosPapeis, pesquisaParaResponderSchema, planoDeParcelas, podeTransicionar, politicaDeMultaNoFio, pontuarCandidatos, precificacao_exports as precificacao, precisaDeAnaliseAutomatica, precisaRenovarPreAutorizacao, precoDeReferenciaDeCategoriaSchema, precoDeReferenciaQuerySchema, precos_exports as precos, precosDeReferenciaSchema, preferenciaDeNotificacaoSchema, previaDaExclusaoDeContaSchema, prioridadeSchema, prorrogarReservaSchema, provedorDeAssinaturaSchema, provedorOAuthSchema, provedoresDisponiveisSchema, quantidadeDeMalas, radius, ratearCustoAnual, ratearOverhead, ratearReceita, refreshSchema, regimeDeFaturamentoSchema, registrarAparelhoSchema, registrarMultaSchema, registrarOverbookingSchema, registrarSinistroSchema, registrarVistoriaSchema, registroSchema, relatorioDaPesquisaSchema, rentalDays, reserva_exports as reserva, resolverPremissas, responderPesquisaSchema, respostaDeChecklistSchema, respostaDeLoginSchema, resultadoDaAdocaoSchema, resultadoDaAnaliseSchema, resultadoDaExclusaoDeContaSchema, resultadoDaRespostaSchema, retencaoDaCaucaoAte, revPac, risco_exports as risco, rotuloDaSemana, rotuloDoNivelDeCombustivel, salvarChecklistSchema, salvarPesosDeAlocacaoSchema, salvarPlanoDeManutencaoSchema, salvarPoliticaDeCustoSchema, salvarPoliticaDeLocacaoSchema, salvarTarifasSchema, sazonalidadeDoDia, segundosDeBloqueio, semanaIso, semantic, semanticLight, senhaSchema, senhasConferem, sessaoDeVisitanteSchema, sessaoSchema, setupTotpRespostaSchema, shadows, simularToleranciaSchema, slugPublicoSchema, solicitarDocumentoSchema, solicitarResetDeSenhaSchema, solicitarVerificacaoDeEmailSchema, somar, startOfLocalDay, statusDaAnaliseCadastralSchema, statusDaContaSchema, statusDaCotacaoSchema, statusDaReservaSchema, statusDaWaitlistSchema, statusDeContratoSchema, statusDeDocumentoSchema, statusDeFaturaSchema, statusDeMultaSchema, statusDeSinistroSchema, statusDeTransacaoSchema, statusDoPrestadorSchema, statusDoUsuarioSchema, sumAll, surfaces, tabelaNaoCrescente, tarifaDaFaixaSchema, taxaDeConversao, taxaDeOcupacao, taxaDeOcupacaoDeDiaria, taxaDeResposta, taxaDeUtilizacao, tco, telefoneSchema, temPrePreenchimento, text, textoDoAviso, ticketMedio, tipoDeAvariaSchema, tipoDeContaSchema, tipoDeDescontoSchema, tipoDeDocumentoSolicitavelSchema, tipoDeExtraSchema, tipoDeLinhaSchema, tipoDePrestadorSchema, tipoDeSinistroSchema, tipoDeVistoriaSchema, toDecimalString, toInstant, toUtcIso, toWire, toZoned, tokenDePushSchema, tokenOpacoSchema, tokens, transicionarReservaSchema, transicoesDePagamentoPossiveis, transicoesPossiveis, trocarVeiculoSchema, typography, ufSchema, usuarioAutenticadoSchema, uuidSchema, validadeDaAnalise, validarChassi, validarChaveDeAcesso, validarCnpj, validarCodigoDeCupom, validarCpf, validarPlaca, validarTelefone, valorContabilNoMes, valorDepreciavel, valorDoExtra, valorResidual, vigenciaValida, vincularOAuthSchema, vistoria_exports as vistoria, vistoriaAvulsaSchema, vitrine_exports as vitrine, waitlistTemContato, zonaDoNps };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map